import { useState, useMemo, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { MapPin, Search, Check, MapPinIcon, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Comprehensive global destinations database with extensive high-preparation locations
export const LOCATIONS = [
  // AFRICA - High Preparation Destinations
  
  // East Africa - High preparation needed
  { name: 'Nairobi', country: 'Kenya' },
  { name: 'Mombasa', country: 'Kenya' },
  { name: 'Kisumu', country: 'Kenya' },
  { name: 'Nakuru', country: 'Kenya' },
  { name: 'Malindi', country: 'Kenya' },
  { name: 'Lamu', country: 'Kenya' },
  { name: 'Amboseli National Park', country: 'Kenya' },
  { name: 'Maasai Mara', country: 'Kenya' },
  { name: 'Mount Kenya', country: 'Kenya' },
  
  { name: 'Dar es Salaam', country: 'Tanzania' },
  { name: 'Arusha', country: 'Tanzania' },
  { name: 'Mwanza', country: 'Tanzania' },
  { name: 'Dodoma', country: 'Tanzania' },
  { name: 'Zanzibar', country: 'Tanzania' },
  { name: 'Pemba Island', country: 'Tanzania' },
  { name: 'Serengeti National Park', country: 'Tanzania' },
  { name: 'Ngorongoro Crater', country: 'Tanzania' },
  { name: 'Mount Kilimanjaro', country: 'Tanzania' },
  { name: 'Tarangire National Park', country: 'Tanzania' },
  
  { name: 'Kampala', country: 'Uganda' },
  { name: 'Jinja', country: 'Uganda' },
  { name: 'Gulu', country: 'Uganda' },
  { name: 'Mbarara', country: 'Uganda' },
  { name: 'Fort Portal', country: 'Uganda' },
  { name: 'Bwindi Impenetrable Forest', country: 'Uganda' },
  { name: 'Queen Elizabeth National Park', country: 'Uganda' },
  { name: 'Murchison Falls', country: 'Uganda' },
  
  { name: 'Kigali', country: 'Rwanda' },
  { name: 'Butare', country: 'Rwanda' },
  { name: 'Gisenyi', country: 'Rwanda' },
  { name: 'Volcanoes National Park', country: 'Rwanda' },
  { name: 'Akagera National Park', country: 'Rwanda' },
  
  { name: 'Addis Ababa', country: 'Ethiopia' },
  { name: 'Bahir Dar', country: 'Ethiopia' },
  { name: 'Gondar', country: 'Ethiopia' },
  { name: 'Axum', country: 'Ethiopia' },
  { name: 'Lalibela', country: 'Ethiopia' },
  { name: 'Harar', country: 'Ethiopia' },
  { name: 'Omo Valley', country: 'Ethiopia' },
  { name: 'Simien Mountains', country: 'Ethiopia' },
  { name: 'Danakil Depression', country: 'Ethiopia' },

  // West Africa - High preparation needed
  { name: 'Lagos', country: 'Nigeria' },
  { name: 'Abuja', country: 'Nigeria' },
  { name: 'Kano', country: 'Nigeria' },
  { name: 'Ibadan', country: 'Nigeria' },
  { name: 'Port Harcourt', country: 'Nigeria' },
  { name: 'Calabar', country: 'Nigeria' },
  { name: 'Jos', country: 'Nigeria' },
  
  { name: 'Accra', country: 'Ghana' },
  { name: 'Kumasi', country: 'Ghana' },
  { name: 'Tamale', country: 'Ghana' },
  { name: 'Cape Coast', country: 'Ghana' },
  { name: 'Elmina', country: 'Ghana' },
  { name: 'Kakum National Park', country: 'Ghana' },
  
  { name: 'Dakar', country: 'Senegal' },
  { name: 'Saint-Louis', country: 'Senegal' },
  { name: 'Thiès', country: 'Senegal' },
  { name: 'Casamance', country: 'Senegal' },
  { name: 'Gorée Island', country: 'Senegal' },
  
  { name: 'Abidjan', country: 'Côte d\'Ivoire' },
  { name: 'Yamoussoukro', country: 'Côte d\'Ivoire' },
  { name: 'San Pedro', country: 'Côte d\'Ivoire' },
  { name: 'Bouaké', country: 'Côte d\'Ivoire' },
  
  { name: 'Bamako', country: 'Mali' },
  { name: 'Timbuktu', country: 'Mali' },
  { name: 'Mopti', country: 'Mali' },
  { name: 'Djenné', country: 'Mali' },
  { name: 'Gao', country: 'Mali' },
  
  { name: 'Ouagadougou', country: 'Burkina Faso' },
  { name: 'Bobo-Dioulasso', country: 'Burkina Faso' },
  { name: 'Koudougou', country: 'Burkina Faso' },
  { name: 'Ouahigouya', country: 'Burkina Faso' },
  { name: 'Banfora', country: 'Burkina Faso' },
  { name: 'Dédougou', country: 'Burkina Faso' },
  { name: 'Kaya', country: 'Burkina Faso' },
  { name: 'Tenkodogo', country: 'Burkina Faso' },
  { name: 'Fada N\'gourma', country: 'Burkina Faso' },
  { name: 'Dori', country: 'Burkina Faso' },
  { name: 'Gaoua', country: 'Burkina Faso' },
  { name: 'Sindou Peaks', country: 'Burkina Faso' },
  { name: 'Cascades de Karfiguéla', country: 'Burkina Faso' },
  { name: 'Lake Tengrela', country: 'Burkina Faso' },
  { name: 'Ruins of Loropéni', country: 'Burkina Faso' },
  { name: 'W National Park', country: 'Burkina Faso' },
  { name: 'Arly National Park', country: 'Burkina Faso' },
  { name: 'Nazinga Game Ranch', country: 'Burkina Faso' },
  
  { name: 'Niamey', country: 'Niger' },
  { name: 'Zinder', country: 'Niger' },
  { name: 'Maradi', country: 'Niger' },
  { name: 'Agadez', country: 'Niger' },
  { name: 'Tahoua', country: 'Niger' },
  { name: 'Dosso', country: 'Niger' },
  { name: 'Diffa', country: 'Niger' },
  { name: 'Arlit', country: 'Niger' },
  { name: 'Téra', country: 'Niger' },
  { name: 'Tillabéri', country: 'Niger' },
  { name: 'W National Park', country: 'Niger' },
  { name: 'Aïr Mountains', country: 'Niger' },
  { name: 'Ténéré Desert', country: 'Niger' },
  { name: 'Timia', country: 'Niger' },
  { name: 'Iférouane', country: 'Niger' },
  
  { name: 'Freetown', country: 'Sierra Leone' },
  { name: 'Bo', country: 'Sierra Leone' },
  { name: 'Kenema', country: 'Sierra Leone' },
  { name: 'Makeni', country: 'Sierra Leone' },
  { name: 'Koidu', country: 'Sierra Leone' },
  { name: 'Lunsar', country: 'Sierra Leone' },
  { name: 'Port Loko', country: 'Sierra Leone' },
  { name: 'Waterloo', country: 'Sierra Leone' },
  { name: 'Kabala', country: 'Sierra Leone' },
  { name: 'Bonthe', country: 'Sierra Leone' },
  { name: 'Banana Islands', country: 'Sierra Leone' },
  { name: 'Tokeh Beach', country: 'Sierra Leone' },
  { name: 'River Number Two Beach', country: 'Sierra Leone' },
  { name: 'Tiwai Island', country: 'Sierra Leone' },
  { name: 'Outamba-Kilimi National Park', country: 'Sierra Leone' },
  { name: 'Gola Rainforest', country: 'Sierra Leone' },
  { name: 'Turtle Islands', country: 'Sierra Leone' },
  
  { name: 'Monrovia', country: 'Liberia' },
  { name: 'Gbarnga', country: 'Liberia' },
  { name: 'Buchanan', country: 'Liberia' },
  { name: 'Ganta', country: 'Liberia' },
  { name: 'Kakata', country: 'Liberia' },
  { name: 'Bensonville', country: 'Liberia' },
  { name: 'Harper', country: 'Liberia' },
  { name: 'Voinjama', country: 'Liberia' },
  { name: 'Robertsport', country: 'Liberia' },
  { name: 'Zwedru', country: 'Liberia' },
  { name: 'Sapo National Park', country: 'Liberia' },
  { name: 'Mount Nimba', country: 'Liberia' },
  { name: 'Lake Piso', country: 'Liberia' },
  { name: 'Marshall', country: 'Liberia' },
  
  { name: 'Conakry', country: 'Guinea' },
  { name: 'Kankan', country: 'Guinea' },
  { name: 'Labé', country: 'Guinea' },
  { name: 'Nzérékoré', country: 'Guinea' },
  { name: 'Kindia', country: 'Guinea' },
  { name: 'Mamou', country: 'Guinea' },
  { name: 'Boké', country: 'Guinea' },
  { name: 'Siguiri', country: 'Guinea' },
  { name: 'Kissidougou', country: 'Guinea' },
  { name: 'Guéckédou', country: 'Guinea' },
  { name: 'Macenta', country: 'Guinea' },
  { name: 'Faranah', country: 'Guinea' },
  { name: 'Dalaba', country: 'Guinea' },
  { name: 'Pita', country: 'Guinea' },
  { name: 'Fouta Djallon', country: 'Guinea' },
  { name: 'Mount Nimba Reserve', country: 'Guinea' },
  { name: 'Îles de Los', country: 'Guinea' },
  { name: 'Kassa Island', country: 'Guinea' },
  { name: 'Room Island', country: 'Guinea' },
  { name: 'Bel Air Beach', country: 'Guinea' },

  // Central Africa - High preparation needed
  { name: 'Kinshasa', country: 'Democratic Republic of Congo' },
  { name: 'Lubumbashi', country: 'Democratic Republic of Congo' },
  { name: 'Goma', country: 'Democratic Republic of Congo' },
  { name: 'Bukavu', country: 'Democratic Republic of Congo' },
  { name: 'Kisangani', country: 'Democratic Republic of Congo' },
  { name: 'Virunga National Park', country: 'Democratic Republic of Congo' },
  
  { name: 'Brazzaville', country: 'Republic of Congo' },
  { name: 'Pointe-Noire', country: 'Republic of Congo' },
  { name: 'Dolisie', country: 'Republic of Congo' },
  { name: 'Nkayi', country: 'Republic of Congo' },
  { name: 'Impfondo', country: 'Republic of Congo' },
  { name: 'Ouesso', country: 'Republic of Congo' },
  { name: 'Owando', country: 'Republic of Congo' },
  { name: 'Sibiti', country: 'Republic of Congo' },
  { name: 'Djambala', country: 'Republic of Congo' },
  { name: 'Kinkala', country: 'Republic of Congo' },
  { name: 'Madingou', country: 'Republic of Congo' },
  { name: 'Gamboma', country: 'Republic of Congo' },
  { name: 'Ewo', country: 'Republic of Congo' },
  { name: 'Odzala-Kokoua National Park', country: 'Republic of Congo' },
  { name: 'Conkouati-Douli National Park', country: 'Republic of Congo' },
  { name: 'Nouabalé-Ndoki National Park', country: 'Republic of Congo' },
  
  { name: 'Yaoundé', country: 'Cameroon' },
  { name: 'Douala', country: 'Cameroon' },
  { name: 'Garoua', country: 'Cameroon' },
  { name: 'Bamenda', country: 'Cameroon' },
  { name: 'Bafoussam', country: 'Cameroon' },
  { name: 'Maroua', country: 'Cameroon' },
  { name: 'Ngaoundéré', country: 'Cameroon' },
  { name: 'Bertoua', country: 'Cameroon' },
  { name: 'Ebolowa', country: 'Cameroon' },
  { name: 'Kumba', country: 'Cameroon' },
  { name: 'Limbe', country: 'Cameroon' },
  { name: 'Buea', country: 'Cameroon' },
  { name: 'Kribi', country: 'Cameroon' },
  { name: 'Edéa', country: 'Cameroon' },
  { name: 'Nkongsamba', country: 'Cameroon' },
  { name: 'Foumban', country: 'Cameroon' },
  { name: 'Dschang', country: 'Cameroon' },
  { name: 'Mount Cameroon', country: 'Cameroon' },
  { name: 'Waza National Park', country: 'Cameroon' },
  { name: 'Bénoué National Park', country: 'Cameroon' },
  { name: 'Korup National Park', country: 'Cameroon' },
  { name: 'Dja Faunal Reserve', country: 'Cameroon' },
  { name: 'Lobéké National Park', country: 'Cameroon' },
  { name: 'Campo Ma\'an National Park', country: 'Cameroon' },
  
  { name: 'Bangui', country: 'Central African Republic' },
  { name: 'Bimbo', country: 'Central African Republic' },
  { name: 'Berbérati', country: 'Central African Republic' },
  { name: 'Carnot', country: 'Central African Republic' },
  { name: 'Bambari', country: 'Central African Republic' },
  { name: 'Bouar', country: 'Central African Republic' },
  { name: 'Bossangoa', country: 'Central African Republic' },
  { name: 'Bria', country: 'Central African Republic' },
  { name: 'Bangassou', country: 'Central African Republic' },
  { name: 'Nola', country: 'Central African Republic' },
  { name: 'Dzanga-Sangha Reserve', country: 'Central African Republic' },
  { name: 'Manovo-Gounda St. Floris', country: 'Central African Republic' },
  { name: 'Bamingui-Bangoran National Park', country: 'Central African Republic' },
  
  { name: 'N\'Djamena', country: 'Chad' },
  { name: 'Moundou', country: 'Chad' },
  { name: 'Sarh', country: 'Chad' },
  { name: 'Abéché', country: 'Chad' },
  { name: 'Kelo', country: 'Chad' },
  { name: 'Koumra', country: 'Chad' },
  { name: 'Pala', country: 'Chad' },
  { name: 'Am Timan', country: 'Chad' },
  { name: 'Bongor', country: 'Chad' },
  { name: 'Mongo', country: 'Chad' },
  { name: 'Doba', country: 'Chad' },
  { name: 'Bol', country: 'Chad' },
  { name: 'Faya-Largeau', country: 'Chad' },
  { name: 'Lake Chad', country: 'Chad' },
  { name: 'Zakouma National Park', country: 'Chad' },
  { name: 'Ennedi Plateau', country: 'Chad' },
  { name: 'Tibesti Mountains', country: 'Chad' },
  { name: 'Ounianga Lakes', country: 'Chad' },
  
  { name: 'Libreville', country: 'Gabon' },
  { name: 'Port-Gentil', country: 'Gabon' },
  { name: 'Franceville', country: 'Gabon' },
  { name: 'Oyem', country: 'Gabon' },
  { name: 'Moanda', country: 'Gabon' },
  { name: 'Mouila', country: 'Gabon' },
  { name: 'Lambaréné', country: 'Gabon' },
  { name: 'Tchibanga', country: 'Gabon' },
  { name: 'Makokou', country: 'Gabon' },
  { name: 'Koulamoutou', country: 'Gabon' },
  { name: 'Lastoursville', country: 'Gabon' },
  { name: 'Mitzic', country: 'Gabon' },
  { name: 'Bitam', country: 'Gabon' },
  { name: 'Gamba', country: 'Gabon' },
  { name: 'Mayumba', country: 'Gabon' },
  { name: 'Loango National Park', country: 'Gabon' },
  { name: 'Ivindo National Park', country: 'Gabon' },
  { name: 'Lopé National Park', country: 'Gabon' },
  { name: 'Akanda National Park', country: 'Gabon' },
  { name: 'Pongara National Park', country: 'Gabon' },
  { name: 'Crystal Mountains', country: 'Gabon' },
  
  { name: 'Malabo', country: 'Equatorial Guinea' },
  { name: 'Bata', country: 'Equatorial Guinea' },
  { name: 'Ebebiyín', country: 'Equatorial Guinea' },
  { name: 'Aconibe', country: 'Equatorial Guinea' },
  { name: 'Añisoc', country: 'Equatorial Guinea' },
  { name: 'Luba', country: 'Equatorial Guinea' },
  { name: 'Evinayong', country: 'Equatorial Guinea' },
  { name: 'Mongomo', country: 'Equatorial Guinea' },
  { name: 'Bioko Island', country: 'Equatorial Guinea' },
  { name: 'Corisco Island', country: 'Equatorial Guinea' },
  { name: 'Annobón', country: 'Equatorial Guinea' },
  { name: 'Monte Alén National Park', country: 'Equatorial Guinea' },
  { name: 'Pico Basilé', country: 'Equatorial Guinea' },

  // Southern Africa - Moderate to high preparation
  { name: 'Cape Town', country: 'South Africa' },
  { name: 'Johannesburg', country: 'South Africa' },
  { name: 'Durban', country: 'South Africa' },
  { name: 'Pretoria', country: 'South Africa' },
  { name: 'Port Elizabeth', country: 'South Africa' },
  { name: 'Bloemfontein', country: 'South Africa' },
  { name: 'East London', country: 'South Africa' },
  { name: 'Pietermaritzburg', country: 'South Africa' },
  { name: 'Kimberley', country: 'South Africa' },
  { name: 'Polokwane', country: 'South Africa' },
  { name: 'Nelspruit', country: 'South Africa' },
  { name: 'Rustenburg', country: 'South Africa' },
  { name: 'George', country: 'South Africa' },
  { name: 'Knysna', country: 'South Africa' },
  { name: 'Plettenberg Bay', country: 'South Africa' },
  { name: 'Hermanus', country: 'South Africa' },
  { name: 'Stellenbosch', country: 'South Africa' },
  { name: 'Franschhoek', country: 'South Africa' },
  { name: 'Paarl', country: 'South Africa' },
  { name: 'Somerset West', country: 'South Africa' },
  { name: 'Mossel Bay', country: 'South Africa' },
  { name: 'Oudtshoorn', country: 'South Africa' },
  { name: 'Swellendam', country: 'South Africa' },
  { name: 'Caledon', country: 'South Africa' },
  { name: 'Langebaan', country: 'South Africa' },
  { name: 'Paternoster', country: 'South Africa' },
  { name: 'Camps Bay', country: 'South Africa' },
  { name: 'Hout Bay', country: 'South Africa' },
  { name: 'Simons Town', country: 'South Africa' },
  { name: 'Kalk Bay', country: 'South Africa' },
  { name: 'Muizenberg', country: 'South Africa' },
  { name: 'Boulders Beach', country: 'South Africa' },
  { name: 'Cape Point', country: 'South Africa' },
  { name: 'Cape of Good Hope', country: 'South Africa' },
  { name: 'Table Mountain', country: 'South Africa' },
  { name: 'Robben Island', country: 'South Africa' },
  { name: 'V&A Waterfront', country: 'South Africa' },
  { name: 'Kruger National Park', country: 'South Africa' },
  { name: 'Sabi Sands', country: 'South Africa' },
  { name: 'Thornybush', country: 'South Africa' },
  { name: 'Timbavati', country: 'South Africa' },
  { name: 'Manyeleti', country: 'South Africa' },
  { name: 'Balule', country: 'South Africa' },
  { name: 'Klaserie', country: 'South Africa' },
  { name: 'Hoedspruit', country: 'South Africa' },
  { name: 'Phalaborwa', country: 'South Africa' },
  { name: 'Hazyview', country: 'South Africa' },
  { name: 'White River', country: 'South Africa' },
  { name: 'Sabie', country: 'South Africa' },
  { name: 'Graskop', country: 'South Africa' },
  { name: 'Pilgrim\'s Rest', country: 'South Africa' },
  { name: 'Blyde River Canyon', country: 'South Africa' },
  { name: 'God\'s Window', country: 'South Africa' },
  { name: 'Bourke\'s Luck Potholes', country: 'South Africa' },
  { name: 'Three Rondavels', country: 'South Africa' },
  { name: 'Kruger Gate', country: 'South Africa' },
  { name: 'Skukuza', country: 'South Africa' },
  { name: 'Satara', country: 'South Africa' },
  { name: 'Punda Maria', country: 'South Africa' },
  { name: 'Letaba', country: 'South Africa' },
  { name: 'Olifants', country: 'South Africa' },
  { name: 'Lower Sabie', country: 'South Africa' },
  { name: 'Crocodile Bridge', country: 'South Africa' },
  { name: 'Garden Route', country: 'South Africa' },
  { name: 'Wilderness', country: 'South Africa' },
  { name: 'Sedgefield', country: 'South Africa' },
  { name: 'Tsitsikamma', country: 'South Africa' },
  { name: 'Storms River', country: 'South Africa' },
  { name: 'Nature\'s Valley', country: 'South Africa' },
  { name: 'Jeffrey\'s Bay', country: 'South Africa' },
  { name: 'St Francis Bay', country: 'South Africa' },
  { name: 'Addo Elephant National Park', country: 'South Africa' },
  { name: 'Shamwari Game Reserve', country: 'South Africa' },
  { name: 'Amakhala Game Reserve', country: 'South Africa' },
  { name: 'Kariega Game Reserve', country: 'South Africa' },
  { name: 'Pumba Private Game Reserve', country: 'South Africa' },
  { name: 'Drakensberg Mountains', country: 'South Africa' },
  { name: 'Royal Natal National Park', country: 'South Africa' },
  { name: 'Amphitheatre', country: 'South Africa' },
  { name: 'Tugela Falls', country: 'South Africa' },
  { name: 'Cathedral Peak', country: 'South Africa' },
  { name: 'Giant\'s Castle', country: 'South Africa' },
  { name: 'Sani Pass', country: 'South Africa' },
  { name: 'Underberg', country: 'South Africa' },
  { name: 'Himeville', country: 'South Africa' },
  { name: 'Winterton', country: 'South Africa' },
  { name: 'Champagne Castle', country: 'South Africa' },
  { name: 'Monk\'s Cowl', country: 'South Africa' },
  { name: 'uKhahlamba-Drakensberg Park', country: 'South Africa' },
  { name: 'Hluhluwe-iMfolozi Park', country: 'South Africa' },
  { name: 'iSimangaliso Wetland Park', country: 'South Africa' },
  { name: 'St Lucia', country: 'South Africa' },
  { name: 'Sodwana Bay', country: 'South Africa' },
  { name: 'Cape Vidal', country: 'South Africa' },
  { name: 'Kosi Bay', country: 'South Africa' },
  { name: 'Phinda Private Game Reserve', country: 'South Africa' },
  { name: 'Thanda Safari', country: 'South Africa' },
  { name: 'Zululand', country: 'South Africa' },
  { name: 'Battlefields Route', country: 'South Africa' },
  { name: 'Ladysmith', country: 'South Africa' },
  { name: 'Dundee', country: 'South Africa' },
  { name: 'Spioenkop', country: 'South Africa' },
  { name: 'Isandlwana', country: 'South Africa' },
  { name: 'Rorke\'s Drift', country: 'South Africa' },
  { name: 'Blood River', country: 'South Africa' },
  { name: 'Eshowe', country: 'South Africa' },
  { name: 'Ulundi', country: 'South Africa' },
  { name: 'Ballito', country: 'South Africa' },
  { name: 'Umhlanga', country: 'South Africa' },
  { name: 'Umdloti', country: 'South Africa' },
  { name: 'Salt Rock', country: 'South Africa' },
  { name: 'Shaka\'s Rock', country: 'South Africa' },
  { name: 'Zimbali', country: 'South Africa' },
  { name: 'Madikwe Game Reserve', country: 'South Africa' },
  { name: 'Pilanesberg National Park', country: 'South Africa' },
  { name: 'Sun City', country: 'South Africa' },
  { name: 'Waterberg', country: 'South Africa' },
  { name: 'Marakele National Park', country: 'South Africa' },
  { name: 'Limpopo', country: 'South Africa' },
  { name: 'Thabazimbi', country: 'South Africa' },
  { name: 'Vaalwater', country: 'South Africa' },
  { name: 'Bela-Bela', country: 'South Africa' },
  { name: 'Mokopane', country: 'South Africa' },
  { name: 'Makhado', country: 'South Africa' },
  { name: 'Thohoyandou', country: 'South Africa' },
  { name: 'Phalaborwa', country: 'South Africa' },
  { name: 'Kgalagadi Transfrontier Park', country: 'South Africa' },
  { name: 'Augrabies Falls', country: 'South Africa' },
  { name: 'Upington', country: 'South Africa' },
  { name: 'Springbok', country: 'South Africa' },
  { name: 'Namaqualand', country: 'South Africa' },
  { name: 'Namakwa', country: 'South Africa' },
  { name: 'Richtersveld', country: 'South Africa' },
  { name: 'Cederberg', country: 'South Africa' },
  { name: 'Clanwilliam', country: 'South Africa' },
  { name: 'Citrusdal', country: 'South Africa' },
  { name: 'Vanrhynsdorp', country: 'South Africa' },
  { name: 'Calvinia', country: 'South Africa' },
  { name: 'Sutherland', country: 'South Africa' },
  { name: 'Karoo National Park', country: 'South Africa' },
  { name: 'Beaufort West', country: 'South Africa' },
  { name: 'Prince Albert', country: 'South Africa' },
  { name: 'Graaff-Reinet', country: 'South Africa' },
  { name: 'Camdeboo National Park', country: 'South Africa' },
  { name: 'Valley of Desolation', country: 'South Africa' },
  { name: 'Nieu-Bethesda', country: 'South Africa' },
  { name: 'Owl House', country: 'South Africa' },
  { name: 'Mountain Zebra National Park', country: 'South Africa' },
  { name: 'Cradock', country: 'South Africa' },
  { name: 'Grahamstown', country: 'South Africa' },
  { name: 'Makhanda', country: 'South Africa' },
  { name: 'Port Alfred', country: 'South Africa' },
  { name: 'Kenton-on-Sea', country: 'South Africa' },
  { name: 'Coffee Bay', country: 'South Africa' },
  { name: 'Hole in the Wall', country: 'South Africa' },
  { name: 'Wild Coast', country: 'South Africa' },
  { name: 'Port St Johns', country: 'South Africa' },
  { name: 'Mbotyi', country: 'South Africa' },
  { name: 'Pondoland', country: 'South Africa' },
  { name: 'Mthatha', country: 'South Africa' },
  { name: 'Queenstown', country: 'South Africa' },
  { name: 'Aliwal North', country: 'South Africa' },
  { name: 'Golden Gate Highlands National Park', country: 'South Africa' },
  { name: 'Clarens', country: 'South Africa' },
  { name: 'Maloti-Drakensberg Park', country: 'South Africa' },
  { name: 'Fouriesburg', country: 'South Africa' },
  { name: 'Bethlehem', country: 'South Africa' },
  { name: 'Harrismith', country: 'South Africa' },
  { name: 'Phuthaditjhaba', country: 'South Africa' },
  { name: 'Welkom', country: 'South Africa' },
  { name: 'Kroonstad', country: 'South Africa' },
  { name: 'Parys', country: 'South Africa' },
  { name: 'Vredefort Dome', country: 'South Africa' },
  
  // Zimbabwe - Expanded
  { name: 'Harare', country: 'Zimbabwe' },
  { name: 'Bulawayo', country: 'Zimbabwe' },
  { name: 'Victoria Falls', country: 'Zimbabwe' },
  { name: 'Livingstone', country: 'Zimbabwe' },
  { name: 'Hwange National Park', country: 'Zimbabwe' },
  { name: 'Mana Pools National Park', country: 'Zimbabwe' },
  { name: 'Matobo National Park', country: 'Zimbabwe' },
  { name: 'Great Zimbabwe', country: 'Zimbabwe' },
  { name: 'Masvingo', country: 'Zimbabwe' },
  { name: 'Mutare', country: 'Zimbabwe' },
  { name: 'Gweru', country: 'Zimbabwe' },
  { name: 'Kwekwe', country: 'Zimbabwe' },
  { name: 'Kadoma', country: 'Zimbabwe' },
  { name: 'Chinhoyi', country: 'Zimbabwe' },
  { name: 'Kariba', country: 'Zimbabwe' },
  { name: 'Lake Kariba', country: 'Zimbabwe' },
  { name: 'Binga', country: 'Zimbabwe' },
  { name: 'Nyanga', country: 'Zimbabwe' },
  { name: 'Eastern Highlands', country: 'Zimbabwe' },
  { name: 'Chimanimani', country: 'Zimbabwe' },
  { name: 'Vumba Mountains', country: 'Zimbabwe' },
  { name: 'Gonarezhou National Park', country: 'Zimbabwe' },
  { name: 'Chizarira National Park', country: 'Zimbabwe' },
  { name: 'Zambezi River', country: 'Zimbabwe' },
  
  // Zambia - Expanded
  { name: 'Lusaka', country: 'Zambia' },
  { name: 'Ndola', country: 'Zambia' },
  { name: 'Kitwe', country: 'Zambia' },
  { name: 'Livingstone', country: 'Zambia' },
  { name: 'Victoria Falls (Zambia)', country: 'Zambia' },
  { name: 'South Luangwa National Park', country: 'Zambia' },
  { name: 'Lower Zambezi National Park', country: 'Zambia' },
  { name: 'North Luangwa National Park', country: 'Zambia' },
  { name: 'Kafue National Park', country: 'Zambia' },
  { name: 'Liuwa Plain National Park', country: 'Zambia' },
  { name: 'Lake Tanganyika', country: 'Zambia' },
  { name: 'Kasama', country: 'Zambia' },
  { name: 'Mansa', country: 'Zambia' },
  { name: 'Mongu', country: 'Zambia' },
  { name: 'Chipata', country: 'Zambia' },
  { name: 'Solwezi', country: 'Zambia' },
  { name: 'Kabwe', country: 'Zambia' },
  { name: 'Chingola', country: 'Zambia' },
  { name: 'Mufulira', country: 'Zambia' },
  { name: 'Luanshya', country: 'Zambia' },
  { name: 'Bangweulu Wetlands', country: 'Zambia' },
  { name: 'Kasanka National Park', country: 'Zambia' },
  { name: 'Shiwa Ng\'andu', country: 'Zambia' },
  
  // Botswana - Expanded
  { name: 'Gaborone', country: 'Botswana' },
  { name: 'Francistown', country: 'Botswana' },
  { name: 'Maun', country: 'Botswana' },
  { name: 'Kasane', country: 'Botswana' },
  { name: 'Okavango Delta', country: 'Botswana' },
  { name: 'Chobe National Park', country: 'Botswana' },
  { name: 'Moremi Game Reserve', country: 'Botswana' },
  { name: 'Savuti', country: 'Botswana' },
  { name: 'Linyanti', country: 'Botswana' },
  { name: 'Makgadikgadi Pans', country: 'Botswana' },
  { name: 'Nxai Pan', country: 'Botswana' },
  { name: 'Kalahari Desert', country: 'Botswana' },
  { name: 'Central Kalahari Game Reserve', country: 'Botswana' },
  { name: 'Tsodilo Hills', country: 'Botswana' },
  { name: 'Gweta', country: 'Botswana' },
  { name: 'Nata', country: 'Botswana' },
  { name: 'Serowe', country: 'Botswana' },
  { name: 'Palapye', country: 'Botswana' },
  { name: 'Lobatse', country: 'Botswana' },
  { name: 'Jwaneng', country: 'Botswana' },
  { name: 'Selebi-Phikwe', country: 'Botswana' },
  { name: 'Khama Rhino Sanctuary', country: 'Botswana' },
  { name: 'Tuli Block', country: 'Botswana' },
  
  // Namibia - Expanded
  { name: 'Windhoek', country: 'Namibia' },
  { name: 'Swakopmund', country: 'Namibia' },
  { name: 'Walvis Bay', country: 'Namibia' },
  { name: 'Sossusvlei', country: 'Namibia' },
  { name: 'Etosha National Park', country: 'Namibia' },
  { name: 'Namib-Naukluft National Park', country: 'Namibia' },
  { name: 'Fish River Canyon', country: 'Namibia' },
  { name: 'Skeleton Coast', country: 'Namibia' },
  { name: 'Damaraland', country: 'Namibia' },
  { name: 'Kaokoland', country: 'Namibia' },
  { name: 'Caprivi Strip', country: 'Namibia' },
  { name: 'Zambezi Region', country: 'Namibia' },
  { name: 'Katima Mulilo', country: 'Namibia' },
  { name: 'Rundu', country: 'Namibia' },
  { name: 'Oshakati', country: 'Namibia' },
  { name: 'Ondangwa', country: 'Namibia' },
  { name: 'Tsumeb', country: 'Namibia' },
  { name: 'Otjiwarongo', country: 'Namibia' },
  { name: 'Outjo', country: 'Namibia' },
  { name: 'Kamanjab', country: 'Namibia' },
  { name: 'Opuwo', country: 'Namibia' },
  { name: 'Grootfontein', country: 'Namibia' },
  { name: 'Keetmanshoop', country: 'Namibia' },
  { name: 'Lüderitz', country: 'Namibia' },
  { name: 'Kolmanskop', country: 'Namibia' },
  { name: 'Aus', country: 'Namibia' },
  { name: 'Sesriem', country: 'Namibia' },
  { name: 'Deadvlei', country: 'Namibia' },
  { name: 'Dune 45', country: 'Namibia' },
  { name: 'Big Daddy Dune', country: 'Namibia' },
  { name: 'Spitzkoppe', country: 'Namibia' },
  { name: 'Twyfelfontein', country: 'Namibia' },
  { name: 'Brandberg', country: 'Namibia' },
  { name: 'Cape Cross', country: 'Namibia' },
  { name: 'Sandwich Harbour', country: 'Namibia' },
  { name: 'Omaruru', country: 'Namibia' },
  { name: 'Usakos', country: 'Namibia' },
  { name: 'Karibib', country: 'Namibia' },
  { name: 'Henties Bay', country: 'Namibia' },
  { name: 'Terrace Bay', country: 'Namibia' },
  { name: 'Palmwag', country: 'Namibia' },
  { name: 'Sesfontein', country: 'Namibia' },
  { name: 'Epupa Falls', country: 'Namibia' },
  { name: 'Ruacana Falls', country: 'Namibia' },
  { name: 'Waterberg Plateau', country: 'Namibia' },
  { name: 'Erongo Mountains', country: 'Namibia' },
  { name: 'Naukluft Mountains', country: 'Namibia' },
  { name: 'Quiver Tree Forest', country: 'Namibia' },
  { name: 'Giant\'s Playground', country: 'Namibia' },
  { name: 'Bwabwata National Park', country: 'Namibia' },
  { name: 'Mudumu National Park', country: 'Namibia' },
  { name: 'Mahango Game Reserve', country: 'Namibia' },
  { name: 'Popa Falls', country: 'Namibia' },
  
  // Mozambique - Expanded
  { name: 'Maputo', country: 'Mozambique' },
  { name: 'Beira', country: 'Mozambique' },
  { name: 'Nampula', country: 'Mozambique' },
  { name: 'Pemba', country: 'Mozambique' },
  { name: 'Inhambane', country: 'Mozambique' },
  { name: 'Quelimane', country: 'Mozambique' },
  { name: 'Tete', country: 'Mozambique' },
  { name: 'Xai-Xai', country: 'Mozambique' },
  { name: 'Maxixe', country: 'Mozambique' },
  { name: 'Lichinga', country: 'Mozambique' },
  { name: 'Chimoio', country: 'Mozambique' },
  { name: 'Nacala', country: 'Mozambique' },
  { name: 'Vilankulo', country: 'Mozambique' },
  { name: 'Bazaruto Archipelago', country: 'Mozambique' },
  { name: 'Bazaruto Island', country: 'Mozambique' },
  { name: 'Benguerra Island', country: 'Mozambique' },
  { name: 'Magaruque Island', country: 'Mozambique' },
  { name: 'Santa Carolina Island', country: 'Mozambique' },
  { name: 'Quirimbas Archipelago', country: 'Mozambique' },
  { name: 'Ibo Island', country: 'Mozambique' },
  { name: 'Quirimba Island', country: 'Mozambique' },
  { name: 'Matemo Island', country: 'Mozambique' },
  { name: 'Medjumbe Island', country: 'Mozambique' },
  { name: 'Vamizi Island', country: 'Mozambique' },
  { name: 'Mozambique Island', country: 'Mozambique' },
  { name: 'Tofo Beach', country: 'Mozambique' },
  { name: 'Barra Beach', country: 'Mozambique' },
  { name: 'Ponta do Ouro', country: 'Mozambique' },
  { name: 'Ponta Malongane', country: 'Mozambique' },
  { name: 'Ponta Mamoli', country: 'Mozambique' },
  { name: 'Bilene', country: 'Mozambique' },
  { name: 'Zavora', country: 'Mozambique' },
  { name: 'Morrungulo', country: 'Mozambique' },
  { name: 'Pomene', country: 'Mozambique' },
  { name: 'Limpopo National Park', country: 'Mozambique' },
  { name: 'Gorongosa National Park', country: 'Mozambique' },
  { name: 'Niassa Reserve', country: 'Mozambique' },
  { name: 'Lake Malawi (Mozambique)', country: 'Mozambique' },
  { name: 'Lake Niassa', country: 'Mozambique' },
  { name: 'Cahora Bassa Dam', country: 'Mozambique' },
  { name: 'Gorongosa Mountain', country: 'Mozambique' },
  
  // Madagascar - Expanded
  { name: 'Antananarivo', country: 'Madagascar' },
  { name: 'Toamasina', country: 'Madagascar' },
  { name: 'Antsirabe', country: 'Madagascar' },
  { name: 'Mahajanga', country: 'Madagascar' },
  { name: 'Toliara', country: 'Madagascar' },
  { name: 'Fianarantsoa', country: 'Madagascar' },
  { name: 'Antsiranana', country: 'Madagascar' },
  { name: 'Diego Suarez', country: 'Madagascar' },
  { name: 'Nosy Be', country: 'Madagascar' },
  { name: 'Sainte Marie', country: 'Madagascar' },
  { name: 'Nosy Boraha', country: 'Madagascar' },
  { name: 'Andasibe-Mantadia National Park', country: 'Madagascar' },
  { name: 'Ranomafana National Park', country: 'Madagascar' },
  { name: 'Isalo National Park', country: 'Madagascar' },
  { name: 'Ankarafantsika National Park', country: 'Madagascar' },
  { name: 'Tsingy de Bemaraha', country: 'Madagascar' },
  { name: 'Avenue of the Baobabs', country: 'Madagascar' },
  { name: 'Morondava', country: 'Madagascar' },
  { name: 'Ifaty', country: 'Madagascar' },
  { name: 'Anakao', country: 'Madagascar' },
  { name: 'Fort Dauphin', country: 'Madagascar' },
  { name: 'Tolagnaro', country: 'Madagascar' },
  { name: 'Berenty Reserve', country: 'Madagascar' },
  { name: 'Andohahela National Park', country: 'Madagascar' },
  { name: 'Amber Mountain National Park', country: 'Madagascar' },
  { name: 'Ankarana Reserve', country: 'Madagascar' },
  { name: 'Montagne d\'Ambre', country: 'Madagascar' },
  { name: 'Masoala National Park', country: 'Madagascar' },
  { name: 'Marojejy National Park', country: 'Madagascar' },
  { name: 'Zahamena National Park', country: 'Madagascar' },
  { name: 'Mantadia National Park', country: 'Madagascar' },
  { name: 'Zombitse-Vohibasia National Park', country: 'Madagascar' },
  { name: 'Kirindy Forest', country: 'Madagascar' },
  { name: 'Beza Mahafaly Reserve', country: 'Madagascar' },
  { name: 'Andringitra National Park', country: 'Madagascar' },
  { name: 'Manakara', country: 'Madagascar' },
  { name: 'Ambinanitelo', country: 'Madagascar' },
  { name: 'Mandrare River', country: 'Madagascar' },
  { name: 'Ifotaka', country: 'Madagascar' },
  { name: 'Lac Alaotra', country: 'Madagascar' },
  { name: 'Ambositra', country: 'Madagascar' },
  { name: 'Antsohihy', country: 'Madagascar' },
  { name: 'Bekopaka', country: 'Madagascar' },
  { name: 'Maroantsetra', country: 'Madagascar' },
  { name: 'Sambava', country: 'Madagascar' },
  { name: 'Vohemar', country: 'Madagascar' },
  { name: 'Ambanja', country: 'Madagascar' },
  { name: 'Ankify', country: 'Madagascar' },
  { name: 'Nosy Komba', country: 'Madagascar' },
  { name: 'Nosy Tanikely', country: 'Madagascar' },
  { name: 'Nosy Sakatia', country: 'Madagascar' },
  { name: 'Nosy Mitsio', country: 'Madagascar' },
  { name: 'Nosy Iranja', country: 'Madagascar' },
  
  // Lesotho
  { name: 'Maseru', country: 'Lesotho' },
  { name: 'Teyateyaneng', country: 'Lesotho' },
  { name: 'Mafeteng', country: 'Lesotho' },
  { name: 'Leribe', country: 'Lesotho' },
  { name: 'Mohale\'s Hoek', country: 'Lesotho' },
  { name: 'Maputsoe', country: 'Lesotho' },
  { name: 'Qacha\'s Nek', country: 'Lesotho' },
  { name: 'Quthing', country: 'Lesotho' },
  { name: 'Butha-Buthe', country: 'Lesotho' },
  { name: 'Mokhotlong', country: 'Lesotho' },
  { name: 'Thaba-Tseka', country: 'Lesotho' },
  { name: 'Sehlabathebe National Park', country: 'Lesotho' },
  { name: 'Ts\'ehlanyane National Park', country: 'Lesotho' },
  { name: 'Bokong Nature Reserve', country: 'Lesotho' },
  { name: 'Katse Dam', country: 'Lesotho' },
  { name: 'Mohale Dam', country: 'Lesotho' },
  { name: 'Maletsunyane Falls', country: 'Lesotho' },
  { name: 'Semonkong', country: 'Lesotho' },
  { name: 'Malealea', country: 'Lesotho' },
  { name: 'Morija', country: 'Lesotho' },
  { name: 'Thaba Bosiu', country: 'Lesotho' },
  { name: 'Afriski Mountain Resort', country: 'Lesotho' },
  
  // Eswatini (Swaziland)
  { name: 'Mbabane', country: 'Eswatini' },
  { name: 'Manzini', country: 'Eswatini' },
  { name: 'Lobamba', country: 'Eswatini' },
  { name: 'Big Bend', country: 'Eswatini' },
  { name: 'Malkerns', country: 'Eswatini' },
  { name: 'Piggs Peak', country: 'Eswatini' },
  { name: 'Siteki', country: 'Eswatini' },
  { name: 'Nhlangano', country: 'Eswatini' },
  { name: 'Hlane Royal National Park', country: 'Eswatini' },
  { name: 'Mlilwane Wildlife Sanctuary', country: 'Eswatini' },
  { name: 'Mkhaya Game Reserve', country: 'Eswatini' },
  { name: 'Malolotja Nature Reserve', country: 'Eswatini' },
  { name: 'Mlawula Nature Reserve', country: 'Eswatini' },
  { name: 'Mantenga Nature Reserve', country: 'Eswatini' },
  { name: 'Ezulwini Valley', country: 'Eswatini' },
  { name: 'Ngwenya', country: 'Eswatini' },
  { name: 'Hawane', country: 'Eswatini' },
  { name: 'Maguga Dam', country: 'Eswatini' },

  // North Africa - Moderate preparation
  { name: 'Cairo', country: 'Egypt' },
  { name: 'Alexandria', country: 'Egypt' },
  { name: 'Luxor', country: 'Egypt' },
  { name: 'Aswan', country: 'Egypt' },
  { name: 'Sharm El Sheikh', country: 'Egypt' },
  { name: 'Hurghada', country: 'Egypt' },
  { name: 'Dahab', country: 'Egypt' },
  { name: 'Giza', country: 'Egypt' },
  { name: 'Port Said', country: 'Egypt' },
  { name: 'Suez', country: 'Egypt' },
  { name: 'Ismailia', country: 'Egypt' },
  { name: 'Fayoum', country: 'Egypt' },
  { name: 'Marsa Alam', country: 'Egypt' },
  { name: 'El Gouna', country: 'Egypt' },
  { name: 'Safaga', country: 'Egypt' },
  { name: 'Soma Bay', country: 'Egypt' },
  { name: 'Makadi Bay', country: 'Egypt' },
  { name: 'Nuweiba', country: 'Egypt' },
  { name: 'Taba', country: 'Egypt' },
  { name: 'Abu Simbel', country: 'Egypt' },
  { name: 'Kom Ombo', country: 'Egypt' },
  { name: 'Edfu', country: 'Egypt' },
  { name: 'Karnak', country: 'Egypt' },
  { name: 'Valley of the Kings', country: 'Egypt' },
  { name: 'Siwa Oasis', country: 'Egypt' },
  { name: 'Bahariya Oasis', country: 'Egypt' },
  { name: 'White Desert', country: 'Egypt' },
  { name: 'Black Desert', country: 'Egypt' },
  { name: 'Ras Muhammad', country: 'Egypt' },
  { name: 'Mount Sinai', country: 'Egypt' },
  { name: 'St. Catherine\'s Monastery', country: 'Egypt' },
  { name: 'Philae Temple', country: 'Egypt' },
  
  { name: 'Marrakech', country: 'Morocco' },
  { name: 'Casablanca', country: 'Morocco' },
  { name: 'Rabat', country: 'Morocco' },
  { name: 'Fez', country: 'Morocco' },
  { name: 'Tangier', country: 'Morocco' },
  { name: 'Chefchaouen', country: 'Morocco' },
  { name: 'Essaouira', country: 'Morocco' },
  { name: 'Agadir', country: 'Morocco' },
  { name: 'Ouarzazate', country: 'Morocco' },
  { name: 'Merzouga', country: 'Morocco' },
  { name: 'Meknes', country: 'Morocco' },
  { name: 'Tetouan', country: 'Morocco' },
  { name: 'Asilah', country: 'Morocco' },
  { name: 'El Jadida', country: 'Morocco' },
  { name: 'Safi', country: 'Morocco' },
  { name: 'Ifrane', country: 'Morocco' },
  { name: 'Taroudant', country: 'Morocco' },
  { name: 'Zagora', country: 'Morocco' },
  { name: 'M\'hamid', country: 'Morocco' },
  { name: 'Dades Valley', country: 'Morocco' },
  { name: 'Todra Gorge', country: 'Morocco' },
  { name: 'Ait Benhaddou', country: 'Morocco' },
  { name: 'Erfoud', country: 'Morocco' },
  { name: 'Rissani', country: 'Morocco' },
  { name: 'Tafraoute', country: 'Morocco' },
  { name: 'Tiznit', country: 'Morocco' },
  { name: 'Mirleft', country: 'Morocco' },
  { name: 'Sidi Ifni', country: 'Morocco' },
  { name: 'Dakhla', country: 'Morocco' },
  { name: 'Laayoune', country: 'Morocco' },
  { name: 'Boumalne Dades', country: 'Morocco' },
  { name: 'Tinghir', country: 'Morocco' },
  { name: 'Midelt', country: 'Morocco' },
  { name: 'Azrou', country: 'Morocco' },
  { name: 'Beni Mellal', country: 'Morocco' },
  { name: 'Kenitra', country: 'Morocco' },
  { name: 'Larache', country: 'Morocco' },
  { name: 'Khouribga', country: 'Morocco' },
  { name: 'Nador', country: 'Morocco' },
  { name: 'Oujda', country: 'Morocco' },
  { name: 'Taza', country: 'Morocco' },
  { name: 'Errachidia', country: 'Morocco' },
  { name: 'Guelmim', country: 'Morocco' },
  { name: 'Tan-Tan', country: 'Morocco' },
  
  { name: 'Tunis', country: 'Tunisia' },
  { name: 'Sousse', country: 'Tunisia' },
  { name: 'Sfax', country: 'Tunisia' },
  { name: 'Kairouan', country: 'Tunisia' },
  { name: 'Djerba', country: 'Tunisia' },
  { name: 'Monastir', country: 'Tunisia' },
  { name: 'Hammamet', country: 'Tunisia' },
  { name: 'Nabeul', country: 'Tunisia' },
  { name: 'Bizerte', country: 'Tunisia' },
  { name: 'Gabès', country: 'Tunisia' },
  { name: 'Tozeur', country: 'Tunisia' },
  { name: 'Douz', country: 'Tunisia' },
  { name: 'Matmata', country: 'Tunisia' },
  { name: 'Ksar Ghilane', country: 'Tunisia' },
  { name: 'Chott el Djerid', country: 'Tunisia' },
  { name: 'El Jem', country: 'Tunisia' },
  { name: 'Carthage', country: 'Tunisia' },
  { name: 'Sidi Bou Said', country: 'Tunisia' },
  { name: 'Tabarka', country: 'Tunisia' },
  { name: 'Ain Draham', country: 'Tunisia' },
  { name: 'Kebili', country: 'Tunisia' },
  { name: 'Tataouine', country: 'Tunisia' },
  { name: 'Mahdia', country: 'Tunisia' },
  { name: 'Kerkennah Islands', country: 'Tunisia' },
  
  { name: 'Tripoli', country: 'Libya' },
  { name: 'Benghazi', country: 'Libya' },
  { name: 'Misrata', country: 'Libya' },
  { name: 'Bayda', country: 'Libya' },
  { name: 'Tobruk', country: 'Libya' },
  { name: 'Zawiya', country: 'Libya' },
  { name: 'Ajdabiya', country: 'Libya' },
  { name: 'Sabha', country: 'Libya' },
  { name: 'Ghat', country: 'Libya' },
  { name: 'Ghadames', country: 'Libya' },
  { name: 'Leptis Magna', country: 'Libya' },
  { name: 'Sabratha', country: 'Libya' },
  { name: 'Cyrene', country: 'Libya' },
  { name: 'Ubari', country: 'Libya' },
  { name: 'Ubari Lakes', country: 'Libya' },
  { name: 'Akakus Mountains', country: 'Libya' },
  
  { name: 'Algiers', country: 'Algeria' },
  { name: 'Oran', country: 'Algeria' },
  { name: 'Constantine', country: 'Algeria' },
  { name: 'Annaba', country: 'Algeria' },
  { name: 'Blida', country: 'Algeria' },
  { name: 'Batna', country: 'Algeria' },
  { name: 'Djelfa', country: 'Algeria' },
  { name: 'Sétif', country: 'Algeria' },
  { name: 'Sidi Bel Abbès', country: 'Algeria' },
  { name: 'Biskra', country: 'Algeria' },
  { name: 'Tébessa', country: 'Algeria' },
  { name: 'El Oued', country: 'Algeria' },
  { name: 'Skikda', country: 'Algeria' },
  { name: 'Tiaret', country: 'Algeria' },
  { name: 'Béjaïa', country: 'Algeria' },
  { name: 'Tlemcen', country: 'Algeria' },
  { name: 'Médéa', country: 'Algeria' },
  { name: 'El Eulma', country: 'Algeria' },
  { name: 'Bordj Bou Arréridj', country: 'Algeria' },
  { name: 'Chlef', country: 'Algeria' },
  { name: 'M\'Sila', country: 'Algeria' },
  { name: 'Tamanrasset', country: 'Algeria' },
  { name: 'Ghardaïa', country: 'Algeria' },
  { name: 'Timimoun', country: 'Algeria' },
  { name: 'Ahaggar Mountains', country: 'Algeria' },
  { name: 'Tassili n\'Ajjer', country: 'Algeria' },
  { name: 'Djémila', country: 'Algeria' },
  { name: 'Timgad', country: 'Algeria' },
  { name: 'Tipaza', country: 'Algeria' },
  { name: 'Beni Hammad Fort', country: 'Algeria' },
  
  { name: 'Khartoum', country: 'Sudan' },
  { name: 'Port Sudan', country: 'Sudan' },
  { name: 'Kassala', country: 'Sudan' },
  { name: 'Omdurman', country: 'Sudan' },
  { name: 'Nyala', country: 'Sudan' },
  { name: 'El Obeid', country: 'Sudan' },
  { name: 'El Fasher', country: 'Sudan' },
  { name: 'Wad Madani', country: 'Sudan' },
  { name: 'Kosti', country: 'Sudan' },
  { name: 'Atbara', country: 'Sudan' },
  { name: 'Dongola', country: 'Sudan' },
  { name: 'Juba', country: 'Sudan' },
  { name: 'Meroe Pyramids', country: 'Sudan' },
  { name: 'Karima', country: 'Sudan' },
  { name: 'Suakin', country: 'Sudan' },

  // ASIA - High Preparation Destinations
  
  // Southeast Asia - Moderate to high preparation
  
  // Thailand - Extensive coverage
  { name: 'Bangkok', country: 'Thailand' },
  { name: 'Chiang Mai', country: 'Thailand' },
  { name: 'Chiang Rai', country: 'Thailand' },
  { name: 'Phuket', country: 'Thailand' },
  { name: 'Pattaya', country: 'Thailand' },
  { name: 'Hat Yai', country: 'Thailand' },
  { name: 'Nakhon Ratchasima', country: 'Thailand' },
  { name: 'Khon Kaen', country: 'Thailand' },
  { name: 'Udon Thani', country: 'Thailand' },
  { name: 'Nakhon Si Thammarat', country: 'Thailand' },
  { name: 'Ayutthaya', country: 'Thailand' },
  { name: 'Sukhothai', country: 'Thailand' },
  { name: 'Kanchanaburi', country: 'Thailand' },
  { name: 'Hua Hin', country: 'Thailand' },
  { name: 'Cha-am', country: 'Thailand' },
  { name: 'Petchaburi', country: 'Thailand' },
  { name: 'Pai', country: 'Thailand' },
  { name: 'Mae Hong Son', country: 'Thailand' },
  { name: 'Chiang Dao', country: 'Thailand' },
  { name: 'Nan', country: 'Thailand' },
  { name: 'Phrae', country: 'Thailand' },
  { name: 'Lampang', country: 'Thailand' },
  { name: 'Phitsanulok', country: 'Thailand' },
  { name: 'Ubon Ratchathani', country: 'Thailand' },
  { name: 'Nong Khai', country: 'Thailand' },
  { name: 'Loei', country: 'Thailand' },
  { name: 'Nakhon Phanom', country: 'Thailand' },
  { name: 'Trat', country: 'Thailand' },
  { name: 'Rayong', country: 'Thailand' },
  { name: 'Chonburi', country: 'Thailand' },
  { name: 'Prachuap Khiri Khan', country: 'Thailand' },
  { name: 'Chumphon', country: 'Thailand' },
  { name: 'Ranong', country: 'Thailand' },
  { name: 'Surat Thani', country: 'Thailand' },
  { name: 'Nakhon Pathom', country: 'Thailand' },
  { name: 'Kanchanaburi', country: 'Thailand' },
  { name: 'Ratchaburi', country: 'Thailand' },
  
  // Thai Islands - Gulf of Thailand
  { name: 'Koh Samui', country: 'Thailand' },
  { name: 'Koh Phangan', country: 'Thailand' },
  { name: 'Koh Tao', country: 'Thailand' },
  { name: 'Koh Chang', country: 'Thailand' },
  { name: 'Koh Kood', country: 'Thailand' },
  { name: 'Koh Mak', country: 'Thailand' },
  { name: 'Koh Samet', country: 'Thailand' },
  { name: 'Koh Larn', country: 'Thailand' },
  { name: 'Koh Sichang', country: 'Thailand' },
  
  // Thai Islands - Andaman Sea
  { name: 'Krabi', country: 'Thailand' },
  { name: 'Ao Nang', country: 'Thailand' },
  { name: 'Railay Beach', country: 'Thailand' },
  { name: 'Koh Phi Phi', country: 'Thailand' },
  { name: 'Koh Lanta', country: 'Thailand' },
  { name: 'Koh Yao Noi', country: 'Thailand' },
  { name: 'Koh Yao Yai', country: 'Thailand' },
  { name: 'Koh Lipe', country: 'Thailand' },
  { name: 'Koh Mook', country: 'Thailand' },
  { name: 'Koh Kradan', country: 'Thailand' },
  { name: 'Koh Ngai', country: 'Thailand' },
  { name: 'Koh Bulon Leh', country: 'Thailand' },
  { name: 'Koh Jum', country: 'Thailand' },
  { name: 'Similan Islands', country: 'Thailand' },
  { name: 'Surin Islands', country: 'Thailand' },
  { name: 'Phang Nga Bay', country: 'Thailand' },
  { name: 'James Bond Island', country: 'Thailand' },
  
  // Vietnam - Extensive coverage
  { name: 'Ho Chi Minh City', country: 'Vietnam' },
  { name: 'Hanoi', country: 'Vietnam' },
  { name: 'Da Nang', country: 'Vietnam' },
  { name: 'Hai Phong', country: 'Vietnam' },
  { name: 'Can Tho', country: 'Vietnam' },
  { name: 'Bien Hoa', country: 'Vietnam' },
  { name: 'Vung Tau', country: 'Vietnam' },
  { name: 'Nha Trang', country: 'Vietnam' },
  { name: 'Hue', country: 'Vietnam' },
  { name: 'Hoi An', country: 'Vietnam' },
  { name: 'Dalat', country: 'Vietnam' },
  { name: 'Quy Nhon', country: 'Vietnam' },
  { name: 'Phan Thiet', country: 'Vietnam' },
  { name: 'Mui Ne', country: 'Vietnam' },
  { name: 'Vinh', country: 'Vietnam' },
  { name: 'Ha Long', country: 'Vietnam' },
  { name: 'Halong Bay', country: 'Vietnam' },
  { name: 'Sapa', country: 'Vietnam' },
  { name: 'Ninh Binh', country: 'Vietnam' },
  { name: 'Tam Coc', country: 'Vietnam' },
  { name: 'Mai Chau', country: 'Vietnam' },
  { name: 'Dong Hoi', country: 'Vietnam' },
  { name: 'Phong Nha', country: 'Vietnam' },
  { name: 'Hoi An Ancient Town', country: 'Vietnam' },
  { name: 'My Son Sanctuary', country: 'Vietnam' },
  { name: 'Cu Chi Tunnels', country: 'Vietnam' },
  { name: 'Mekong Delta', country: 'Vietnam' },
  { name: 'Ba Na Hills', country: 'Vietnam' },
  { name: 'Marble Mountains', country: 'Vietnam' },
  { name: 'Cat Ba Island', country: 'Vietnam' },
  { name: 'Phu Quoc', country: 'Vietnam' },
  { name: 'Con Dao Islands', country: 'Vietnam' },
  { name: 'Cham Islands', country: 'Vietnam' },
  { name: 'Ly Son Island', country: 'Vietnam' },
  { name: 'Nam Du Islands', country: 'Vietnam' },
  { name: 'Whale Island', country: 'Vietnam' },
  { name: 'Quan Lan Island', country: 'Vietnam' },
  { name: 'Co To Island', country: 'Vietnam' },
  
  // Cambodia - Extensive coverage
  { name: 'Phnom Penh', country: 'Cambodia' },
  { name: 'Siem Reap', country: 'Cambodia' },
  { name: 'Angkor Wat', country: 'Cambodia' },
  { name: 'Angkor Thom', country: 'Cambodia' },
  { name: 'Bayon Temple', country: 'Cambodia' },
  { name: 'Ta Prohm', country: 'Cambodia' },
  { name: 'Banteay Srei', country: 'Cambodia' },
  { name: 'Sihanoukville', country: 'Cambodia' },
  { name: 'Battambang', country: 'Cambodia' },
  { name: 'Kampot', country: 'Cambodia' },
  { name: 'Kep', country: 'Cambodia' },
  { name: 'Kratie', country: 'Cambodia' },
  { name: 'Mondulkiri', country: 'Cambodia' },
  { name: 'Ratanakiri', country: 'Cambodia' },
  { name: 'Preah Vihear', country: 'Cambodia' },
  { name: 'Koh Kong', country: 'Cambodia' },
  { name: 'Takeo', country: 'Cambodia' },
  { name: 'Kampong Cham', country: 'Cambodia' },
  { name: 'Kampong Thom', country: 'Cambodia' },
  { name: 'Kampong Chhnang', country: 'Cambodia' },
  { name: 'Tonle Sap Lake', country: 'Cambodia' },
  { name: 'Koh Rong', country: 'Cambodia' },
  { name: 'Koh Rong Samloem', country: 'Cambodia' },
  { name: 'Koh Tonsay (Rabbit Island)', country: 'Cambodia' },
  { name: 'Koh Ta Kiev', country: 'Cambodia' },
  { name: 'Koh Russei (Bamboo Island)', country: 'Cambodia' },
  { name: 'Koh Sdach', country: 'Cambodia' },
  { name: 'Song Saa Private Island', country: 'Cambodia' },
  
  // Laos - Extensive coverage
  { name: 'Vientiane', country: 'Laos' },
  { name: 'Luang Prabang', country: 'Laos' },
  { name: 'Vang Vieng', country: 'Laos' },
  { name: 'Pakse', country: 'Laos' },
  { name: 'Savannakhet', country: 'Laos' },
  { name: 'Thakhek', country: 'Laos' },
  { name: 'Luang Namtha', country: 'Laos' },
  { name: 'Phongsali', country: 'Laos' },
  { name: 'Sam Neua', country: 'Laos' },
  { name: 'Phonsavan', country: 'Laos' },
  { name: 'Plain of Jars', country: 'Laos' },
  { name: 'Champasak', country: 'Laos' },
  { name: 'Paksong', country: 'Laos' },
  { name: 'Nong Khiaw', country: 'Laos' },
  { name: 'Muang Ngoi', country: 'Laos' },
  { name: 'Don Det', country: 'Laos' },
  { name: 'Don Khon', country: 'Laos' },
  { name: '4000 Islands', country: 'Laos' },
  { name: 'Bolaven Plateau', country: 'Laos' },
  { name: 'Wat Phou', country: 'Laos' },
  { name: 'Kuang Si Waterfall', country: 'Laos' },
  { name: 'Kong Lor Cave', country: 'Laos' },
  { name: 'Vieng Xai Caves', country: 'Laos' },
  { name: 'Tat Kuang Si', country: 'Laos' },
  
  // Myanmar - Extensive coverage
  { name: 'Yangon', country: 'Myanmar' },
  { name: 'Mandalay', country: 'Myanmar' },
  { name: 'Naypyidaw', country: 'Myanmar' },
  { name: 'Bagan', country: 'Myanmar' },
  { name: 'Inle Lake', country: 'Myanmar' },
  { name: 'Ngapali Beach', country: 'Myanmar' },
  { name: 'Mawlamyine', country: 'Myanmar' },
  { name: 'Pathein', country: 'Myanmar' },
  { name: 'Myeik', country: 'Myanmar' },
  { name: 'Taunggyi', country: 'Myanmar' },
  { name: 'Pyin Oo Lwin', country: 'Myanmar' },
  { name: 'Kalaw', country: 'Myanmar' },
  { name: 'Hsipaw', country: 'Myanmar' },
  { name: 'Mrauk U', country: 'Myanmar' },
  { name: 'Kyaiktiyo (Golden Rock)', country: 'Myanmar' },
  { name: 'Ngwe Saung Beach', country: 'Myanmar' },
  { name: 'Chaung Tha Beach', country: 'Myanmar' },
  { name: 'Hpa-An', country: 'Myanmar' },
  { name: 'Sagaing', country: 'Myanmar' },
  { name: 'Mingun', country: 'Myanmar' },
  { name: 'Amarapura', country: 'Myanmar' },
  { name: 'Mergui Archipelago', country: 'Myanmar' },
  { name: 'Myeik Archipelago', country: 'Myanmar' },
  
  // Indonesia - Extensive coverage with many islands
  { name: 'Jakarta', country: 'Indonesia' },
  { name: 'Surabaya', country: 'Indonesia' },
  { name: 'Bandung', country: 'Indonesia' },
  { name: 'Medan', country: 'Indonesia' },
  { name: 'Semarang', country: 'Indonesia' },
  { name: 'Palembang', country: 'Indonesia' },
  { name: 'Makassar', country: 'Indonesia' },
  { name: 'Tangerang', country: 'Indonesia' },
  { name: 'Depok', country: 'Indonesia' },
  { name: 'Batam', country: 'Indonesia' },
  { name: 'Pekanbaru', country: 'Indonesia' },
  { name: 'Bogor', country: 'Indonesia' },
  { name: 'Padang', country: 'Indonesia' },
  { name: 'Malang', country: 'Indonesia' },
  { name: 'Denpasar', country: 'Indonesia' },
  { name: 'Yogyakarta', country: 'Indonesia' },
  { name: 'Solo (Surakarta)', country: 'Indonesia' },
  { name: 'Pontianak', country: 'Indonesia' },
  { name: 'Banjarmasin', country: 'Indonesia' },
  { name: 'Manado', country: 'Indonesia' },
  { name: 'Balikpapan', country: 'Indonesia' },
  { name: 'Samarinda', country: 'Indonesia' },
  { name: 'Mataram', country: 'Indonesia' },
  { name: 'Ambon', country: 'Indonesia' },
  { name: 'Kupang', country: 'Indonesia' },
  { name: 'Jayapura', country: 'Indonesia' },
  { name: 'Sorong', country: 'Indonesia' },
  { name: 'Ternate', country: 'Indonesia' },
  { name: 'Tidore', country: 'Indonesia' },
  { name: 'Palu', country: 'Indonesia' },
  { name: 'Gorontalo', country: 'Indonesia' },
  { name: 'Kendari', country: 'Indonesia' },
  
  // Bali & nearby islands
  { name: 'Bali', country: 'Indonesia' },
  { name: 'Ubud', country: 'Indonesia' },
  { name: 'Seminyak', country: 'Indonesia' },
  { name: 'Canggu', country: 'Indonesia' },
  { name: 'Kuta', country: 'Indonesia' },
  { name: 'Sanur', country: 'Indonesia' },
  { name: 'Nusa Dua', country: 'Indonesia' },
  { name: 'Jimbaran', country: 'Indonesia' },
  { name: 'Uluwatu', country: 'Indonesia' },
  { name: 'Amed', country: 'Indonesia' },
  { name: 'Lovina', country: 'Indonesia' },
  { name: 'Candidasa', country: 'Indonesia' },
  { name: 'Padang Bai', country: 'Indonesia' },
  { name: 'Munduk', country: 'Indonesia' },
  { name: 'Sidemen', country: 'Indonesia' },
  { name: 'Nusa Penida', country: 'Indonesia' },
  { name: 'Nusa Lembongan', country: 'Indonesia' },
  { name: 'Nusa Ceningan', country: 'Indonesia' },
  
  // Lombok & Gili Islands
  { name: 'Lombok', country: 'Indonesia' },
  { name: 'Senggigi', country: 'Indonesia' },
  { name: 'Kuta Lombok', country: 'Indonesia' },
  { name: 'Selong Belanak', country: 'Indonesia' },
  { name: 'Mount Rinjani', country: 'Indonesia' },
  { name: 'Gili Trawangan', country: 'Indonesia' },
  { name: 'Gili Meno', country: 'Indonesia' },
  { name: 'Gili Air', country: 'Indonesia' },
  { name: 'Gili Nanggu', country: 'Indonesia' },
  { name: 'Gili Gede', country: 'Indonesia' },
  { name: 'Gili Asahan', country: 'Indonesia' },
  
  // Java
  { name: 'Borobudur', country: 'Indonesia' },
  { name: 'Prambanan', country: 'Indonesia' },
  { name: 'Mount Bromo', country: 'Indonesia' },
  { name: 'Ijen Crater', country: 'Indonesia' },
  { name: 'Karimunjawa Islands', country: 'Indonesia' },
  { name: 'Thousand Islands (Pulau Seribu)', country: 'Indonesia' },
  { name: 'Pangandaran', country: 'Indonesia' },
  { name: 'Banyuwangi', country: 'Indonesia' },
  
  // Sumatra
  { name: 'Sumatra', country: 'Indonesia' },
  { name: 'Lake Toba', country: 'Indonesia' },
  { name: 'Bukit Lawang', country: 'Indonesia' },
  { name: 'Bukittinggi', country: 'Indonesia' },
  { name: 'Danau Maninjau', country: 'Indonesia' },
  { name: 'Mentawai Islands', country: 'Indonesia' },
  { name: 'Nias Island', country: 'Indonesia' },
  { name: 'Pulau Weh', country: 'Indonesia' },
  { name: 'Banda Aceh', country: 'Indonesia' },
  { name: 'Belitung Island', country: 'Indonesia' },
  { name: 'Bangka Island', country: 'Indonesia' },
  
  // Komodo & Flores
  { name: 'Flores', country: 'Indonesia' },
  { name: 'Komodo Island', country: 'Indonesia' },
  { name: 'Rinca Island', country: 'Indonesia' },
  { name: 'Labuan Bajo', country: 'Indonesia' },
  { name: 'Maumere', country: 'Indonesia' },
  { name: 'Ende', country: 'Indonesia' },
  { name: 'Kelimutu', country: 'Indonesia' },
  { name: 'Ruteng', country: 'Indonesia' },
  { name: 'Padar Island', country: 'Indonesia' },
  { name: 'Pink Beach', country: 'Indonesia' },
  
  // Raja Ampat & Papua
  { name: 'Raja Ampat', country: 'Indonesia' },
  { name: 'Waigeo', country: 'Indonesia' },
  { name: 'Batanta', country: 'Indonesia' },
  { name: 'Salawati', country: 'Indonesia' },
  { name: 'Misool', country: 'Indonesia' },
  { name: 'Kri Island', country: 'Indonesia' },
  { name: 'Wayag Islands', country: 'Indonesia' },
  { name: 'Baliem Valley', country: 'Indonesia' },
  
  // Sulawesi
  { name: 'Sulawesi', country: 'Indonesia' },
  { name: 'Bunaken', country: 'Indonesia' },
  { name: 'Togean Islands', country: 'Indonesia' },
  { name: 'Tana Toraja', country: 'Indonesia' },
  { name: 'Wakatobi', country: 'Indonesia' },
  { name: 'Lembeh Strait', country: 'Indonesia' },
  { name: 'Derawan Islands', country: 'Indonesia' },
  
  // Borneo/Kalimantan
  { name: 'Borneo', country: 'Indonesia' },
  { name: 'Tanjung Puting National Park', country: 'Indonesia' },
  { name: 'Derawan', country: 'Indonesia' },
  { name: 'Kakaban Island', country: 'Indonesia' },
  { name: 'Maratua Island', country: 'Indonesia' },
  { name: 'Sangalaki Island', country: 'Indonesia' },
  
  // Maluku Islands
  { name: 'Banda Islands', country: 'Indonesia' },
  { name: 'Ternate Island', country: 'Indonesia' },
  { name: 'Tidore Island', country: 'Indonesia' },
  { name: 'Halmahera', country: 'Indonesia' },
  { name: 'Morotai Island', country: 'Indonesia' },
  { name: 'Seram Island', country: 'Indonesia' },
  
  // Philippines - Extensive coverage with many islands
  { name: 'Manila', country: 'Philippines' },
  { name: 'Quezon City', country: 'Philippines' },
  { name: 'Caloocan', country: 'Philippines' },
  { name: 'Davao City', country: 'Philippines' },
  { name: 'Cebu City', country: 'Philippines' },
  { name: 'Zamboanga City', country: 'Philippines' },
  { name: 'Taguig', country: 'Philippines' },
  { name: 'Antipolo', country: 'Philippines' },
  { name: 'Pasig', country: 'Philippines' },
  { name: 'Cagayan de Oro', country: 'Philippines' },
  { name: 'Parañaque', country: 'Philippines' },
  { name: 'Makati', country: 'Philippines' },
  { name: 'Valenzuela', country: 'Philippines' },
  { name: 'Bacoor', country: 'Philippines' },
  { name: 'General Santos', country: 'Philippines' },
  { name: 'Las Piñas', country: 'Philippines' },
  { name: 'Bacolod', country: 'Philippines' },
  { name: 'Iloilo City', country: 'Philippines' },
  { name: 'Muntinlupa', country: 'Philippines' },
  { name: 'Baguio', country: 'Philippines' },
  { name: 'Angeles City', country: 'Philippines' },
  { name: 'Olongapo', country: 'Philippines' },
  { name: 'Tagaytay', country: 'Philippines' },
  { name: 'Vigan', country: 'Philippines' },
  { name: 'Laoag', country: 'Philippines' },
  { name: 'Puerto Princesa', country: 'Philippines' },
  { name: 'Tacloban', country: 'Philippines' },
  { name: 'Legazpi', country: 'Philippines' },
  { name: 'Naga', country: 'Philippines' },
  { name: 'Butuan', country: 'Philippines' },
  { name: 'Iligan', country: 'Philippines' },
  { name: 'Cotabato', country: 'Philippines' },
  { name: 'Dumaguete', country: 'Philippines' },
  { name: 'Batangas', country: 'Philippines' },
  { name: 'Subic Bay', country: 'Philippines' },
  
  // Luzon Islands
  { name: 'Batanes', country: 'Philippines' },
  { name: 'Batan Island', country: 'Philippines' },
  { name: 'Sabtang Island', country: 'Philippines' },
  { name: 'Itbayat Island', country: 'Philippines' },
  { name: 'Catanduanes', country: 'Philippines' },
  { name: 'Masbate', country: 'Philippines' },
  { name: 'Marinduque', country: 'Philippines' },
  { name: 'Romblon', country: 'Philippines' },
  { name: 'Mindoro', country: 'Philippines' },
  { name: 'Puerto Galera', country: 'Philippines' },
  { name: 'Sablayan', country: 'Philippines' },
  { name: 'Calaguas Islands', country: 'Philippines' },
  { name: 'Caramoan', country: 'Philippines' },
  { name: 'Donsol', country: 'Philippines' },
  
  // Visayas Islands
  { name: 'Cebu', country: 'Philippines' },
  { name: 'Mactan', country: 'Philippines' },
  { name: 'Malapascua', country: 'Philippines' },
  { name: 'Bantayan Island', country: 'Philippines' },
  { name: 'Camotes Islands', country: 'Philippines' },
  { name: 'Moalboal', country: 'Philippines' },
  { name: 'Oslob', country: 'Philippines' },
  { name: 'Bohol', country: 'Philippines' },
  { name: 'Panglao Island', country: 'Philippines' },
  { name: 'Anda', country: 'Philippines' },
  { name: 'Balicasag Island', country: 'Philippines' },
  { name: 'Pamilacan Island', country: 'Philippines' },
  { name: 'Negros', country: 'Philippines' },
  { name: 'Apo Island', country: 'Philippines' },
  { name: 'Siquijor', country: 'Philippines' },
  { name: 'Boracay', country: 'Philippines' },
  { name: 'Aklan', country: 'Philippines' },
  { name: 'Panay', country: 'Philippines' },
  { name: 'Guimaras', country: 'Philippines' },
  { name: 'Islas de Gigantes', country: 'Philippines' },
  { name: 'Sicogon Island', country: 'Philippines' },
  { name: 'Concepcion Islands', country: 'Philippines' },
  { name: 'Samal Island', country: 'Philippines' },
  { name: 'Leyte', country: 'Philippines' },
  { name: 'Kalanggaman Island', country: 'Philippines' },
  { name: 'Biliran', country: 'Philippines' },
  { name: 'Samar', country: 'Philippines' },
  { name: 'Sohoton Cove', country: 'Philippines' },
  
  // Palawan
  { name: 'Palawan', country: 'Philippines' },
  { name: 'El Nido', country: 'Philippines' },
  { name: 'Coron', country: 'Philippines' },
  { name: 'Port Barton', country: 'Philippines' },
  { name: 'San Vicente', country: 'Philippines' },
  { name: 'Taytay', country: 'Philippines' },
  { name: 'Balabac', country: 'Philippines' },
  { name: 'Busuanga', country: 'Philippines' },
  { name: 'Culion', country: 'Philippines' },
  { name: 'Linapacan', country: 'Philippines' },
  { name: 'Calauit Island', country: 'Philippines' },
  { name: 'Dimakya Island', country: 'Philippines' },
  { name: 'Bacuit Archipelago', country: 'Philippines' },
  { name: 'Nacpan Beach', country: 'Philippines' },
  { name: 'Underground River', country: 'Philippines' },
  
  // Mindanao
  { name: 'Siargao', country: 'Philippines' },
  { name: 'Cloud 9', country: 'Philippines' },
  { name: 'Daku Island', country: 'Philippines' },
  { name: 'Guyam Island', country: 'Philippines' },
  { name: 'Naked Island', country: 'Philippines' },
  { name: 'Sohoton Cove Siargao', country: 'Philippines' },
  { name: 'Camiguin', country: 'Philippines' },
  { name: 'White Island Camiguin', country: 'Philippines' },
  { name: 'Mantigue Island', country: 'Philippines' },
  { name: 'Dinagat Islands', country: 'Philippines' },
  { name: 'Bucas Grande', country: 'Philippines' },
  { name: 'Enchanted River', country: 'Philippines' },
  { name: 'Tinuy-an Falls', country: 'Philippines' },
  { name: 'Britania Islands', country: 'Philippines' },
  { name: 'Lake Sebu', country: 'Philippines' },
  { name: 'Dahican Beach', country: 'Philippines' },
  
  // Malaysia - Extensive coverage
  { name: 'Kuala Lumpur', country: 'Malaysia' },
  { name: 'George Town', country: 'Malaysia' },
  { name: 'Penang', country: 'Malaysia' },
  { name: 'Ipoh', country: 'Malaysia' },
  { name: 'Johor Bahru', country: 'Malaysia' },
  { name: 'Shah Alam', country: 'Malaysia' },
  { name: 'Petaling Jaya', country: 'Malaysia' },
  { name: 'Malacca', country: 'Malaysia' },
  { name: 'Kuching', country: 'Malaysia' },
  { name: 'Kota Kinabalu', country: 'Malaysia' },
  { name: 'Klang', country: 'Malaysia' },
  { name: 'Seremban', country: 'Malaysia' },
  { name: 'Kuantan', country: 'Malaysia' },
  { name: 'Kota Bharu', country: 'Malaysia' },
  { name: 'Alor Setar', country: 'Malaysia' },
  { name: 'Miri', country: 'Malaysia' },
  { name: 'Sandakan', country: 'Malaysia' },
  { name: 'Tawau', country: 'Malaysia' },
  { name: 'Labuan', country: 'Malaysia' },
  { name: 'Putrajaya', country: 'Malaysia' },
  { name: 'Cameron Highlands', country: 'Malaysia' },
  { name: 'Genting Highlands', country: 'Malaysia' },
  { name: 'Fraser\'s Hill', country: 'Malaysia' },
  { name: 'Taiping', country: 'Malaysia' },
  { name: 'Kuala Terengganu', country: 'Malaysia' },
  { name: 'Dungun', country: 'Malaysia' },
  { name: 'Mersing', country: 'Malaysia' },
  { name: 'Desaru', country: 'Malaysia' },
  
  // Malaysian Islands - Peninsular
  { name: 'Langkawi', country: 'Malaysia' },
  { name: 'Pulau Payar', country: 'Malaysia' },
  { name: 'Pulau Dayang Bunting', country: 'Malaysia' },
  { name: 'Perhentian Islands', country: 'Malaysia' },
  { name: 'Perhentian Kecil', country: 'Malaysia' },
  { name: 'Perhentian Besar', country: 'Malaysia' },
  { name: 'Redang Island', country: 'Malaysia' },
  { name: 'Tioman Island', country: 'Malaysia' },
  { name: 'Rawa Island', country: 'Malaysia' },
  { name: 'Sibu Island', country: 'Malaysia' },
  { name: 'Tinggi Island', country: 'Malaysia' },
  { name: 'Besar Island', country: 'Malaysia' },
  { name: 'Kapas Island', country: 'Malaysia' },
  { name: 'Tenggol Island', country: 'Malaysia' },
  { name: 'Pangkor Island', country: 'Malaysia' },
  { name: 'Penang Island', country: 'Malaysia' },
  
  // Malaysian Borneo Islands
  { name: 'Sipadan Island', country: 'Malaysia' },
  { name: 'Mabul Island', country: 'Malaysia' },
  { name: 'Kapalai Island', country: 'Malaysia' },
  { name: 'Mataking Island', country: 'Malaysia' },
  { name: 'Mantanani Islands', country: 'Malaysia' },
  { name: 'Tunku Abdul Rahman Park', country: 'Malaysia' },
  { name: 'Gaya Island', country: 'Malaysia' },
  { name: 'Manukan Island', country: 'Malaysia' },
  { name: 'Sapi Island', country: 'Malaysia' },
  { name: 'Mamutik Island', country: 'Malaysia' },
  { name: 'Sulug Island', country: 'Malaysia' },
  { name: 'Lankayan Island', country: 'Malaysia' },
  { name: 'Bohey Dulang', country: 'Malaysia' },
  { name: 'Tun Sakaran Marine Park', country: 'Malaysia' },
  
  // Borneo National Parks & Nature
  { name: 'Mount Kinabalu', country: 'Malaysia' },
  { name: 'Kinabalu Park', country: 'Malaysia' },
  { name: 'Sepilok Orangutan Centre', country: 'Malaysia' },
  { name: 'Kinabatangan River', country: 'Malaysia' },
  { name: 'Danum Valley', country: 'Malaysia' },
  { name: 'Maliau Basin', country: 'Malaysia' },
  { name: 'Gunung Mulu National Park', country: 'Malaysia' },
  { name: 'Niah Caves', country: 'Malaysia' },
  { name: 'Bako National Park', country: 'Malaysia' },
  { name: 'Similajau National Park', country: 'Malaysia' },
  { name: 'Batang Ai National Park', country: 'Malaysia' },
  { name: 'Taman Negara', country: 'Malaysia' },
  
  // Singapore
  { name: 'Singapore', country: 'Singapore' },
  { name: 'Sentosa Island', country: 'Singapore' },
  { name: 'Pulau Ubin', country: 'Singapore' },
  { name: 'Sisters\' Islands', country: 'Singapore' },
  { name: 'St. John\'s Island', country: 'Singapore' },
  { name: 'Lazarus Island', country: 'Singapore' },
  { name: 'Kusu Island', country: 'Singapore' },

  // South Asia - High preparation needed
  { name: 'Mumbai', country: 'India' },
  { name: 'Delhi', country: 'India' },
  { name: 'Bangalore', country: 'India' },
  { name: 'Chennai', country: 'India' },
  { name: 'Kolkata', country: 'India' },
  { name: 'Pune', country: 'India' },
  { name: 'Hyderabad', country: 'India' },
  { name: 'Ahmedabad', country: 'India' },
  { name: 'Jaipur', country: 'India' },
  { name: 'Lucknow', country: 'India' },
  { name: 'Kanpur', country: 'India' },
  { name: 'Nagpur', country: 'India' },
  { name: 'Indore', country: 'India' },
  { name: 'Agra', country: 'India' },
  { name: 'Varanasi', country: 'India' },
  { name: 'Kochi', country: 'India' },
  { name: 'Goa', country: 'India' },
  { name: 'Rishikesh', country: 'India' },
  { name: 'Dharamshala', country: 'India' },
  { name: 'Manali', country: 'India' },
  { name: 'Shimla', country: 'India' },
  { name: 'Darjeeling', country: 'India' },
  { name: 'Leh', country: 'India' },
  { name: 'Srinagar', country: 'India' },
  { name: 'Amritsar', country: 'India' },
  { name: 'Udaipur', country: 'India' },
  { name: 'Jodhpur', country: 'India' },
  { name: 'Pushkar', country: 'India' },
  { name: 'Kerala Backwaters', country: 'India' },
  { name: 'Hampi', country: 'India' },
  { name: 'Khajuraho', country: 'India' },
  
  { name: 'Dhaka', country: 'Bangladesh' },
  { name: 'Chittagong', country: 'Bangladesh' },
  { name: 'Sylhet', country: 'Bangladesh' },
  { name: 'Rajshahi', country: 'Bangladesh' },
  { name: 'Khulna', country: 'Bangladesh' },
  { name: 'Cox\'s Bazar', country: 'Bangladesh' },
  { name: 'Sundarbans', country: 'Bangladesh' },
  
  { name: 'Karachi', country: 'Pakistan' },
  { name: 'Lahore', country: 'Pakistan' },
  { name: 'Islamabad', country: 'Pakistan' },
  { name: 'Faisalabad', country: 'Pakistan' },
  { name: 'Rawalpindi', country: 'Pakistan' },
  { name: 'Multan', country: 'Pakistan' },
  { name: 'Peshawar', country: 'Pakistan' },
  { name: 'Quetta', country: 'Pakistan' },
  { name: 'Gilgit', country: 'Pakistan' },
  { name: 'Hunza Valley', country: 'Pakistan' },
  
  { name: 'Kathmandu', country: 'Nepal' },
  { name: 'Pokhara', country: 'Nepal' },
  { name: 'Chitwan', country: 'Nepal' },
  { name: 'Lumbini', country: 'Nepal' },
  { name: 'Everest Base Camp', country: 'Nepal' },
  { name: 'Annapurna Circuit', country: 'Nepal' },
  
  { name: 'Colombo', country: 'Sri Lanka' },
  { name: 'Kandy', country: 'Sri Lanka' },
  { name: 'Galle', country: 'Sri Lanka' },
  { name: 'Nuwara Eliya', country: 'Sri Lanka' },
  { name: 'Anuradhapura', country: 'Sri Lanka' },
  { name: 'Polonnaruwa', country: 'Sri Lanka' },
  { name: 'Sigiriya', country: 'Sri Lanka' },
  { name: 'Ella', country: 'Sri Lanka' },
  { name: 'Mirissa', country: 'Sri Lanka' },
  { name: 'Arugam Bay', country: 'Sri Lanka' },

  // Central Asia - High preparation needed
  { name: 'Tashkent', country: 'Uzbekistan' },
  { name: 'Samarkand', country: 'Uzbekistan' },
  { name: 'Bukhara', country: 'Uzbekistan' },
  { name: 'Khiva', country: 'Uzbekistan' },
  { name: 'Nukus', country: 'Uzbekistan' },
  
  { name: 'Almaty', country: 'Kazakhstan' },
  { name: 'Nur-Sultan', country: 'Kazakhstan' },
  { name: 'Shymkent', country: 'Kazakhstan' },
  { name: 'Aktobe', country: 'Kazakhstan' },
  
  { name: 'Bishkek', country: 'Kyrgyzstan' },
  { name: 'Osh', country: 'Kyrgyzstan' },
  { name: 'Karakol', country: 'Kyrgyzstan' },
  { name: 'Issyk-Kul', country: 'Kyrgyzstan' },
  
  { name: 'Dushanbe', country: 'Tajikistan' },
  { name: 'Khujand', country: 'Tajikistan' },
  { name: 'Khorog', country: 'Tajikistan' },
  { name: 'Pamir Highway', country: 'Tajikistan' },
  
  { name: 'Ashgabat', country: 'Turkmenistan' },
  { name: 'Turkmenbashi', country: 'Turkmenistan' },
  { name: 'Mary', country: 'Turkmenistan' },
  { name: 'Darvaza Gas Crater', country: 'Turkmenistan' },

  // East Asia - Lower preparation but important destinations
  { name: 'Beijing', country: 'China' },
  { name: 'Shanghai', country: 'China' },
  { name: 'Guangzhou', country: 'China' },
  { name: 'Shenzhen', country: 'China' },
  { name: 'Chengdu', country: 'China' },
  { name: 'Xi\'an', country: 'China' },
  { name: 'Hangzhou', country: 'China' },
  { name: 'Suzhou', country: 'China' },
  { name: 'Nanjing', country: 'China' },
  { name: 'Wuhan', country: 'China' },
  { name: 'Chongqing', country: 'China' },
  { name: 'Tianjin', country: 'China' },
  { name: 'Qingdao', country: 'China' },
  { name: 'Dalian', country: 'China' },
  { name: 'Harbin', country: 'China' },
  { name: 'Urumqi', country: 'China' },
  { name: 'Lhasa', country: 'China' },
  { name: 'Guilin', country: 'China' },
  { name: 'Yangshuo', country: 'China' },
  { name: 'Zhangjiajie', country: 'China' },
  { name: 'Shenyang', country: 'China' },
  { name: 'Changchun', country: 'China' },
  { name: 'Kunming', country: 'China' },
  { name: 'Lanzhou', country: 'China' },
  { name: 'Ningbo', country: 'China' },
  { name: 'Xiamen', country: 'China' },
  { name: 'Fuzhou', country: 'China' },
  { name: 'Hefei', country: 'China' },
  { name: 'Nanchang', country: 'China' },
  { name: 'Changsha', country: 'China' },
  { name: 'Guiyang', country: 'China' },
  { name: 'Taiyuan', country: 'China' },
  { name: 'Shijiazhuang', country: 'China' },
  { name: 'Hohhot', country: 'China' },
  { name: 'Yinchuan', country: 'China' },
  { name: 'Xining', country: 'China' },
  { name: 'Jinan', country: 'China' },
  { name: 'Zhengzhou', country: 'China' },
  { name: 'Huangshan', country: 'China' },
  { name: 'Lijiang', country: 'China' },
  { name: 'Dali', country: 'China' },
  { name: 'Shangri-La', country: 'China' },
  { name: 'Jiuzhaigou', country: 'China' },
  { name: 'Emei Mountain', country: 'China' },
  { name: 'Leshan', country: 'China' },
  { name: 'Huanglong', country: 'China' },
  { name: 'Huashan', country: 'China' },
  { name: 'Luoyang', country: 'China' },
  { name: 'Kaifeng', country: 'China' },
  { name: 'Pingyao', country: 'China' },
  { name: 'Datong', country: 'China' },
  { name: 'Wutai Mountain', country: 'China' },
  { name: 'Yanan', country: 'China' },
  { name: 'Dunhuang', country: 'China' },
  { name: 'Jiayuguan', country: 'China' },
  { name: 'Turpan', country: 'China' },
  { name: 'Kashgar', country: 'China' },
  { name: 'Kanas Lake', country: 'China' },
  { name: 'Heavenly Lake', country: 'China' },
  { name: 'Shigatse', country: 'China' },
  { name: 'Gyantse', country: 'China' },
  { name: 'Namtso Lake', country: 'China' },
  { name: 'Mount Everest Base Camp', country: 'China' },
  { name: 'Mount Kailash', country: 'China' },
  { name: 'Potala Palace', country: 'China' },
  { name: 'Terracotta Army', country: 'China' },
  { name: 'Forbidden City', country: 'China' },
  { name: 'Great Wall', country: 'China' },
  { name: 'Mutianyu', country: 'China' },
  { name: 'Badaling', country: 'China' },
  { name: 'Jinshanling', country: 'China' },
  { name: 'Simatai', country: 'China' },
  { name: 'Summer Palace', country: 'China' },
  { name: 'Temple of Heaven', country: 'China' },
  { name: 'Ming Tombs', country: 'China' },
  { name: 'Bund', country: 'China' },
  { name: 'Yu Garden', country: 'China' },
  { name: 'Oriental Pearl Tower', country: 'China' },
  { name: 'Zhujiajiao', country: 'China' },
  { name: 'Tongli', country: 'China' },
  { name: 'Wuzhen', country: 'China' },
  { name: 'Zhouzhuang', country: 'China' },
  { name: 'Xitang', country: 'China' },
  { name: 'West Lake', country: 'China' },
  { name: 'Longjing Tea Village', country: 'China' },
  { name: 'Lingyin Temple', country: 'China' },
  { name: 'Fenghuang', country: 'China' },
  { name: 'Zhangjiajie National Forest', country: 'China' },
  { name: 'Tianmen Mountain', country: 'China' },
  { name: 'Glass Bridge', country: 'China' },
  { name: 'Li River', country: 'China' },
  { name: 'Longji Rice Terraces', country: 'China' },
  { name: 'Reed Flute Cave', country: 'China' },
  { name: 'Elephant Trunk Hill', country: 'China' },
  { name: 'Tiger Leaping Gorge', country: 'China' },
  { name: 'Jade Dragon Snow Mountain', country: 'China' },
  { name: 'Blue Moon Valley', country: 'China' },
  { name: 'Three Pagodas', country: 'China' },
  { name: 'Erhai Lake', country: 'China' },
  { name: 'Stone Forest', country: 'China' },
  { name: 'Puzhehei', country: 'China' },
  { name: 'Yuanyang Rice Terraces', country: 'China' },
  { name: 'Xishuangbanna', country: 'China' },
  { name: 'Zhaoqing', country: 'China' },
  { name: 'Kaiping', country: 'China' },
  { name: 'Shantou', country: 'China' },
  { name: 'Zhuhai', country: 'China' },
  { name: 'Macau', country: 'China' },
  { name: 'Haikou', country: 'China' },
  { name: 'Sanya', country: 'China' },
  { name: 'Hainan Island', country: 'China' },
  { name: 'Yalong Bay', country: 'China' },
  { name: 'Wuzhizhou Island', country: 'China' },
  { name: 'Nanshan Temple', country: 'China' },
  { name: 'Yangtze River', country: 'China' },
  { name: 'Three Gorges', country: 'China' },
  { name: 'Fengdu Ghost City', country: 'China' },
  { name: 'Baidi City', country: 'China' },
  { name: 'Dazu Rock Carvings', country: 'China' },
  { name: 'Wulong Karst', country: 'China' },
  { name: 'Mount Wudang', country: 'China' },
  { name: 'Yellow Crane Tower', country: 'China' },
  { name: 'East Lake', country: 'China' },
  { name: 'Wudang Mountains', country: 'China' },
  { name: 'Shennongjia', country: 'China' },
  { name: 'Zhangye Danxia', country: 'China' },
  { name: 'Mogao Caves', country: 'China' },
  { name: 'Crescent Lake', country: 'China' },
  { name: 'Singing Sand Dunes', country: 'China' },
  { name: 'Yumen Pass', country: 'China' },
  { name: 'Labrang Monastery', country: 'China' },
  { name: 'Xiahe', country: 'China' },
  { name: 'Qinghai Lake', country: 'China' },
  { name: 'Chaka Salt Lake', country: 'China' },
  { name: 'Kumbum Monastery', country: 'China' },
  { name: 'Mount Qomolangma', country: 'China' },
  { name: 'Rongbuk Monastery', country: 'China' },
  { name: 'Yamdrok Lake', country: 'China' },
  { name: 'Samye Monastery', country: 'China' },
  { name: 'Tashilhunpo Monastery', country: 'China' },
  { name: 'Jokhang Temple', country: 'China' },
  { name: 'Barkhor Street', country: 'China' },
  { name: 'Sera Monastery', country: 'China' },
  { name: 'Drepung Monastery', country: 'China' },
  { name: 'Ganden Monastery', country: 'China' },
  
  { name: 'Tokyo', country: 'Japan' },
  { name: 'Osaka', country: 'Japan' },
  { name: 'Kyoto', country: 'Japan' },
  { name: 'Yokohama', country: 'Japan' },
  { name: 'Nagoya', country: 'Japan' },
  { name: 'Sapporo', country: 'Japan' },
  { name: 'Fukuoka', country: 'Japan' },
  { name: 'Kobe', country: 'Japan' },
  { name: 'Hiroshima', country: 'Japan' },
  { name: 'Sendai', country: 'Japan' },
  { name: 'Nara', country: 'Japan' },
  { name: 'Kanazawa', country: 'Japan' },
  { name: 'Takayama', country: 'Japan' },
  { name: 'Nikko', country: 'Japan' },
  { name: 'Kamakura', country: 'Japan' },
  { name: 'Hakone', country: 'Japan' },
  { name: 'Mount Fuji', country: 'Japan' },
  { name: 'Kawaguchiko', country: 'Japan' },
  { name: 'Fujiyoshida', country: 'Japan' },
  { name: 'Nagano', country: 'Japan' },
  { name: 'Matsumoto', country: 'Japan' },
  { name: 'Shirakawa-go', country: 'Japan' },
  { name: 'Gokayama', country: 'Japan' },
  { name: 'Toyama', country: 'Japan' },
  { name: 'Tateyama Kurobe Alpine Route', country: 'Japan' },
  { name: 'Ishikawa', country: 'Japan' },
  { name: 'Fukui', country: 'Japan' },
  { name: 'Gifu', country: 'Japan' },
  { name: 'Hida', country: 'Japan' },
  { name: 'Okayama', country: 'Japan' },
  { name: 'Kurashiki', country: 'Japan' },
  { name: 'Naoshima', country: 'Japan' },
  { name: 'Teshima', country: 'Japan' },
  { name: 'Shodoshima', country: 'Japan' },
  { name: 'Takamatsu', country: 'Japan' },
  { name: 'Tokushima', country: 'Japan' },
  { name: 'Kochi', country: 'Japan' },
  { name: 'Matsuyama', country: 'Japan' },
  { name: 'Dogo Onsen', country: 'Japan' },
  { name: 'Onomichi', country: 'Japan' },
  { name: 'Shimanami Kaido', country: 'Japan' },
  { name: 'Miyajima', country: 'Japan' },
  { name: 'Itsukushima Shrine', country: 'Japan' },
  { name: 'Peace Memorial Park', country: 'Japan' },
  { name: 'Himeji', country: 'Japan' },
  { name: 'Himeji Castle', country: 'Japan' },
  { name: 'Arima Onsen', country: 'Japan' },
  { name: 'Mount Rokko', country: 'Japan' },
  { name: 'Akashi', country: 'Japan' },
  { name: 'Awaji Island', country: 'Japan' },
  { name: 'Koyasan', country: 'Japan' },
  { name: 'Wakayama', country: 'Japan' },
  { name: 'Kumano Kodo', country: 'Japan' },
  { name: 'Ise', country: 'Japan' },
  { name: 'Ise Grand Shrine', country: 'Japan' },
  { name: 'Toba', country: 'Japan' },
  { name: 'Shima', country: 'Japan' },
  { name: 'Nagashima Spa Land', country: 'Japan' },
  { name: 'Inuyama', country: 'Japan' },
  { name: 'Gero Onsen', country: 'Japan' },
  { name: 'Magome', country: 'Japan' },
  { name: 'Tsumago', country: 'Japan' },
  { name: 'Nakasendo Trail', country: 'Japan' },
  { name: 'Karuizawa', country: 'Japan' },
  { name: 'Kusatsu Onsen', country: 'Japan' },
  { name: 'Minakami', country: 'Japan' },
  { name: 'Gunma', country: 'Japan' },
  { name: 'Tochigi', country: 'Japan' },
  { name: 'Utsunomiya', country: 'Japan' },
  { name: 'Mashiko', country: 'Japan' },
  { name: 'Ashikaga', country: 'Japan' },
  { name: 'Mito', country: 'Japan' },
  { name: 'Ibaraki', country: 'Japan' },
  { name: 'Hitachi Seaside Park', country: 'Japan' },
  { name: 'Fukushima', country: 'Japan' },
  { name: 'Aizu-Wakamatsu', country: 'Japan' },
  { name: 'Bandai-Asahi National Park', country: 'Japan' },
  { name: 'Yamagata', country: 'Japan' },
  { name: 'Zao Onsen', country: 'Japan' },
  { name: 'Ginzan Onsen', country: 'Japan' },
  { name: 'Yamadera', country: 'Japan' },
  { name: 'Akita', country: 'Japan' },
  { name: 'Lake Tazawa', country: 'Japan' },
  { name: 'Kakunodate', country: 'Japan' },
  { name: 'Nyuto Onsen', country: 'Japan' },
  { name: 'Aomori', country: 'Japan' },
  { name: 'Hirosaki', country: 'Japan' },
  { name: 'Towada', country: 'Japan' },
  { name: 'Oirase Gorge', country: 'Japan' },
  { name: 'Hakodate', country: 'Japan' },
  { name: 'Otaru', country: 'Japan' },
  { name: 'Asahikawa', country: 'Japan' },
  { name: 'Furano', country: 'Japan' },
  { name: 'Biei', country: 'Japan' },
  { name: 'Niseko', country: 'Japan' },
  { name: 'Rusutsu', country: 'Japan' },
  { name: 'Noboribetsu', country: 'Japan' },
  { name: 'Jozankei Onsen', country: 'Japan' },
  { name: 'Lake Toya', country: 'Japan' },
  { name: 'Shikotsu Lake', country: 'Japan' },
  { name: 'Akan National Park', country: 'Japan' },
  { name: 'Lake Akan', country: 'Japan' },
  { name: 'Lake Mashu', country: 'Japan' },
  { name: 'Kushiro', country: 'Japan' },
  { name: 'Abashiri', country: 'Japan' },
  { name: 'Shiretoko', country: 'Japan' },
  { name: 'Rebun Island', country: 'Japan' },
  { name: 'Rishiri Island', country: 'Japan' },
  { name: 'Kitakyushu', country: 'Japan' },
  { name: 'Nagasaki', country: 'Japan' },
  { name: 'Sasebo', country: 'Japan' },
  { name: 'Kujukushima', country: 'Japan' },
  { name: 'Huis Ten Bosch', country: 'Japan' },
  { name: 'Kumamoto', country: 'Japan' },
  { name: 'Kumamoto Castle', country: 'Japan' },
  { name: 'Aso', country: 'Japan' },
  { name: 'Mount Aso', country: 'Japan' },
  { name: 'Kurokawa Onsen', country: 'Japan' },
  { name: 'Beppu', country: 'Japan' },
  { name: 'Yufuin', country: 'Japan' },
  { name: 'Oita', country: 'Japan' },
  { name: 'Usuki', country: 'Japan' },
  { name: 'Miyazaki', country: 'Japan' },
  { name: 'Takachiho', country: 'Japan' },
  { name: 'Aoshima', country: 'Japan' },
  { name: 'Kagoshima', country: 'Japan' },
  { name: 'Sakurajima', country: 'Japan' },
  { name: 'Ibusuki', country: 'Japan' },
  { name: 'Yakushima', country: 'Japan' },
  { name: 'Tanegashima', country: 'Japan' },
  { name: 'Amami Oshima', country: 'Japan' },
  { name: 'Okinawa', country: 'Japan' },
  { name: 'Naha', country: 'Japan' },
  { name: 'Shuri Castle', country: 'Japan' },
  { name: 'Ishigaki', country: 'Japan' },
  { name: 'Miyako Island', country: 'Japan' },
  { name: 'Iriomote', country: 'Japan' },
  { name: 'Taketomi Island', country: 'Japan' },
  { name: 'Zamami Island', country: 'Japan' },
  { name: 'Tokashiki Island', country: 'Japan' },
  { name: 'Kerama Islands', country: 'Japan' },
  { name: 'Churaumi Aquarium', country: 'Japan' },
  { name: 'Cape Manzamo', country: 'Japan' },
  { name: 'Shikinaen Garden', country: 'Japan' },
  { name: 'Nakijin Castle', country: 'Japan' },
  { name: 'Katsuren Castle', country: 'Japan' },
  { name: 'Hateruma', country: 'Japan' },
  { name: 'Yonaguni', country: 'Japan' },
  
  { name: 'Seoul', country: 'South Korea' },
  { name: 'Busan', country: 'South Korea' },
  { name: 'Incheon', country: 'South Korea' },
  { name: 'Daegu', country: 'South Korea' },
  { name: 'Daejeon', country: 'South Korea' },
  { name: 'Gwangju', country: 'South Korea' },
  { name: 'Jeju Island', country: 'South Korea' },
  { name: 'Gyeongju', country: 'South Korea' },
  { name: 'Andong', country: 'South Korea' },
  
  { name: 'Ulaanbaatar', country: 'Mongolia' },
  { name: 'Erdenet', country: 'Mongolia' },
  { name: 'Darkhan', country: 'Mongolia' },
  { name: 'Gobi Desert', country: 'Mongolia' },
  { name: 'Choibalsan', country: 'Mongolia' },
  { name: 'Kharkhorin', country: 'Mongolia' },
  { name: 'Khövsgöl Lake', country: 'Mongolia' },
  { name: 'Terelj National Park', country: 'Mongolia' },
  { name: 'Altai Mountains', country: 'Mongolia' },
  { name: 'Flaming Cliffs', country: 'Mongolia' },
  { name: 'Khongoryn Els', country: 'Mongolia' },
  { name: 'Orkhon Valley', country: 'Mongolia' },
  { name: 'Hustai National Park', country: 'Mongolia' },
  { name: 'Tsagaan Suvarga', country: 'Mongolia' },
  { name: 'Yolyn Am', country: 'Mongolia' },
  { name: 'Bayanzag', country: 'Mongolia' },

  // Middle East - High to moderate preparation
  { name: 'Dubai', country: 'UAE' },
  { name: 'Abu Dhabi', country: 'UAE' },
  { name: 'Sharjah', country: 'UAE' },
  { name: 'Al Ain', country: 'UAE' },
  { name: 'Fujairah', country: 'UAE' },
  { name: 'Ajman', country: 'UAE' },
  { name: 'Ras Al Khaimah', country: 'UAE' },
  { name: 'Umm Al Quwain', country: 'UAE' },
  { name: 'Jebel Jais', country: 'UAE' },
  { name: 'Hatta', country: 'UAE' },
  { name: 'Liwa Oasis', country: 'UAE' },
  { name: 'Sir Bani Yas Island', country: 'UAE' },
  { name: 'Palm Jumeirah', country: 'UAE' },
  { name: 'Burj Khalifa', country: 'UAE' },
  { name: 'Sheikh Zayed Mosque', country: 'UAE' },
  { name: 'Al Bidya Mosque', country: 'UAE' },
  { name: 'Khor Fakkan', country: 'UAE' },
  { name: 'Dibba', country: 'UAE' },
  
  { name: 'Doha', country: 'Qatar' },
  { name: 'Al Rayyan', country: 'Qatar' },
  { name: 'Al Wakrah', country: 'Qatar' },
  { name: 'Al Khor', country: 'Qatar' },
  { name: 'Lusail', country: 'Qatar' },
  { name: 'Dukhan', country: 'Qatar' },
  { name: 'Mesaieed', country: 'Qatar' },
  { name: 'Al Shamal', country: 'Qatar' },
  { name: 'Khor Al Adaid', country: 'Qatar' },
  { name: 'Purple Island', country: 'Qatar' },
  { name: 'Museum of Islamic Art', country: 'Qatar' },
  { name: 'Souq Waqif', country: 'Qatar' },
  { name: 'Katara Cultural Village', country: 'Qatar' },
  { name: 'The Pearl Qatar', country: 'Qatar' },
  
  { name: 'Riyadh', country: 'Saudi Arabia' },
  { name: 'Jeddah', country: 'Saudi Arabia' },
  { name: 'Mecca', country: 'Saudi Arabia' },
  { name: 'Medina', country: 'Saudi Arabia' },
  { name: 'Dammam', country: 'Saudi Arabia' },
  { name: 'Taif', country: 'Saudi Arabia' },
  { name: 'Al Khobar', country: 'Saudi Arabia' },
  { name: 'Abha', country: 'Saudi Arabia' },
  { name: 'NEOM', country: 'Saudi Arabia' },
  { name: 'Tabuk', country: 'Saudi Arabia' },
  { name: 'Buraidah', country: 'Saudi Arabia' },
  { name: 'Khamis Mushait', country: 'Saudi Arabia' },
  { name: 'Hail', country: 'Saudi Arabia' },
  { name: 'Najran', country: 'Saudi Arabia' },
  { name: 'Dhahran', country: 'Saudi Arabia' },
  { name: 'Jubail', country: 'Saudi Arabia' },
  { name: 'Yanbu', country: 'Saudi Arabia' },
  { name: 'Al-Qatif', country: 'Saudi Arabia' },
  { name: 'Al Ula', country: 'Saudi Arabia' },
  { name: 'Madain Saleh', country: 'Saudi Arabia' },
  { name: 'Edge of the World', country: 'Saudi Arabia' },
  { name: 'Farasān Islands', country: 'Saudi Arabia' },
  { name: 'Asir Mountains', country: 'Saudi Arabia' },
  { name: 'Al Wahbah Crater', country: 'Saudi Arabia' },
  { name: 'Dumat al-Jandal', country: 'Saudi Arabia' },
  
  { name: 'Kuwait City', country: 'Kuwait' },
  { name: 'Hawalli', country: 'Kuwait' },
  { name: 'Al Ahmadi', country: 'Kuwait' },
  { name: 'Salmiya', country: 'Kuwait' },
  { name: 'Al Jahra', country: 'Kuwait' },
  { name: 'Fahaheel', country: 'Kuwait' },
  { name: 'Mangaf', country: 'Kuwait' },
  { name: 'Fintas', country: 'Kuwait' },
  { name: 'Failaka Island', country: 'Kuwait' },
  { name: 'Kubbar Island', country: 'Kuwait' },
  { name: 'Kuwait Towers', country: 'Kuwait' },
  { name: 'Grand Mosque', country: 'Kuwait' },
  
  { name: 'Manama', country: 'Bahrain' },
  { name: 'Riffa', country: 'Bahrain' },
  { name: 'Muharraq', country: 'Bahrain' },
  { name: 'Hamad Town', country: 'Bahrain' },
  { name: 'A\'ali', country: 'Bahrain' },
  { name: 'Isa Town', country: 'Bahrain' },
  { name: 'Sitra', country: 'Bahrain' },
  { name: 'Budaiya', country: 'Bahrain' },
  { name: 'Jidhafs', country: 'Bahrain' },
  { name: 'Al-Manamah', country: 'Bahrain' },
  { name: 'Bahrain Fort', country: 'Bahrain' },
  { name: 'Tree of Life', country: 'Bahrain' },
  { name: 'Al Areen Wildlife Park', country: 'Bahrain' },
  
  { name: 'Muscat', country: 'Oman' },
  { name: 'Salalah', country: 'Oman' },
  { name: 'Sohar', country: 'Oman' },
  { name: 'Nizwa', country: 'Oman' },
  { name: 'Sur', country: 'Oman' },
  { name: 'Wahiba Sands', country: 'Oman' },
  { name: 'Bahla', country: 'Oman' },
  { name: 'Rustaq', country: 'Oman' },
  { name: 'Ibra', country: 'Oman' },
  { name: 'Jebel Akhdar', country: 'Oman' },
  { name: 'Jebel Shams', country: 'Oman' },
  { name: 'Wadi Shab', country: 'Oman' },
  { name: 'Wadi Bani Khalid', country: 'Oman' },
  { name: 'Masirah Island', country: 'Oman' },
  { name: 'Ras al Jinz', country: 'Oman' },
  { name: 'Muttrah Souq', country: 'Oman' },
  { name: 'Sultan Qaboos Mosque', country: 'Oman' },
  { name: 'Nakhal Fort', country: 'Oman' },
  { name: 'Al Hoota Cave', country: 'Oman' },
  { name: 'Bimmah Sinkhole', country: 'Oman' },
  { name: 'Khasab', country: 'Oman' },
  { name: 'Musandam Peninsula', country: 'Oman' },
  { name: 'Duqm', country: 'Oman' },
  
  { name: 'Baghdad', country: 'Iraq' },
  { name: 'Basra', country: 'Iraq' },
  { name: 'Erbil', country: 'Iraq' },
  { name: 'Mosul', country: 'Iraq' },
  { name: 'Najaf', country: 'Iraq' },
  { name: 'Karbala', country: 'Iraq' },
  { name: 'Sulaymaniyah', country: 'Iraq' },
  { name: 'Dohuk', country: 'Iraq' },
  { name: 'Kirkuk', country: 'Iraq' },
  { name: 'Samarra', country: 'Iraq' },
  { name: 'Babylon', country: 'Iraq' },
  { name: 'Ur', country: 'Iraq' },
  { name: 'Ctesiphon', country: 'Iraq' },
  { name: 'Ziggurat of Ur', country: 'Iraq' },
  { name: 'Kurdistan Region', country: 'Iraq' },
  { name: 'Amadiya', country: 'Iraq' },
  
  { name: 'Tehran', country: 'Iran' },
  { name: 'Mashhad', country: 'Iran' },
  { name: 'Isfahan', country: 'Iran' },
  { name: 'Karaj', country: 'Iran' },
  { name: 'Tabriz', country: 'Iran' },
  { name: 'Shiraz', country: 'Iran' },
  { name: 'Ahvaz', country: 'Iran' },
  { name: 'Qom', country: 'Iran' },
  { name: 'Kermanshah', country: 'Iran' },
  { name: 'Urmia', country: 'Iran' },
  { name: 'Yazd', country: 'Iran' },
  { name: 'Kerman', country: 'Iran' },
  { name: 'Persepolis', country: 'Iran' },
  
  { name: 'Istanbul', country: 'Turkey' },
  { name: 'Ankara', country: 'Turkey' },
  { name: 'Izmir', country: 'Turkey' },
  { name: 'Bursa', country: 'Turkey' },
  { name: 'Adana', country: 'Turkey' },
  { name: 'Gaziantep', country: 'Turkey' },
  { name: 'Konya', country: 'Turkey' },
  { name: 'Antalya', country: 'Turkey' },
  { name: 'Kayseri', country: 'Turkey' },
  { name: 'Mersin', country: 'Turkey' },
  { name: 'Cappadocia', country: 'Turkey' },
  { name: 'Pamukkale', country: 'Turkey' },
  { name: 'Ephesus', country: 'Turkey' },
  { name: 'Troy', country: 'Turkey' },
  { name: 'Bodrum', country: 'Turkey' },
  { name: 'Kusadasi', country: 'Turkey' },
  
  { name: 'Yerevan', country: 'Armenia' },
  { name: 'Gyumri', country: 'Armenia' },
  { name: 'Vanadzor', country: 'Armenia' },
  { name: 'Ejmiatsin', country: 'Armenia' },
  { name: 'Geghard Monastery', country: 'Armenia' },
  { name: 'Lake Sevan', country: 'Armenia' },
  { name: 'Garni Temple', country: 'Armenia' },
  { name: 'Tatev Monastery', country: 'Armenia' },
  { name: 'Dilijan', country: 'Armenia' },
  { name: 'Jermuk', country: 'Armenia' },
  { name: 'Goris', country: 'Armenia' },
  { name: 'Khor Virap', country: 'Armenia' },
  { name: 'Noravank Monastery', country: 'Armenia' },
  { name: 'Sevanavank', country: 'Armenia' },
  { name: 'Amberd Fortress', country: 'Armenia' },
  { name: 'Mount Aragats', country: 'Armenia' },
  
  { name: 'Baku', country: 'Azerbaijan' },
  { name: 'Ganja', country: 'Azerbaijan' },
  { name: 'Sumqayit', country: 'Azerbaijan' },
  { name: 'Mingachevir', country: 'Azerbaijan' },
  { name: 'Gobustan', country: 'Azerbaijan' },
  { name: 'Sheki', country: 'Azerbaijan' },
  { name: 'Quba', country: 'Azerbaijan' },
  { name: 'Gabala', country: 'Azerbaijan' },
  { name: 'Lankaran', country: 'Azerbaijan' },
  { name: 'Nakhchivan', country: 'Azerbaijan' },
  { name: 'Shahdag', country: 'Azerbaijan' },
  { name: 'Ateshgah Fire Temple', country: 'Azerbaijan' },
  { name: 'Yanar Dag', country: 'Azerbaijan' },
  { name: 'Maiden Tower', country: 'Azerbaijan' },
  { name: 'Flame Towers', country: 'Azerbaijan' },
  { name: 'Caspian Sea Coast', country: 'Azerbaijan' },
  
  { name: 'Tbilisi', country: 'Georgia' },
  { name: 'Batumi', country: 'Georgia' },
  { name: 'Kutaisi', country: 'Georgia' },
  { name: 'Rustavi', country: 'Georgia' },
  { name: 'Zugdidi', country: 'Georgia' },
  { name: 'Svaneti', country: 'Georgia' },
  { name: 'Kakheti', country: 'Georgia' },
  { name: 'Mestia', country: 'Georgia' },
  { name: 'Ushguli', country: 'Georgia' },
  { name: 'Gori', country: 'Georgia' },
  { name: 'Mtskheta', country: 'Georgia' },
  { name: 'Kazbegi', country: 'Georgia' },
  { name: 'Vardzia', country: 'Georgia' },
  { name: 'Uplistsikhe', country: 'Georgia' },
  { name: 'Gergeti Trinity Church', country: 'Georgia' },
  { name: 'Prometheus Cave', country: 'Georgia' },
  { name: 'Martvili Canyon', country: 'Georgia' },
  { name: 'Okatse Canyon', country: 'Georgia' },
  { name: 'Borjomi', country: 'Georgia' },
  { name: 'Bakuriani', country: 'Georgia' },
  { name: 'Gudauri', country: 'Georgia' },
  { name: 'Signagi', country: 'Georgia' },
  { name: 'Telavi', country: 'Georgia' },
  { name: 'David Gareja', country: 'Georgia' },
  
  { name: 'Tel Aviv', country: 'Israel' },
  { name: 'Jerusalem', country: 'Israel' },
  { name: 'Haifa', country: 'Israel' },
  { name: 'Rishon LeZion', country: 'Israel' },
  { name: 'Petah Tikva', country: 'Israel' },
  { name: 'Ashdod', country: 'Israel' },
  { name: 'Netanya', country: 'Israel' },
  { name: 'Beer Sheva', country: 'Israel' },
  { name: 'Eilat', country: 'Israel' },
  { name: 'Dead Sea', country: 'Israel' },
  { name: 'Nazareth', country: 'Israel' },
  { name: 'Bethlehem', country: 'Palestine' },
  { name: 'Gaza City', country: 'Palestine' },
  { name: 'Ramallah', country: 'Palestine' },
  { name: 'Hebron', country: 'Palestine' },
  { name: 'Nablus', country: 'Palestine' },
  { name: 'Jericho', country: 'Palestine' },
  { name: 'Jenin', country: 'Palestine' },
  { name: 'Tulkarm', country: 'Palestine' },
  { name: 'Qalqilya', country: 'Palestine' },
  { name: 'Bethany', country: 'Palestine' },
  { name: 'Beit Sahour', country: 'Palestine' },
  { name: 'Beit Jala', country: 'Palestine' },
  { name: 'Nablus', country: 'Palestine' },
  
  { name: 'Amman', country: 'Jordan' },
  { name: 'Zarqa', country: 'Jordan' },
  { name: 'Irbid', country: 'Jordan' },
  { name: 'Russeifa', country: 'Jordan' },
  { name: 'Wadi Musa', country: 'Jordan' },
  { name: 'Petra', country: 'Jordan' },
  { name: 'Wadi Rum', country: 'Jordan' },
  { name: 'Aqaba', country: 'Jordan' },
  { name: 'Jerash', country: 'Jordan' },
  { name: 'Dead Sea', country: 'Jordan' },
  
  { name: 'Beirut', country: 'Lebanon' },
  { name: 'Tripoli', country: 'Lebanon' },
  { name: 'Sidon', country: 'Lebanon' },
  { name: 'Tyre', country: 'Lebanon' },
  { name: 'Baalbek', country: 'Lebanon' },
  { name: 'Byblos', country: 'Lebanon' },
  { name: 'Zahle', country: 'Lebanon' },
  { name: 'Jounieh', country: 'Lebanon' },
  
  { name: 'Damascus', country: 'Syria' },
  { name: 'Aleppo', country: 'Syria' },
  { name: 'Homs', country: 'Syria' },
  { name: 'Latakia', country: 'Syria' },
  { name: 'Hama', country: 'Syria' },
  { name: 'Deir ez-Zor', country: 'Syria' },
  { name: 'Palmyra', country: 'Syria' },
  { name: 'Bosra', country: 'Syria' },

  // EUROPE - Lower to moderate preparation
  
  // Western Europe
  { name: 'London', country: 'UK' },
  { name: 'Birmingham', country: 'UK' },
  { name: 'Leeds', country: 'UK' },
  { name: 'Glasgow', country: 'UK' },
  { name: 'Sheffield', country: 'UK' },
  { name: 'Bradford', country: 'UK' },
  { name: 'Edinburgh', country: 'UK' },
  { name: 'Liverpool', country: 'UK' },
  { name: 'Manchester', country: 'UK' },
  { name: 'Bristol', country: 'UK' },
  { name: 'Cardiff', country: 'UK' },
  { name: 'Leicester', country: 'UK' },
  { name: 'Coventry', country: 'UK' },
  { name: 'Hull', country: 'UK' },
  { name: 'Belfast', country: 'UK' },
  { name: 'Stoke-on-Trent', country: 'UK' },
  { name: 'Wolverhampton', country: 'UK' },
  { name: 'Plymouth', country: 'UK' },
  { name: 'Derby', country: 'UK' },
  { name: 'Swansea', country: 'UK' },
  { name: 'Southampton', country: 'UK' },
  { name: 'Salford', country: 'UK' },
  { name: 'Aberdeen', country: 'UK' },
  { name: 'Westminster', country: 'UK' },
  { name: 'Portsmouth', country: 'UK' },
  { name: 'York', country: 'UK' },
  { name: 'Peterborough', country: 'UK' },
  { name: 'Dundee', country: 'UK' },
  { name: 'Lancaster', country: 'UK' },
  { name: 'Oxford', country: 'UK' },
  { name: 'Newport', country: 'UK' },
  { name: 'Preston', country: 'UK' },
  { name: 'St Albans', country: 'UK' },
  { name: 'Norwich', country: 'UK' },
  { name: 'Chester', country: 'UK' },
  { name: 'Cambridge', country: 'UK' },
  { name: 'Salisbury', country: 'UK' },
  { name: 'Exeter', country: 'UK' },
  { name: 'Gloucester', country: 'UK' },
  { name: 'Lisburn', country: 'UK' },
  { name: 'Chichester', country: 'UK' },
  { name: 'Winchester', country: 'UK' },
  { name: 'Londonderry', country: 'UK' },
  { name: 'Carlisle', country: 'UK' },
  { name: 'Worcester', country: 'UK' },
  { name: 'Bath', country: 'UK' },
  { name: 'Durham', country: 'UK' },
  { name: 'Lincoln', country: 'UK' },
  { name: 'Hereford', country: 'UK' },
  { name: 'Armagh', country: 'UK' },
  { name: 'Inverness', country: 'UK' },
  { name: 'Stirling', country: 'UK' },
  { name: 'Canterbury', country: 'UK' },
  { name: 'Lichfield', country: 'UK' },
  { name: 'Newry', country: 'UK' },
  { name: 'Ripon', country: 'UK' },
  { name: 'Bangor', country: 'UK' },
  { name: 'Truro', country: 'UK' },
  { name: 'Ely', country: 'UK' },
  { name: 'Wells', country: 'UK' },
  { name: 'St Asaph', country: 'UK' },
  { name: 'St Davids', country: 'UK' },
  { name: 'Lake District', country: 'UK' },
  { name: 'Scottish Highlands', country: 'UK' },
  { name: 'Isle of Skye', country: 'UK' },
  { name: 'Snowdonia', country: 'UK' },
  { name: 'Cotswolds', country: 'UK' },
  { name: 'Cornwall', country: 'UK' },
  { name: 'Peak District', country: 'UK' },
  { name: 'Yorkshire Dales', country: 'UK' },
  { name: 'New Forest', country: 'UK' },
  { name: 'Brecon Beacons', country: 'UK' },
  { name: 'Giant\'s Causeway', country: 'UK' },
  { name: 'Loch Ness', country: 'UK' },
  { name: 'Ben Nevis', country: 'UK' },
  { name: 'Hadrian\'s Wall', country: 'UK' },
  { name: 'Stonehenge', country: 'UK' },
  { name: 'Windsor Castle', country: 'UK' },
  { name: 'Buckingham Palace', country: 'UK' },
  
  { name: 'Dublin', country: 'Ireland' },
  { name: 'Cork', country: 'Ireland' },
  { name: 'Limerick', country: 'Ireland' },
  { name: 'Galway', country: 'Ireland' },
  { name: 'Waterford', country: 'Ireland' },
  { name: 'Drogheda', country: 'Ireland' },
  { name: 'Kilkenny', country: 'Ireland' },
  { name: 'Wexford', country: 'Ireland' },
  { name: 'Sligo', country: 'Ireland' },
  { name: 'Clonmel', country: 'Ireland' },
  { name: 'Dundalk', country: 'Ireland' },
  { name: 'Bray', country: 'Ireland' },
  { name: 'Navan', country: 'Ireland' },
  { name: 'Ennis', country: 'Ireland' },
  { name: 'Tralee', country: 'Ireland' },
  { name: 'Carlow', country: 'Ireland' },
  { name: 'Naas', country: 'Ireland' },
  { name: 'Athlone', country: 'Ireland' },
  { name: 'Letterkenny', country: 'Ireland' },
  { name: 'Tullamore', country: 'Ireland' },
  { name: 'Cliffs of Moher', country: 'Ireland' },
  { name: 'Ring of Kerry', country: 'Ireland' },
  { name: 'Dingle Peninsula', country: 'Ireland' },
  { name: 'Giant\'s Causeway', country: 'Ireland' },
  { name: 'Killarney National Park', country: 'Ireland' },
  { name: 'Aran Islands', country: 'Ireland' },
  { name: 'Skellig Michael', country: 'Ireland' },
  { name: 'Newgrange', country: 'Ireland' },
  
  { name: 'Paris', country: 'France' },
  { name: 'Marseille', country: 'France' },
  { name: 'Lyon', country: 'France' },
  { name: 'Toulouse', country: 'France' },
  { name: 'Nice', country: 'France' },
  { name: 'Nantes', country: 'France' },
  { name: 'Montpellier', country: 'France' },
  { name: 'Strasbourg', country: 'France' },
  { name: 'Bordeaux', country: 'France' },
  { name: 'Lille', country: 'France' },
  { name: 'Rennes', country: 'France' },
  { name: 'Reims', country: 'France' },
  { name: 'Saint-Étienne', country: 'France' },
  { name: 'Toulon', country: 'France' },
  { name: 'Le Havre', country: 'France' },
  { name: 'Grenoble', country: 'France' },
  { name: 'Dijon', country: 'France' },
  { name: 'Angers', country: 'France' },
  { name: 'Nîmes', country: 'France' },
  { name: 'Villeurbanne', country: 'France' },
  { name: 'Le Mans', country: 'France' },
  { name: 'Aix-en-Provence', country: 'France' },
  { name: 'Brest', country: 'France' },
  { name: 'Limoges', country: 'France' },
  { name: 'Tours', country: 'France' },
  { name: 'Amiens', country: 'France' },
  { name: 'Perpignan', country: 'France' },
  { name: 'Metz', country: 'France' },
  { name: 'Besançon', country: 'France' },
  { name: 'Boulogne-Billancourt', country: 'France' },
  { name: 'Orléans', country: 'France' },
  { name: 'Mulhouse', country: 'France' },
  { name: 'Rouen', country: 'France' },
  { name: 'Pau', country: 'France' },
  { name: 'Caen', country: 'France' },
  { name: 'Nancy', country: 'France' },
  { name: 'Argenteuil', country: 'France' },
  { name: 'Saint-Denis', country: 'France' },
  { name: 'Montreuil', country: 'France' },
  { name: 'Avignon', country: 'France' },
  { name: 'Antibes', country: 'France' },
  { name: 'Saint-Tropez', country: 'France' },
  { name: 'Fréjus', country: 'France' },
  { name: 'Menton', country: 'France' },
  { name: 'Èze', country: 'France' },
  { name: 'Villefranche-sur-Mer', country: 'France' },
  { name: 'Grasse', country: 'France' },
  { name: 'Vence', country: 'France' },
  { name: 'Saint-Paul-de-Vence', country: 'France' },
  { name: 'Juan-les-Pins', country: 'France' },
  { name: 'Cap d\'Antibes', country: 'France' },
  { name: 'Cassis', country: 'France' },
  { name: 'La Ciotat', country: 'France' },
  { name: 'Bandol', country: 'France' },
  { name: 'Hyères', country: 'France' },
  { name: 'Porquerolles', country: 'France' },
  { name: 'Port-Cros', country: 'France' },
  { name: 'Île du Levant', country: 'France' },
  { name: 'Saint-Maxime', country: 'France' },
  { name: 'Sainte-Maxime', country: 'France' },
  { name: 'Ramatuelle', country: 'France' },
  { name: 'Gassin', country: 'France' },
  { name: 'Port Grimaud', country: 'France' },
  { name: 'Grimaud', country: 'France' },
  { name: 'Cavalaire-sur-Mer', country: 'France' },
  { name: 'Le Lavandou', country: 'France' },
  { name: 'Bormes-les-Mimosas', country: 'France' },
  { name: 'Arles', country: 'France' },
  { name: 'Les Baux-de-Provence', country: 'France' },
  { name: 'Saint-Rémy-de-Provence', country: 'France' },
  { name: 'Gordes', country: 'France' },
  { name: 'Roussillon', country: 'France' },
  { name: 'Bonnieux', country: 'France' },
  { name: 'Ménerbes', country: 'France' },
  { name: 'Lacoste', country: 'France' },
  { name: 'Lourmarin', country: 'France' },
  { name: 'Apt', country: 'France' },
  { name: 'Isle-sur-la-Sorgue', country: 'France' },
  { name: 'Fontaine-de-Vaucluse', country: 'France' },
  { name: 'Orange', country: 'France' },
  { name: 'Vaison-la-Romaine', country: 'France' },
  { name: 'Séguret', country: 'France' },
  { name: 'Gigondas', country: 'France' },
  { name: 'Châteauneuf-du-Pape', country: 'France' },
  { name: 'Pont du Gard', country: 'France' },
  { name: 'Uzès', country: 'France' },
  { name: 'Aigues-Mortes', country: 'France' },
  { name: 'Saintes-Maries-de-la-Mer', country: 'France' },
  { name: 'Camargue', country: 'France' },
  { name: 'Carcassonne', country: 'France' },
  { name: 'Albi', country: 'France' },
  { name: 'Cordes-sur-Ciel', country: 'France' },
  { name: 'Collioure', country: 'France' },
  { name: 'Céret', country: 'France' },
  { name: 'Prades', country: 'France' },
  { name: 'Amélie-les-Bains', country: 'France' },
  { name: 'Lourdes', country: 'France' },
  { name: 'Cauterets', country: 'France' },
  { name: 'Gavarnie', country: 'France' },
  { name: 'Luz-Saint-Sauveur', country: 'France' },
  { name: 'Bagnères-de-Bigorre', country: 'France' },
  { name: 'Saint-Jean-Pied-de-Port', country: 'France' },
  { name: 'Bayonne', country: 'France' },
  { name: 'Biarritz', country: 'France' },
  { name: 'Saint-Jean-de-Luz', country: 'France' },
  { name: 'Hendaye', country: 'France' },
  { name: 'Hossegor', country: 'France' },
  { name: 'Capbreton', country: 'France' },
  { name: 'Arcachon', country: 'France' },
  { name: 'Dune du Pilat', country: 'France' },
  { name: 'Cap Ferret', country: 'France' },
  { name: 'Lacanau', country: 'France' },
  { name: 'Pauillac', country: 'France' },
  { name: 'Margaux', country: 'France' },
  { name: 'Saint-Émilion', country: 'France' },
  { name: 'Bergerac', country: 'France' },
  { name: 'Sarlat-la-Canéda', country: 'France' },
  { name: 'Rocamadour', country: 'France' },
  { name: 'Cahors', country: 'France' },
  { name: 'Figeac', country: 'France' },
  { name: 'Conques', country: 'France' },
  { name: 'Rodez', country: 'France' },
  { name: 'Millau', country: 'France' },
  { name: 'Gorges du Tarn', country: 'France' },
  { name: 'Mont-Saint-Michel', country: 'France' },
  { name: 'Saint-Malo', country: 'France' },
  { name: 'Dinard', country: 'France' },
  { name: 'Cancale', country: 'France' },
  { name: 'Dinan', country: 'France' },
  { name: 'Paimpol', country: 'France' },
  { name: 'Perros-Guirec', country: 'France' },
  { name: 'Ploumanac\'h', country: 'France' },
  { name: 'Trébeurden', country: 'France' },
  { name: 'Roscoff', country: 'France' },
  { name: 'Morlaix', country: 'France' },
  { name: 'Quimper', country: 'France' },
  { name: 'Concarneau', country: 'France' },
  { name: 'Pont-Aven', country: 'France' },
  { name: 'Quiberon', country: 'France' },
  { name: 'Belle-Île-en-Mer', country: 'France' },
  { name: 'Carnac', country: 'France' },
  { name: 'Vannes', country: 'France' },
  { name: 'Auray', country: 'France' },
  { name: 'La Baule', country: 'France' },
  { name: 'Pornic', country: 'France' },
  { name: 'Noirmoutier', country: 'France' },
  { name: 'Île de Ré', country: 'France' },
  { name: 'Île d\'Oléron', country: 'France' },
  { name: 'La Rochelle', country: 'France' },
  { name: 'Rochefort', country: 'France' },
  { name: 'Saintes', country: 'France' },
  { name: 'Cognac', country: 'France' },
  { name: 'Angoulême', country: 'France' },
  { name: 'Poitiers', country: 'France' },
  { name: 'Futuroscope', country: 'France' },
  { name: 'Chambord', country: 'France' },
  { name: 'Chenonceau', country: 'France' },
  { name: 'Amboise', country: 'France' },
  { name: 'Blois', country: 'France' },
  { name: 'Cheverny', country: 'France' },
  { name: 'Chaumont-sur-Loire', country: 'France' },
  { name: 'Azay-le-Rideau', country: 'France' },
  { name: 'Villandry', country: 'France' },
  { name: 'Chinon', country: 'France' },
  { name: 'Saumur', country: 'France' },
  { name: 'Fontevraud', country: 'France' },
  { name: 'Angers (Loire)', country: 'France' },
  { name: 'Le Mans (Sarthe)', country: 'France' },
  { name: 'Chartres', country: 'France' },
  { name: 'Giverny', country: 'France' },
  { name: 'Auvers-sur-Oise', country: 'France' },
  { name: 'Chantilly', country: 'France' },
  { name: 'Senlis', country: 'France' },
  { name: 'Compiègne', country: 'France' },
  { name: 'Beauvais', country: 'France' },
  { name: 'Étretat', country: 'France' },
  { name: 'Honfleur', country: 'France' },
  { name: 'Deauville', country: 'France' },
  { name: 'Trouville-sur-Mer', country: 'France' },
  { name: 'Cabourg', country: 'France' },
  { name: 'Bayeux', country: 'France' },
  { name: 'D-Day Beaches', country: 'France' },
  { name: 'Omaha Beach', country: 'France' },
  { name: 'Arromanches', country: 'France' },
  { name: 'Cherbourg', country: 'France' },
  { name: 'Barfleur', country: 'France' },
  { name: 'Saint-Vaast-la-Hougue', country: 'France' },
  { name: 'Coutances', country: 'France' },
  { name: 'Granville', country: 'France' },
  { name: 'Villers-sur-Mer', country: 'France' },
  { name: 'Houlgate', country: 'France' },
  { name: 'Beaune', country: 'France' },
  { name: 'Dijon (Burgundy)', country: 'France' },
  { name: 'Vézelay', country: 'France' },
  { name: 'Cluny', country: 'France' },
  { name: 'Mâcon', country: 'France' },
  { name: 'Tournus', country: 'France' },
  { name: 'Autun', country: 'France' },
  { name: 'Semur-en-Auxois', country: 'France' },
  { name: 'Flavigny-sur-Ozerain', country: 'France' },
  { name: 'Noyers-sur-Serein', country: 'France' },
  { name: 'Auxerre', country: 'France' },
  { name: 'Chablis', country: 'France' },
  { name: 'Épernay', country: 'France' },
  { name: 'Reims (Champagne)', country: 'France' },
  { name: 'Troyes', country: 'France' },
  { name: 'Provins', country: 'France' },
  { name: 'Colmar', country: 'France' },
  { name: 'Strasbourg (Alsace)', country: 'France' },
  { name: 'Riquewihr', country: 'France' },
  { name: 'Ribeauvillé', country: 'France' },
  { name: 'Eguisheim', country: 'France' },
  { name: 'Kaysersberg', country: 'France' },
  { name: 'Obernai', country: 'France' },
  { name: 'Haut-Koenigsbourg', country: 'France' },
  { name: 'Mulhouse (Alsace)', country: 'France' },
  { name: 'Metz (Lorraine)', country: 'France' },
  { name: 'Nancy (Lorraine)', country: 'France' },
  { name: 'Verdun', country: 'France' },
  { name: 'Épinal', country: 'France' },
  { name: 'Besançon (Franche-Comté)', country: 'France' },
  { name: 'Belfort', country: 'France' },
  { name: 'Arbois', country: 'France' },
  { name: 'Baume-les-Messieurs', country: 'France' },
  { name: 'Jura Mountains', country: 'France' },
  { name: 'Annecy', country: 'France' },
  { name: 'Lake Annecy', country: 'France' },
  { name: 'Chamonix-Mont-Blanc', country: 'France' },
  { name: 'Megève', country: 'France' },
  { name: 'Courchevel', country: 'France' },
  { name: 'Val d\'Isère', country: 'France' },
  { name: 'Tignes', country: 'France' },
  { name: 'Les Deux Alpes', country: 'France' },
  { name: 'Alpe d\'Huez', country: 'France' },
  { name: 'Méribel', country: 'France' },
  { name: 'Val Thorens', country: 'France' },
  { name: 'La Plagne', country: 'France' },
  { name: 'Les Arcs', country: 'France' },
  { name: 'Morzine', country: 'France' },
  { name: 'Avoriaz', country: 'France' },
  { name: 'Évian-les-Bains', country: 'France' },
  { name: 'Thonon-les-Bains', country: 'France' },
  { name: 'Aix-les-Bains', country: 'France' },
  { name: 'Chambéry', country: 'France' },
  { name: 'Albertville', country: 'France' },
  { name: 'Grenoble (Alps)', country: 'France' },
  { name: 'Briançon', country: 'France' },
  { name: 'Serre Chevalier', country: 'France' },
  { name: 'Gap', country: 'France' },
  { name: 'Embrun', country: 'France' },
  { name: 'Lac de Serre-Ponçon', country: 'France' },
  { name: 'Sisteron', country: 'France' },
  { name: 'Digne-les-Bains', country: 'France' },
  { name: 'Moustiers-Sainte-Marie', country: 'France' },
  { name: 'Verdon Gorge', country: 'France' },
  { name: 'Castellane', country: 'France' },
  { name: 'Entrevaux', country: 'France' },
  { name: 'Lyon (Rhône-Alpes)', country: 'France' },
  { name: 'Vienne', country: 'France' },
  { name: 'Pérouges', country: 'France' },
  { name: 'Bourg-en-Bresse', country: 'France' },
  { name: 'Valence', country: 'France' },
  { name: 'Montélimar', country: 'France' },
  { name: 'Nyons', country: 'France' },
  { name: 'Grignan', country: 'France' },
  { name: 'Die', country: 'France' },
  { name: 'Vercors', country: 'France' },
  { name: 'Mont Ventoux', country: 'France' },
  { name: 'Dentelles de Montmirail', country: 'France' },
  { name: 'Luberon', country: 'France' },
  { name: 'Alpilles', country: 'France' },
  { name: 'Calanques', country: 'France' },
  { name: 'French Riviera', country: 'France' },
  { name: 'Provence', country: 'France' },
  { name: 'Loire Valley', country: 'France' },
  { name: 'Normandy', country: 'France' },
  { name: 'Brittany', country: 'France' },
  { name: 'Burgundy', country: 'France' },
  { name: 'Champagne', country: 'France' },
  { name: 'Alsace', country: 'France' },
  { name: 'French Alps', country: 'France' },
  { name: 'Dordogne', country: 'France' },
  { name: 'Basque Country (France)', country: 'France' },
  { name: 'Pyrénées', country: 'France' },
  { name: 'Aquitaine', country: 'France' },
  { name: 'Languedoc', country: 'France' },
  { name: 'Corsica', country: 'France' },
  { name: 'Ajaccio', country: 'France' },
  { name: 'Bastia', country: 'France' },
  { name: 'Bonifacio', country: 'France' },
  { name: 'Porto-Vecchio', country: 'France' },
  { name: 'Calvi', country: 'France' },
  { name: 'L\'Île-Rousse', country: 'France' },
  { name: 'Corte', country: 'France' },
  { name: 'Scandola', country: 'France' },
  { name: 'Calanques de Piana', country: 'France' },
  { name: 'Cap Corse', country: 'France' },
  { name: 'Lavezzi Islands', country: 'France' },
  { name: 'Versailles', country: 'France' },
  { name: 'Fontainebleau', country: 'France' },
  { name: 'Vaux-le-Vicomte', country: 'France' },
  { name: 'Disneyland Paris', country: 'France' },
  { name: 'Mont Blanc', country: 'France' },
  { name: 'Cannes', country: 'France' },
  { name: 'Monaco', country: 'Monaco' },
  
  { name: 'Berlin', country: 'Germany' },
  { name: 'Hamburg', country: 'Germany' },
  { name: 'Munich', country: 'Germany' },
  { name: 'Cologne', country: 'Germany' },
  { name: 'Frankfurt', country: 'Germany' },
  { name: 'Stuttgart', country: 'Germany' },
  { name: 'Düsseldorf', country: 'Germany' },
  { name: 'Leipzig', country: 'Germany' },
  { name: 'Dortmund', country: 'Germany' },
  { name: 'Essen', country: 'Germany' },
  { name: 'Bremen', country: 'Germany' },
  { name: 'Dresden', country: 'Germany' },
  { name: 'Hanover', country: 'Germany' },
  { name: 'Nuremberg', country: 'Germany' },
  { name: 'Duisburg', country: 'Germany' },
  { name: 'Bochum', country: 'Germany' },
  { name: 'Wuppertal', country: 'Germany' },
  { name: 'Bielefeld', country: 'Germany' },
  { name: 'Bonn', country: 'Germany' },
  { name: 'Münster', country: 'Germany' },
  { name: 'Karlsruhe', country: 'Germany' },
  { name: 'Mannheim', country: 'Germany' },
  { name: 'Augsburg', country: 'Germany' },
  { name: 'Wiesbaden', country: 'Germany' },
  { name: 'Mönchengladbach', country: 'Germany' },
  { name: 'Gelsenkirchen', country: 'Germany' },
  { name: 'Aachen', country: 'Germany' },
  { name: 'Braunschweig', country: 'Germany' },
  { name: 'Chemnitz', country: 'Germany' },
  { name: 'Kiel', country: 'Germany' },
  { name: 'Halle', country: 'Germany' },
  { name: 'Magdeburg', country: 'Germany' },
  { name: 'Freiburg', country: 'Germany' },
  { name: 'Krefeld', country: 'Germany' },
  { name: 'Mainz', country: 'Germany' },
  { name: 'Lübeck', country: 'Germany' },
  { name: 'Erfurt', country: 'Germany' },
  { name: 'Oberhausen', country: 'Germany' },
  { name: 'Rostock', country: 'Germany' },
  { name: 'Kassel', country: 'Germany' },
  { name: 'Hagen', country: 'Germany' },
  { name: 'Saarbrücken', country: 'Germany' },
  { name: 'Hamm', country: 'Germany' },
  { name: 'Potsdam', country: 'Germany' },
  { name: 'Ludwigshafen', country: 'Germany' },
  { name: 'Oldenburg', country: 'Germany' },
  { name: 'Leverkusen', country: 'Germany' },
  { name: 'Osnabrück', country: 'Germany' },
  { name: 'Solingen', country: 'Germany' },
  { name: 'Heidelberg', country: 'Germany' },
  { name: 'Herne', country: 'Germany' },
  { name: 'Neuss', country: 'Germany' },
  { name: 'Paderborn', country: 'Germany' },
  { name: 'Regensburg', country: 'Germany' },
  { name: 'Würzburg', country: 'Germany' },
  { name: 'Ingolstadt', country: 'Germany' },
  { name: 'Fürth', country: 'Germany' },
  { name: 'Wolfsburg', country: 'Germany' },
  { name: 'Offenbach', country: 'Germany' },
  { name: 'Ulm', country: 'Germany' },
  { name: 'Heilbronn', country: 'Germany' },
  { name: 'Pforzheim', country: 'Germany' },
  { name: 'Göttingen', country: 'Germany' },
  { name: 'Bottrop', country: 'Germany' },
  { name: 'Trier', country: 'Germany' },
  { name: 'Recklinghausen', country: 'Germany' },
  { name: 'Reutlingen', country: 'Germany' },
  { name: 'Bremerhaven', country: 'Germany' },
  { name: 'Koblenz', country: 'Germany' },
  { name: 'Bergisch Gladbach', country: 'Germany' },
  { name: 'Jena', country: 'Germany' },
  { name: 'Remscheid', country: 'Germany' },
  { name: 'Erlangen', country: 'Germany' },
  { name: 'Moers', country: 'Germany' },
  { name: 'Siegen', country: 'Germany' },
  { name: 'Hildesheim', country: 'Germany' },
  { name: 'Salzgitter', country: 'Germany' },
  { name: 'Cottbus', country: 'Germany' },
  { name: 'Bavarian Alps', country: 'Germany' },
  
  { name: 'Rome', country: 'Italy' },
  { name: 'Milan', country: 'Italy' },
  { name: 'Naples', country: 'Italy' },
  { name: 'Turin', country: 'Italy' },
  { name: 'Palermo', country: 'Italy' },
  { name: 'Genoa', country: 'Italy' },
  { name: 'Bologna', country: 'Italy' },
  { name: 'Florence', country: 'Italy' },
  { name: 'Bari', country: 'Italy' },
  { name: 'Catania', country: 'Italy' },
  { name: 'Venice', country: 'Italy' },
  { name: 'Verona', country: 'Italy' },
  { name: 'Messina', country: 'Italy' },
  { name: 'Padua', country: 'Italy' },
  { name: 'Trieste', country: 'Italy' },
  { name: 'Brescia', country: 'Italy' },
  { name: 'Taranto', country: 'Italy' },
  { name: 'Prato', country: 'Italy' },
  { name: 'Parma', country: 'Italy' },
  { name: 'Modena', country: 'Italy' },
  { name: 'Reggio Calabria', country: 'Italy' },
  { name: 'Reggio Emilia', country: 'Italy' },
  { name: 'Perugia', country: 'Italy' },
  { name: 'Livorno', country: 'Italy' },
  { name: 'Ravenna', country: 'Italy' },
  { name: 'Cagliari', country: 'Italy' },
  { name: 'Foggia', country: 'Italy' },
  { name: 'Rimini', country: 'Italy' },
  { name: 'Salerno', country: 'Italy' },
  { name: 'Ferrara', country: 'Italy' },
  { name: 'Sassari', country: 'Italy' },
  { name: 'Latina', country: 'Italy' },
  { name: 'Pisa', country: 'Italy' },
  { name: 'Bergamo', country: 'Italy' },
  { name: 'Vicenza', country: 'Italy' },
  { name: 'Terni', country: 'Italy' },
  { name: 'Bolzano', country: 'Italy' },
  { name: 'Amalfi Coast', country: 'Italy' },
  { name: 'Cinque Terre', country: 'Italy' },
  { name: 'Lake Como', country: 'Italy' },
  { name: 'Tuscany', country: 'Italy' },
  { name: 'Sicily', country: 'Italy' },
  { name: 'Sardinia', country: 'Italy' },
  { name: 'Capri', country: 'Italy' },
  { name: 'Positano', country: 'Italy' },
  { name: 'Sorrento', country: 'Italy' },
  { name: 'Siena', country: 'Italy' },
  { name: 'San Gimignano', country: 'Italy' },
  { name: 'Assisi', country: 'Italy' },
  { name: 'Orvieto', country: 'Italy' },
  { name: 'Matera', country: 'Italy' },
  { name: 'Lecce', country: 'Italy' },
  { name: 'Taormina', country: 'Italy' },
  { name: 'Palermo', country: 'Italy' },
  { name: 'Syracuse', country: 'Italy' },
  { name: 'Agrigento', country: 'Italy' },
  { name: 'Trapani', country: 'Italy' },
  { name: 'Marsala', country: 'Italy' },
  { name: 'Cefalù', country: 'Italy' },
  { name: 'Ragusa', country: 'Italy' },
  { name: 'Noto', country: 'Italy' },
  { name: 'Modica', country: 'Italy' },
  { name: 'Sciacca', country: 'Italy' },
  { name: 'Erice', country: 'Italy' },
  { name: 'Piazza Armerina', country: 'Italy' },
  { name: 'Enna', country: 'Italy' },
  { name: 'Lipari', country: 'Italy' },
  { name: 'Vulcano', country: 'Italy' },
  { name: 'Stromboli', country: 'Italy' },
  { name: 'Salina', country: 'Italy' },
  { name: 'Panarea', country: 'Italy' },
  { name: 'Filicudi', country: 'Italy' },
  { name: 'Alicudi', country: 'Italy' },
  { name: 'Favignana', country: 'Italy' },
  { name: 'Levanzo', country: 'Italy' },
  { name: 'Marettimo', country: 'Italy' },
  { name: 'Lampedusa', country: 'Italy' },
  { name: 'Pantelleria', country: 'Italy' },
  { name: 'Ustica', country: 'Italy' },
  { name: 'Alghero', country: 'Italy' },
  { name: 'Olbia', country: 'Italy' },
  { name: 'Costa Smeralda', country: 'Italy' },
  { name: 'Porto Cervo', country: 'Italy' },
  { name: 'La Maddalena', country: 'Italy' },
  { name: 'Santa Teresa Gallura', country: 'Italy' },
  { name: 'Castelsardo', country: 'Italy' },
  { name: 'Stintino', country: 'Italy' },
  { name: 'Bosa', country: 'Italy' },
  { name: 'Oristano', country: 'Italy' },
  { name: 'Tharros', country: 'Italy' },
  { name: 'Villasimius', country: 'Italy' },
  { name: 'Chia', country: 'Italy' },
  { name: 'Pula', country: 'Italy' },
  { name: 'Nora', country: 'Italy' },
  { name: 'San Teodoro', country: 'Italy' },
  { name: 'Cala Gonone', country: 'Italy' },
  { name: 'Dorgali', country: 'Italy' },
  { name: 'Nuoro', country: 'Italy' },
  { name: 'Orgosolo', country: 'Italy' },
  { name: 'Barumini', country: 'Italy' },
  { name: 'Tiscali', country: 'Italy' },
  { name: 'Portovenere', country: 'Italy' },
  { name: 'Levanto', country: 'Italy' },
  { name: 'Monterosso al Mare', country: 'Italy' },
  { name: 'Vernazza', country: 'Italy' },
  { name: 'Corniglia', country: 'Italy' },
  { name: 'Manarola', country: 'Italy' },
  { name: 'Riomaggiore', country: 'Italy' },
  { name: 'La Spezia', country: 'Italy' },
  { name: 'Portofino', country: 'Italy' },
  { name: 'Santa Margherita Ligure', country: 'Italy' },
  { name: 'Rapallo', country: 'Italy' },
  { name: 'Camogli', country: 'Italy' },
  { name: 'San Remo', country: 'Italy' },
  { name: 'Bordighera', country: 'Italy' },
  { name: 'Ventimiglia', country: 'Italy' },
  { name: 'Alassio', country: 'Italy' },
  { name: 'Finale Ligure', country: 'Italy' },
  { name: 'Savona', country: 'Italy' },
  { name: 'Imperia', country: 'Italy' },
  { name: 'Lake Garda', country: 'Italy' },
  { name: 'Sirmione', country: 'Italy' },
  { name: 'Desenzano del Garda', country: 'Italy' },
  { name: 'Peschiera del Garda', country: 'Italy' },
  { name: 'Lazise', country: 'Italy' },
  { name: 'Bardolino', country: 'Italy' },
  { name: 'Garda', country: 'Italy' },
  { name: 'Malcesine', country: 'Italy' },
  { name: 'Limone sul Garda', country: 'Italy' },
  { name: 'Riva del Garda', country: 'Italy' },
  { name: 'Torbole', country: 'Italy' },
  { name: 'Lake Maggiore', country: 'Italy' },
  { name: 'Stresa', country: 'Italy' },
  { name: 'Verbania', country: 'Italy' },
  { name: 'Baveno', country: 'Italy' },
  { name: 'Cannobio', country: 'Italy' },
  { name: 'Locarno', country: 'Italy' },
  { name: 'Isola Bella', country: 'Italy' },
  { name: 'Isola Madre', country: 'Italy' },
  { name: 'Isola dei Pescatori', country: 'Italy' },
  { name: 'Bellagio', country: 'Italy' },
  { name: 'Varenna', country: 'Italy' },
  { name: 'Menaggio', country: 'Italy' },
  { name: 'Lecco', country: 'Italy' },
  { name: 'Tremezzo', country: 'Italy' },
  { name: 'Cernobbio', country: 'Italy' },
  { name: 'Como', country: 'Italy' },
  { name: 'Lake Orta', country: 'Italy' },
  { name: 'Orta San Giulio', country: 'Italy' },
  { name: 'Isola di San Giulio', country: 'Italy' },
  { name: 'Sacro Monte di Orta', country: 'Italy' },
  { name: 'Montepulciano', country: 'Italy' },
  { name: 'Montalcino', country: 'Italy' },
  { name: 'Pienza', country: 'Italy' },
  { name: 'San Quirico d\'Orcia', country: 'Italy' },
  { name: 'Cortona', country: 'Italy' },
  { name: 'Arezzo', country: 'Italy' },
  { name: 'Lucca', country: 'Italy' },
  { name: 'Pistoia', country: 'Italy' },
  { name: 'Volterra', country: 'Italy' },
  { name: 'Massa Marittima', country: 'Italy' },
  { name: 'Pitigliano', country: 'Italy' },
  { name: 'Sovana', country: 'Italy' },
  { name: 'Sorano', country: 'Italy' },
  { name: 'Saturnia', country: 'Italy' },
  { name: 'Chianti', country: 'Italy' },
  { name: 'Greve in Chianti', country: 'Italy' },
  { name: 'Radda in Chianti', country: 'Italy' },
  { name: 'Castellina in Chianti', country: 'Italy' },
  { name: 'Gaiole in Chianti', country: 'Italy' },
  { name: 'Val d\'Orcia', country: 'Italy' },
  { name: 'Bagno Vignoni', country: 'Italy' },
  { name: 'Castiglione d\'Orcia', country: 'Italy' },
  { name: 'Radicofani', country: 'Italy' },
  { name: 'Monteriggioni', country: 'Italy' },
  { name: 'Colle di Val d\'Elsa', country: 'Italy' },
  { name: 'Spoleto', country: 'Italy' },
  { name: 'Gubbio', country: 'Italy' },
  { name: 'Todi', country: 'Italy' },
  { name: 'Spello', country: 'Italy' },
  { name: 'Bevagna', country: 'Italy' },
  { name: 'Montefalco', country: 'Italy' },
  { name: 'Norcia', country: 'Italy' },
  { name: 'Cascia', country: 'Italy' },
  { name: 'Cascata delle Marmore', country: 'Italy' },
  { name: 'Lago di Trasimeno', country: 'Italy' },
  { name: 'Passignano sul Trasimeno', country: 'Italy' },
  { name: 'Castiglione del Lago', country: 'Italy' },
  { name: 'Alberobello', country: 'Italy' },
  { name: 'Ostuni', country: 'Italy' },
  { name: 'Locorotondo', country: 'Italy' },
  { name: 'Martina Franca', country: 'Italy' },
  { name: 'Polignano a Mare', country: 'Italy' },
  { name: 'Monopoli', country: 'Italy' },
  { name: 'Otranto', country: 'Italy' },
  { name: 'Gallipoli', country: 'Italy' },
  { name: 'Santa Maria di Leuca', country: 'Italy' },
  { name: 'Castro', country: 'Italy' },
  { name: 'Brindisi', country: 'Italy' },
  { name: 'Trani', country: 'Italy' },
  { name: 'Barletta', country: 'Italy' },
  { name: 'Castel del Monte', country: 'Italy' },
  { name: 'Vieste', country: 'Italy' },
  { name: 'Peschici', country: 'Italy' },
  { name: 'Tremiti Islands', country: 'Italy' },
  { name: 'Gargano Peninsula', country: 'Italy' },
  { name: 'Monte Sant\'Angelo', country: 'Italy' },
  { name: 'Ravello', country: 'Italy' },
  { name: 'Atrani', country: 'Italy' },
  { name: 'Amalfi', country: 'Italy' },
  { name: 'Furore', country: 'Italy' },
  { name: 'Praiano', country: 'Italy' },
  { name: 'Conca dei Marini', country: 'Italy' },
  { name: 'Cetara', country: 'Italy' },
  { name: 'Maiori', country: 'Italy' },
  { name: 'Minori', country: 'Italy' },
  { name: 'Vietri sul Mare', country: 'Italy' },
  { name: 'Paestum', country: 'Italy' },
  { name: 'Cilento', country: 'Italy' },
  { name: 'Castellabate', country: 'Italy' },
  { name: 'Agropoli', country: 'Italy' },
  { name: 'Acciaroli', country: 'Italy' },
  { name: 'Ischia', country: 'Italy' },
  { name: 'Procida', country: 'Italy' },
  { name: 'Anacapri', country: 'Italy' },
  { name: 'Marina Grande', country: 'Italy' },
  { name: 'Blue Grotto', country: 'Italy' },
  { name: 'Pompeii', country: 'Italy' },
  { name: 'Herculaneum', country: 'Italy' },
  { name: 'Mount Vesuvius', country: 'Italy' },
  { name: 'Caserta', country: 'Italy' },
  { name: 'Reggia di Caserta', country: 'Italy' },
  { name: 'Abruzzo National Park', country: 'Italy' },
  { name: 'L\'Aquila', country: 'Italy' },
  { name: 'Gran Sasso', country: 'Italy' },
  { name: 'Campo Imperatore', country: 'Italy' },
  { name: 'Sulmona', country: 'Italy' },
  { name: 'Scanno', country: 'Italy' },
  { name: 'Pescara', country: 'Italy' },
  { name: 'Chieti', country: 'Italy' },
  { name: 'Rocca Calascio', country: 'Italy' },
  { name: 'Santo Stefano di Sessanio', country: 'Italy' },
  { name: 'Civita di Bagnoregio', country: 'Italy' },
  { name: 'Viterbo', country: 'Italy' },
  { name: 'Bomarzo', country: 'Italy' },
  { name: 'Calcata', country: 'Italy' },
  { name: 'Tivoli', country: 'Italy' },
  { name: 'Villa d\'Este', country: 'Italy' },
  { name: 'Hadrian\'s Villa', country: 'Italy' },
  { name: 'Frascati', country: 'Italy' },
  { name: 'Castelli Romani', country: 'Italy' },
  { name: 'Nemi', country: 'Italy' },
  { name: 'Genzano', country: 'Italy' },
  { name: 'Sperlonga', country: 'Italy' },
  { name: 'Gaeta', country: 'Italy' },
  { name: 'Terracina', country: 'Italy' },
  { name: 'Sabaudia', country: 'Italy' },
  { name: 'San Felice Circeo', country: 'Italy' },
  { name: 'Ponza', country: 'Italy' },
  { name: 'Ventotene', country: 'Italy' },
  { name: 'Dolomites', country: 'Italy' },
  { name: 'Cortina d\'Ampezzo', country: 'Italy' },
  { name: 'Val Gardena', country: 'Italy' },
  { name: 'Ortisei', country: 'Italy' },
  { name: 'Selva di Val Gardena', country: 'Italy' },
  { name: 'Santa Cristina Valgardena', country: 'Italy' },
  { name: 'Val di Fassa', country: 'Italy' },
  { name: 'Canazei', country: 'Italy' },
  { name: 'Moena', country: 'Italy' },
  { name: 'Alta Badia', country: 'Italy' },
  { name: 'Corvara', country: 'Italy' },
  { name: 'La Villa', country: 'Italy' },
  { name: 'Arabba', country: 'Italy' },
  { name: 'Tre Cime di Lavaredo', country: 'Italy' },
  { name: 'Lago di Braies', country: 'Italy' },
  { name: 'Lago di Carezza', country: 'Italy' },
  { name: 'Merano', country: 'Italy' },
  { name: 'Bressanone', country: 'Italy' },
  { name: 'Brunico', country: 'Italy' },
  { name: 'Vipiteno', country: 'Italy' },
  { name: 'Trento', country: 'Italy' },
  { name: 'Rovereto', country: 'Italy' },
  { name: 'Madonna di Campiglio', country: 'Italy' },
  { name: 'San Martino di Castrozza', country: 'Italy' },
  { name: 'Molveno', country: 'Italy' },
  { name: 'Riva del Garda', country: 'Italy' },
  { name: 'Bassano del Grappa', country: 'Italy' },
  { name: 'Asolo', country: 'Italy' },
  { name: 'Treviso', country: 'Italy' },
  { name: 'Castelfranco Veneto', country: 'Italy' },
  { name: 'Cittadella', country: 'Italy' },
  { name: 'Este', country: 'Italy' },
  { name: 'Monselice', country: 'Italy' },
  { name: 'Arquà Petrarca', country: 'Italy' },
  { name: 'Abano Terme', country: 'Italy' },
  { name: 'Montagnana', country: 'Italy' },
  { name: 'Chioggia', country: 'Italy' },
  { name: 'Burano', country: 'Italy' },
  { name: 'Murano', country: 'Italy' },
  { name: 'Torcello', country: 'Italy' },
  { name: 'Lido di Venezia', country: 'Italy' },
  { name: 'Caorle', country: 'Italy' },
  { name: 'Bibione', country: 'Italy' },
  { name: 'Lignano Sabbiadoro', country: 'Italy' },
  { name: 'Grado', country: 'Italy' },
  { name: 'Aquileia', country: 'Italy' },
  { name: 'Udine', country: 'Italy' },
  { name: 'Cividale del Friuli', country: 'Italy' },
  { name: 'Pordenone', country: 'Italy' },
  { name: 'Gorizia', country: 'Italy' },
  { name: 'Palmanova', country: 'Italy' },
  { name: 'Mantua', country: 'Italy' },
  { name: 'Cremona', country: 'Italy' },
  { name: 'Sabbioneta', country: 'Italy' },
  { name: 'Sirmione', country: 'Italy' },
  { name: 'Pavia', country: 'Italy' },
  { name: 'Certosa di Pavia', country: 'Italy' },
  { name: 'Lodi', country: 'Italy' },
  { name: 'Crema', country: 'Italy' },
  { name: 'Monza', country: 'Italy' },
  { name: 'Lecco', country: 'Italy' },
  { name: 'Sondrio', country: 'Italy' },
  { name: 'Bormio', country: 'Italy' },
  { name: 'Livigno', country: 'Italy' },
  { name: 'Valtellina', country: 'Italy' },
  { name: 'Tirano', country: 'Italy' },
  { name: 'Bergamo Alta', country: 'Italy' },
  { name: 'Crespi d\'Adda', country: 'Italy' },
  { name: 'Iseo', country: 'Italy' },
  { name: 'Monte Isola', country: 'Italy' },
  { name: 'Franciacorta', country: 'Italy' },
  { name: 'Salo', country: 'Italy' },
  { name: 'Gardone Riviera', country: 'Italy' },
  { name: 'Torri del Benaco', country: 'Italy' },
  
  { name: 'Madrid', country: 'Spain' },
  { name: 'Barcelona', country: 'Spain' },
  { name: 'Valencia', country: 'Spain' },
  { name: 'Seville', country: 'Spain' },
  { name: 'Zaragoza', country: 'Spain' },
  { name: 'Málaga', country: 'Spain' },
  { name: 'Murcia', country: 'Spain' },
  { name: 'Palma', country: 'Spain' },
  { name: 'Las Palmas', country: 'Spain' },
  { name: 'Bilbao', country: 'Spain' },
  { name: 'Alicante', country: 'Spain' },
  { name: 'Córdoba', country: 'Spain' },
  { name: 'Valladolid', country: 'Spain' },
  { name: 'Vigo', country: 'Spain' },
  { name: 'Gijón', country: 'Spain' },
  { name: 'Hospitalet de Llobregat', country: 'Spain' },
  { name: 'Vitoria-Gasteiz', country: 'Spain' },
  { name: 'A Coruña', country: 'Spain' },
  { name: 'Elche', country: 'Spain' },
  { name: 'Oviedo', country: 'Spain' },
  { name: 'Santa Cruz de Tenerife', country: 'Spain' },
  { name: 'Badalona', country: 'Spain' },
  { name: 'Cartagena', country: 'Spain' },
  { name: 'Terrassa', country: 'Spain' },
  { name: 'Jerez de la Frontera', country: 'Spain' },
  { name: 'Sabadell', country: 'Spain' },
  { name: 'Móstoles', country: 'Spain' },
  { name: 'Alcalá de Henares', country: 'Spain' },
  { name: 'Pamplona', country: 'Spain' },
  { name: 'Fuenlabrada', country: 'Spain' },
  { name: 'Almería', country: 'Spain' },
  { name: 'Leganés', country: 'Spain' },
  { name: 'Santander', country: 'Spain' },
  { name: 'Burgos', country: 'Spain' },
  { name: 'Castellón de la Plana', country: 'Spain' },
  { name: 'Alcorcón', country: 'Spain' },
  { name: 'Albacete', country: 'Spain' },
  { name: 'Getafe', country: 'Spain' },
  { name: 'Salamanca', country: 'Spain' },
  { name: 'San Cristóbal de La Laguna', country: 'Spain' },
  { name: 'Huelva', country: 'Spain' },
  { name: 'Badajoz', country: 'Spain' },
  { name: 'Tarragona', country: 'Spain' },
  { name: 'León', country: 'Spain' },
  { name: 'Cádiz', country: 'Spain' },
  { name: 'Jaén', country: 'Spain' },
  { name: 'Ourense', country: 'Spain' },
  { name: 'Reus', country: 'Spain' },
  { name: 'Torrelavega', country: 'Spain' },
  { name: 'Elda', country: 'Spain' },
  { name: 'Lugo', country: 'Spain' },
  { name: 'Santiago de Compostela', country: 'Spain' },
  { name: 'Girona', country: 'Spain' },
  { name: 'Cáceres', country: 'Spain' },
  { name: 'Lorca', country: 'Spain' },
  { name: 'Coslada', country: 'Spain' },
  { name: 'Talavera de la Reina', country: 'Spain' },
  { name: 'El Puerto de Santa María', country: 'Spain' },
  { name: 'Cornellà de Llobregat', country: 'Spain' },
  { name: 'Avilés', country: 'Spain' },
  { name: 'Palencia', country: 'Spain' },
  { name: 'Gáldar', country: 'Spain' },
  { name: 'Barakaldo', country: 'Spain' },
  
  { name: 'Athens', country: 'Greece' },
  { name: 'Thessaloniki', country: 'Greece' },
  { name: 'Patras', country: 'Greece' },
  { name: 'Heraklion', country: 'Greece' },
  { name: 'Larissa', country: 'Greece' },
  { name: 'Volos', country: 'Greece' },
  { name: 'Ioannina', country: 'Greece' },
  { name: 'Kavala', country: 'Greece' },
  { name: 'Chania', country: 'Greece' },
  { name: 'Rethymno', country: 'Greece' },
  { name: 'Agios Nikolaos', country: 'Greece' },
  { name: 'Ierapetra', country: 'Greece' },
  
  // Cyclades Islands
  { name: 'Santorini', country: 'Greece' },
  { name: 'Mykonos', country: 'Greece' },
  { name: 'Naxos', country: 'Greece' },
  { name: 'Paros', country: 'Greece' },
  { name: 'Ios', country: 'Greece' },
  { name: 'Milos', country: 'Greece' },
  { name: 'Sifnos', country: 'Greece' },
  { name: 'Syros', country: 'Greece' },
  { name: 'Tinos', country: 'Greece' },
  { name: 'Andros', country: 'Greece' },
  { name: 'Folegandros', country: 'Greece' },
  { name: 'Amorgos', country: 'Greece' },
  { name: 'Antiparos', country: 'Greece' },
  { name: 'Serifos', country: 'Greece' },
  { name: 'Kythnos', country: 'Greece' },
  { name: 'Anafi', country: 'Greece' },
  { name: 'Sikinos', country: 'Greece' },
  { name: 'Koufonisia', country: 'Greece' },
  
  // Dodecanese Islands
  { name: 'Rhodes', country: 'Greece' },
  { name: 'Kos', country: 'Greece' },
  { name: 'Karpathos', country: 'Greece' },
  { name: 'Patmos', country: 'Greece' },
  { name: 'Leros', country: 'Greece' },
  { name: 'Kalymnos', country: 'Greece' },
  { name: 'Symi', country: 'Greece' },
  { name: 'Tilos', country: 'Greece' },
  { name: 'Nisyros', country: 'Greece' },
  { name: 'Astypalea', country: 'Greece' },
  { name: 'Kastellorizo', country: 'Greece' },
  { name: 'Halki', country: 'Greece' },
  
  // Ionian Islands
  { name: 'Corfu', country: 'Greece' },
  { name: 'Zakynthos', country: 'Greece' },
  { name: 'Kefalonia', country: 'Greece' },
  { name: 'Lefkada', country: 'Greece' },
  { name: 'Ithaca', country: 'Greece' },
  { name: 'Paxos', country: 'Greece' },
  { name: 'Antipaxos', country: 'Greece' },
  { name: 'Kythira', country: 'Greece' },
  
  // Sporades Islands
  { name: 'Skiathos', country: 'Greece' },
  { name: 'Skopelos', country: 'Greece' },
  { name: 'Alonissos', country: 'Greece' },
  { name: 'Skyros', country: 'Greece' },
  
  // Northeast Aegean Islands
  { name: 'Lesvos', country: 'Greece' },
  { name: 'Chios', country: 'Greece' },
  { name: 'Samos', country: 'Greece' },
  { name: 'Ikaria', country: 'Greece' },
  { name: 'Lemnos', country: 'Greece' },
  { name: 'Thasos', country: 'Greece' },
  { name: 'Samothrace', country: 'Greece' },
  { name: 'Fourni', country: 'Greece' },
  
  // Saronic Islands
  { name: 'Hydra', country: 'Greece' },
  { name: 'Spetses', country: 'Greece' },
  { name: 'Aegina', country: 'Greece' },
  { name: 'Poros', country: 'Greece' },
  { name: 'Salamina', country: 'Greece' },
  { name: 'Agistri', country: 'Greece' },
  
  // Crete regions
  { name: 'Crete', country: 'Greece' },
  { name: 'Elounda', country: 'Greece' },
  { name: 'Matala', country: 'Greece' },
  { name: 'Balos', country: 'Greece' },
  { name: 'Elafonisi', country: 'Greece' },
  { name: 'Knossos', country: 'Greece' },
  { name: 'Samaria Gorge', country: 'Greece' },
  
  // Historical & mainland destinations
  { name: 'Meteora', country: 'Greece' },
  { name: 'Delphi', country: 'Greece' },
  { name: 'Olympia', country: 'Greece' },
  { name: 'Nafplio', country: 'Greece' },
  { name: 'Monemvasia', country: 'Greece' },
  { name: 'Mycenae', country: 'Greece' },
  { name: 'Epidaurus', country: 'Greece' },
  { name: 'Vergina', country: 'Greece' },
  { name: 'Kalamata', country: 'Greece' },
  { name: 'Alexandroupoli', country: 'Greece' },
  { name: 'Kastoria', country: 'Greece' },
  { name: 'Xanthi', country: 'Greece' },
  { name: 'Drama', country: 'Greece' },
  { name: 'Kozani', country: 'Greece' },
  { name: 'Serres', country: 'Greece' },
  { name: 'Tripoli', country: 'Greece' },
  { name: 'Sparta', country: 'Greece' },
  { name: 'Corinth', country: 'Greece' },
  { name: 'Delphi Archaeological Site', country: 'Greece' },
  { name: 'Mount Olympus', country: 'Greece' },
  { name: 'Mount Athos', country: 'Greece' },
  { name: 'Pelion', country: 'Greece' },
  { name: 'Parga', country: 'Greece' },
  { name: 'Preveza', country: 'Greece' },
  { name: 'Metsovo', country: 'Greece' },
  { name: 'Zagorochoria', country: 'Greece' },
  { name: 'Vikos Gorge', country: 'Greece' },
  { name: 'Pilio', country: 'Greece' },
  { name: 'Loutraki', country: 'Greece' },
  { name: 'Porto Heli', country: 'Greece' },
  { name: 'Ermioni', country: 'Greece' },
  { name: 'Tolo', country: 'Greece' },
  { name: 'Kardamyli', country: 'Greece' },
  { name: 'Gythio', country: 'Greece' },
  { name: 'Methoni', country: 'Greece' },
  { name: 'Pylos', country: 'Greece' },
  { name: 'Stoupa', country: 'Greece' },
  { name: 'Kalavrita', country: 'Greece' },
  
  // Add many more countries and cities here...
  // Additional Spain locations - Expanded
  { name: 'Barcelona', country: 'Spain' },
  { name: 'Valencia', country: 'Spain' },
  { name: 'Seville', country: 'Spain' },
  { name: 'Zaragoza', country: 'Spain' },
  { name: 'Málaga', country: 'Spain' },
  { name: 'Murcia', country: 'Spain' },
  { name: 'Palma', country: 'Spain' },
  { name: 'Las Palmas', country: 'Spain' },
  { name: 'Bilbao', country: 'Spain' },
  { name: 'Alicante', country: 'Spain' },
  { name: 'Córdoba', country: 'Spain' },
  { name: 'Valladolid', country: 'Spain' },
  { name: 'Vigo', country: 'Spain' },
  { name: 'Gijón', country: 'Spain' },
  { name: 'L\'Hospitalet de Llobregat', country: 'Spain' },
  { name: 'A Coruña', country: 'Spain' },
  { name: 'Vitoria-Gasteiz', country: 'Spain' },
  { name: 'Granada', country: 'Spain' },
  { name: 'Elche', country: 'Spain' },
  { name: 'Oviedo', country: 'Spain' },
  { name: 'Badalona', country: 'Spain' },
  { name: 'Cartagena', country: 'Spain' },
  { name: 'Terrassa', country: 'Spain' },
  { name: 'Jerez de la Frontera', country: 'Spain' },
  { name: 'Sabadell', country: 'Spain' },
  { name: 'Móstoles', country: 'Spain' },
  { name: 'Alcalá de Henares', country: 'Spain' },
  { name: 'Pamplona', country: 'Spain' },
  { name: 'Fuenlabrada', country: 'Spain' },
  { name: 'Almería', country: 'Spain' },
  { name: 'Leganés', country: 'Spain' },
  { name: 'Santander', country: 'Spain' },
  { name: 'Burgos', country: 'Spain' },
  { name: 'Castellón de la Plana', country: 'Spain' },
  { name: 'Albacete', country: 'Spain' },
  { name: 'Alcorcón', country: 'Spain' },
  { name: 'Getafe', country: 'Spain' },
  { name: 'Salamanca', country: 'Spain' },
  { name: 'Huelva', country: 'Spain' },
  { name: 'Badajoz', country: 'Spain' },
  { name: 'Logroño', country: 'Spain' },
  { name: 'León', country: 'Spain' },
  { name: 'Tarragona', country: 'Spain' },
  { name: 'Cádiz', country: 'Spain' },
  { name: 'Lleida', country: 'Spain' },
  { name: 'Girona', country: 'Spain' },
  { name: 'Jaén', country: 'Spain' },
  { name: 'Ourense', country: 'Spain' },
  { name: 'Santiago de Compostela', country: 'Spain' },
  { name: 'Toledo', country: 'Spain' },
  { name: 'Ronda', country: 'Spain' },
  { name: 'San Sebastian', country: 'Spain' },
  { name: 'Segovia', country: 'Spain' },
  { name: 'Ávila', country: 'Spain' },
  { name: 'Cuenca', country: 'Spain' },
  { name: 'Marbella', country: 'Spain' },
  { name: 'Torremolinos', country: 'Spain' },
  { name: 'Benidorm', country: 'Spain' },
  { name: 'Nerja', country: 'Spain' },
  { name: 'Mijas', country: 'Spain' },
  { name: 'Estepona', country: 'Spain' },
  { name: 'Fuengirola', country: 'Spain' },
  { name: 'Sitges', country: 'Spain' },
  { name: 'Lloret de Mar', country: 'Spain' },
  { name: 'Tossa de Mar', country: 'Spain' },
  { name: 'Cadaqués', country: 'Spain' },
  { name: 'Peñíscola', country: 'Spain' },
  { name: 'Denia', country: 'Spain' },
  { name: 'Jávea', country: 'Spain' },
  { name: 'Calpe', country: 'Spain' },
  { name: 'Altea', country: 'Spain' },
  { name: 'Santa Pola', country: 'Spain' },
  { name: 'Torrevieja', country: 'Spain' },
  { name: 'Mojácar', country: 'Spain' },
  { name: 'Cabo de Gata', country: 'Spain' },
  { name: 'San José', country: 'Spain' },
  { name: 'Tarifa', country: 'Spain' },
  { name: 'Conil de la Frontera', country: 'Spain' },
  { name: 'Vejer de la Frontera', country: 'Spain' },
  { name: 'Sanlúcar de Barrameda', country: 'Spain' },
  { name: 'El Puerto de Santa María', country: 'Spain' },
  { name: 'Chipiona', country: 'Spain' },
  { name: 'Rota', country: 'Spain' },
  { name: 'La Línea de la Concepción', country: 'Spain' },
  { name: 'Algeciras', country: 'Spain' },
  { name: 'Mérida', country: 'Spain' },
  { name: 'Plasencia', country: 'Spain' },
  { name: 'Trujillo', country: 'Spain' },
  { name: 'Zafra', country: 'Spain' },
  { name: 'Pontevedra', country: 'Spain' },
  { name: 'Lugo', country: 'Spain' },
  { name: 'Ferrol', country: 'Spain' },
  { name: 'Orense', country: 'Spain' },
  { name: 'Ribeira', country: 'Spain' },
  { name: 'Viveiro', country: 'Spain' },
  { name: 'Ribadeo', country: 'Spain' },
  { name: 'Sanxenxo', country: 'Spain' },
  { name: 'O Grove', country: 'Spain' },
  { name: 'Cambados', country: 'Spain' },
  { name: 'Combarro', country: 'Spain' },
  { name: 'Baiona', country: 'Spain' },
  { name: 'Cangas', country: 'Spain' },
  { name: 'Llanes', country: 'Spain' },
  { name: 'Ribadesella', country: 'Spain' },
  { name: 'Covadonga', country: 'Spain' },
  { name: 'Picos de Europa', country: 'Spain' },
  { name: 'Cangas de Onís', country: 'Spain' },
  { name: 'Cudillero', country: 'Spain' },
  { name: 'Luarca', country: 'Spain' },
  { name: 'Castro Urdiales', country: 'Spain' },
  { name: 'Laredo', country: 'Spain' },
  { name: 'Santoña', country: 'Spain' },
  { name: 'Comillas', country: 'Spain' },
  { name: 'San Vicente de la Barquera', country: 'Spain' },
  { name: 'Santillana del Mar', country: 'Spain' },
  { name: 'Hondarribia', country: 'Spain' },
  { name: 'Getaria', country: 'Spain' },
  { name: 'Zarautz', country: 'Spain' },
  { name: 'Lekeitio', country: 'Spain' },
  { name: 'Mundaka', country: 'Spain' },
  { name: 'Bermeo', country: 'Spain' },
  { name: 'Guernica', country: 'Spain' },
  { name: 'Vitoria', country: 'Spain' },
  { name: 'Haro', country: 'Spain' },
  { name: 'Calahorra', country: 'Spain' },
  { name: 'Soria', country: 'Spain' },
  { name: 'El Burgo de Osma', country: 'Spain' },
  { name: 'Aranda de Duero', country: 'Spain' },
  { name: 'Pedraza', country: 'Spain' },
  { name: 'La Granja de San Ildefonso', country: 'Spain' },
  { name: 'Aranjuez', country: 'Spain' },
  { name: 'Chinchón', country: 'Spain' },
  { name: 'Alcalá de Guadaira', country: 'Spain' },
  { name: 'Carmona', country: 'Spain' },
  { name: 'Écija', country: 'Spain' },
  { name: 'Osuna', country: 'Spain' },
  { name: 'Antequera', country: 'Spain' },
  { name: 'Frigiliana', country: 'Spain' },
  { name: 'Comares', country: 'Spain' },
  { name: 'Ronda la Vieja', country: 'Spain' },
  { name: 'Grazalema', country: 'Spain' },
  { name: 'Arcos de la Frontera', country: 'Spain' },
  { name: 'Zahara de la Sierra', country: 'Spain' },
  { name: 'Setenil de las Bodegas', country: 'Spain' },
  { name: 'Olvera', country: 'Spain' },
  { name: 'Úbeda', country: 'Spain' },
  { name: 'Baeza', country: 'Spain' },
  { name: 'Cazorla', country: 'Spain' },
  { name: 'La Alpujarra', country: 'Spain' },
  { name: 'Lanjarón', country: 'Spain' },
  { name: 'Pampaneira', country: 'Spain' },
  { name: 'Bubión', country: 'Spain' },
  { name: 'Capileira', country: 'Spain' },
  { name: 'Trevélez', country: 'Spain' },
  { name: 'Sierra Nevada', country: 'Spain' },
  { name: 'Guadix', country: 'Spain' },
  { name: 'Baza', country: 'Spain' },
  { name: 'Almuñécar', country: 'Spain' },
  { name: 'Salobreña', country: 'Spain' },
  { name: 'Ibiza', country: 'Spain' },
  { name: 'Majorca', country: 'Spain' },
  { name: 'Menorca', country: 'Spain' },
  { name: 'Formentera', country: 'Spain' },
  { name: 'Tenerife', country: 'Spain' },
  { name: 'Gran Canaria', country: 'Spain' },
  { name: 'Lanzarote', country: 'Spain' },
  { name: 'La Palma', country: 'Spain' },
  { name: 'Fuerteventura', country: 'Spain' },
  { name: 'La Gomera', country: 'Spain' },
  { name: 'El Hierro', country: 'Spain' },
  { name: 'Maspalomas', country: 'Spain' },
  { name: 'Puerto de la Cruz', country: 'Spain' },
  { name: 'Los Cristianos', country: 'Spain' },
  { name: 'Playa de las Américas', country: 'Spain' },
  { name: 'Costa Adeje', country: 'Spain' },
  { name: 'Puerto del Carmen', country: 'Spain' },
  { name: 'Playa Blanca', country: 'Spain' },
  { name: 'Corralejo', country: 'Spain' },
  { name: 'Morro Jable', country: 'Spain' },
  { name: 'San Antonio', country: 'Spain' },
  { name: 'San José (Ibiza)', country: 'Spain' },
  { name: 'Santa Eulalia', country: 'Spain' },
  { name: 'Portinatx', country: 'Spain' },
  { name: 'Es Canar', country: 'Spain' },
  { name: 'Cala d\'Or', country: 'Spain' },
  { name: 'Alcúdia', country: 'Spain' },
  { name: 'Pollença', country: 'Spain' },
  { name: 'Sóller', country: 'Spain' },
  { name: 'Valldemossa', country: 'Spain' },
  { name: 'Deià', country: 'Spain' },
  { name: 'Fornalutx', country: 'Spain' },
  { name: 'Artà', country: 'Spain' },
  { name: 'Capdepera', country: 'Spain' },
  { name: 'Cala Ratjada', country: 'Spain' },
  { name: 'Porto Cristo', country: 'Spain' },
  { name: 'Manacor', country: 'Spain' },
  { name: 'Felanitx', country: 'Spain' },
  { name: 'Santanyí', country: 'Spain' },
  { name: 'Ses Salines', country: 'Spain' },
  { name: 'Colònia de Sant Jordi', country: 'Spain' },
  { name: 'Mahón', country: 'Spain' },
  { name: 'Ciutadella', country: 'Spain' },
  { name: 'Fornells', country: 'Spain' },
  { name: 'Cala Galdana', country: 'Spain' },
  { name: 'Son Bou', country: 'Spain' },
  { name: 'Binibeca', country: 'Spain' },
  { name: 'Es Castell', country: 'Spain' },
  { name: 'Basque Country', country: 'Spain' },
  { name: 'Costa Brava', country: 'Spain' },
  { name: 'Costa del Sol', country: 'Spain' },
  { name: 'Costa Blanca', country: 'Spain' },
  { name: 'Costa de la Luz', country: 'Spain' },
  { name: 'Costa Dorada', country: 'Spain' },
  { name: 'Costa Verde', country: 'Spain' },
  { name: 'Rías Baixas', country: 'Spain' },
  { name: 'Canary Islands', country: 'Spain' },
  { name: 'Balearic Islands', country: 'Spain' },
  { name: 'Camino de Santiago', country: 'Spain' },
  { name: 'Alhambra', country: 'Spain' },
  { name: 'Park Güell', country: 'Spain' },
  { name: 'Sagrada Familia', country: 'Spain' },
  { name: 'Montserrat', country: 'Spain' },
  { name: 'El Escorial', country: 'Spain' },
  { name: 'Guadalupe', country: 'Spain' },

  // Portugal - Comprehensive Coverage
  { name: 'Lisbon', country: 'Portugal' },
  { name: 'Porto', country: 'Portugal' },
  { name: 'Faro', country: 'Portugal' },
  { name: 'Braga', country: 'Portugal' },
  { name: 'Coimbra', country: 'Portugal' },
  { name: 'Funchal', country: 'Portugal' },
  { name: 'Aveiro', country: 'Portugal' },
  { name: 'Setúbal', country: 'Portugal' },
  { name: 'Évora', country: 'Portugal' },
  { name: 'Guimarães', country: 'Portugal' },
  { name: 'Cascais', country: 'Portugal' },
  { name: 'Sintra', country: 'Portugal' },
  { name: 'Óbidos', country: 'Portugal' },
  { name: 'Lagos', country: 'Portugal' },
  { name: 'Albufeira', country: 'Portugal' },
  { name: 'Portimão', country: 'Portugal' },
  { name: 'Tavira', country: 'Portugal' },
  { name: 'Vila Nova de Gaia', country: 'Portugal' },
  { name: 'Algarve', country: 'Portugal' },
  { name: 'Madeira', country: 'Portugal' },
  { name: 'Azores', country: 'Portugal' },
  { name: 'Douro Valley', country: 'Portugal' },
  { name: 'Ponta Delgada', country: 'Portugal' },
  { name: 'Angra do Heroísmo', country: 'Portugal' },
  { name: 'Viana do Castelo', country: 'Portugal' },
  { name: 'Viseu', country: 'Portugal' },
  { name: 'Leiria', country: 'Portugal' },
  { name: 'Batalha', country: 'Portugal' },
  { name: 'Tomar', country: 'Portugal' },
  { name: 'Monsanto', country: 'Portugal' },
  { name: 'Nazaré', country: 'Portugal' },
  { name: 'Peniche', country: 'Portugal' },
  { name: 'Ericeira', country: 'Portugal' },
  { name: 'Mafra', country: 'Portugal' },
  { name: 'Estoril', country: 'Portugal' },
  { name: 'Costa da Caparica', country: 'Portugal' },
  { name: 'Sesimbra', country: 'Portugal' },
  { name: 'Arrábida', country: 'Portugal' },
  { name: 'Palmela', country: 'Portugal' },
  { name: 'Santarém', country: 'Portugal' },
  { name: 'Fátima', country: 'Portugal' },
  { name: 'Alcobaça', country: 'Portugal' },
  { name: 'Caldas da Rainha', country: 'Portugal' },
  { name: 'Penacova', country: 'Portugal' },
  { name: 'Conimbriga', country: 'Portugal' },
  { name: 'Buçaco', country: 'Portugal' },
  { name: 'Aveiro Ria', country: 'Portugal' },
  { name: 'Costa Nova', country: 'Portugal' },
  { name: 'Ílhavo', country: 'Portugal' },
  { name: 'Ovar', country: 'Portugal' },
  { name: 'Espinho', country: 'Portugal' },
  { name: 'Matosinhos', country: 'Portugal' },
  { name: 'Póvoa de Varzim', country: 'Portugal' },
  { name: 'Vila do Conde', country: 'Portugal' },
  { name: 'Barcelos', country: 'Portugal' },
  { name: 'Ponte de Lima', country: 'Portugal' },
  { name: 'Valença', country: 'Portugal' },
  { name: 'Caminha', country: 'Portugal' },
  { name: 'Gerês', country: 'Portugal' },
  { name: 'Peneda-Gerês National Park', country: 'Portugal' },
  { name: 'Vila Real', country: 'Portugal' },
  { name: 'Lamego', country: 'Portugal' },
  { name: 'Peso da Régua', country: 'Portugal' },
  { name: 'Pinhão', country: 'Portugal' },
  { name: 'Amarante', country: 'Portugal' },
  { name: 'Bragança', country: 'Portugal' },
  { name: 'Miranda do Douro', country: 'Portugal' },
  { name: 'Chaves', country: 'Portugal' },
  { name: 'Guarda', country: 'Portugal' },
  { name: 'Castelo Branco', country: 'Portugal' },
  { name: 'Monsanto (Beira)', country: 'Portugal' },
  { name: 'Sortelha', country: 'Portugal' },
  { name: 'Marvão', country: 'Portugal' },
  { name: 'Castelo de Vide', country: 'Portugal' },
  { name: 'Elvas', country: 'Portugal' },
  { name: 'Portalegre', country: 'Portugal' },
  { name: 'Estremoz', country: 'Portugal' },
  { name: 'Monsaraz', country: 'Portugal' },
  { name: 'Beja', country: 'Portugal' },
  { name: 'Moura', country: 'Portugal' },
  { name: 'Mértola', country: 'Portugal' },
  { name: 'Castro Marim', country: 'Portugal' },
  { name: 'Vila Real de Santo António', country: 'Portugal' },
  { name: 'Monte Gordo', country: 'Portugal' },
  { name: 'Cabanas de Tavira', country: 'Portugal' },
  { name: 'Fuzeta', country: 'Portugal' },
  { name: 'Olhão', country: 'Portugal' },
  { name: 'Fuseta', country: 'Portugal' },
  { name: 'Ilha da Culatra', country: 'Portugal' },
  { name: 'Ilha da Armona', country: 'Portugal' },
  { name: 'Ilha de Faro', country: 'Portugal' },
  { name: 'Quinta do Lago', country: 'Portugal' },
  { name: 'Vale do Lobo', country: 'Portugal' },
  { name: 'Vilamoura', country: 'Portugal' },
  { name: 'Quarteira', country: 'Portugal' },
  { name: 'Loulé', country: 'Portugal' },
  { name: 'Almancil', country: 'Portugal' },
  { name: 'Boliqueime', country: 'Portugal' },
  { name: 'Alte', country: 'Portugal' },
  { name: 'Silves', country: 'Portugal' },
  { name: 'Carvoeiro', country: 'Portugal' },
  { name: 'Ferragudo', country: 'Portugal' },
  { name: 'Alvor', country: 'Portugal' },
  { name: 'Praia da Rocha', country: 'Portugal' },
  { name: 'Monchique', country: 'Portugal' },
  { name: 'Caldas de Monchique', country: 'Portugal' },
  { name: 'Aljezur', country: 'Portugal' },
  { name: 'Arrifana', country: 'Portugal' },
  { name: 'Odeceixe', country: 'Portugal' },
  { name: 'Sagres', country: 'Portugal' },
  { name: 'Cabo de São Vicente', country: 'Portugal' },
  { name: 'Vila do Bispo', country: 'Portugal' },
  { name: 'Burgau', country: 'Portugal' },
  { name: 'Luz', country: 'Portugal' },
  { name: 'Ponta da Piedade', country: 'Portugal' },
  { name: 'Meia Praia', country: 'Portugal' },
  { name: 'Alvor Lagoon', country: 'Portugal' },
  { name: 'Ria Formosa', country: 'Portugal' },
  { name: 'Benagil Caves', country: 'Portugal' },
  { name: 'Ponta de Sagres', country: 'Portugal' },
  
  // Madeira Islands
  { name: 'Funchal (Madeira)', country: 'Portugal' },
  { name: 'Porto Santo', country: 'Portugal' },
  { name: 'Santana', country: 'Portugal' },
  { name: 'Machico', country: 'Portugal' },
  { name: 'Câmara de Lobos', country: 'Portugal' },
  { name: 'Caniço', country: 'Portugal' },
  { name: 'Porto Moniz', country: 'Portugal' },
  { name: 'Ribeira Brava', country: 'Portugal' },
  { name: 'Calheta', country: 'Portugal' },
  { name: 'Ponta do Sol', country: 'Portugal' },
  { name: 'São Vicente', country: 'Portugal' },
  { name: 'Pico do Arieiro', country: 'Portugal' },
  { name: 'Pico Ruivo', country: 'Portugal' },
  { name: 'Cabo Girão', country: 'Portugal' },
  
  // Azores Islands
  { name: 'São Miguel', country: 'Portugal' },
  { name: 'Terceira', country: 'Portugal' },
  { name: 'Faial', country: 'Portugal' },
  { name: 'Pico Island', country: 'Portugal' },
  { name: 'São Jorge', country: 'Portugal' },
  { name: 'Graciosa', country: 'Portugal' },
  { name: 'Flores', country: 'Portugal' },
  { name: 'Corvo', country: 'Portugal' },
  { name: 'Santa Maria', country: 'Portugal' },
  { name: 'Horta', country: 'Portugal' },
  { name: 'Madalena', country: 'Portugal' },
  { name: 'Velas', country: 'Portugal' },
  { name: 'Santa Cruz das Flores', country: 'Portugal' },
  { name: 'Vila do Corvo', country: 'Portugal' },
  { name: 'Vila Franca do Campo', country: 'Portugal' },
  { name: 'Furnas', country: 'Portugal' },
  { name: 'Sete Cidades', country: 'Portugal' },
  { name: 'Lagoa do Fogo', country: 'Portugal' },
  { name: 'Nordeste', country: 'Portugal' },
  { name: 'Ribeira Grande', country: 'Portugal' },
  { name: 'Mosteiros', country: 'Portugal' },
  { name: 'Praia da Vitória', country: 'Portugal' },
  { name: 'Biscoitos', country: 'Portugal' },
  { name: 'Algar do Carvão', country: 'Portugal' },

  // SCANDINAVIA & NORDIC COUNTRIES
  // Norway - Comprehensive Coverage
  { name: 'Oslo', country: 'Norway' },
  { name: 'Bergen', country: 'Norway' },
  { name: 'Stavanger', country: 'Norway' },
  { name: 'Trondheim', country: 'Norway' },
  { name: 'Tromsø', country: 'Norway' },
  { name: 'Drammen', country: 'Norway' },
  { name: 'Fredrikstad', country: 'Norway' },
  { name: 'Kristiansand', country: 'Norway' },
  { name: 'Ålesund', country: 'Norway' },
  { name: 'Bodø', country: 'Norway' },
  { name: 'Narvik', country: 'Norway' },
  { name: 'Lillehammer', country: 'Norway' },
  { name: 'Haugesund', country: 'Norway' },
  { name: 'Molde', country: 'Norway' },
  { name: 'Harstad', country: 'Norway' },
  { name: 'Alta', country: 'Norway' },
  { name: 'Lofoten Islands', country: 'Norway' },
  { name: 'Svalbard', country: 'Norway' },
  { name: 'Flåm', country: 'Norway' },
  { name: 'Geirangerfjord', country: 'Norway' },
  { name: 'Preikestolen', country: 'Norway' },
  { name: 'Nordkapp', country: 'Norway' },
  { name: 'Trondheim', country: 'Norway' },
  { name: 'Sognefjord', country: 'Norway' },
  { name: 'Hardangerfjord', country: 'Norway' },
  
  // Sweden - Comprehensive Coverage
  { name: 'Stockholm', country: 'Sweden' },
  { name: 'Gothenburg', country: 'Sweden' },
  { name: 'Malmö', country: 'Sweden' },
  { name: 'Uppsala', country: 'Sweden' },
  { name: 'Linköping', country: 'Sweden' },
  { name: 'Västerås', country: 'Sweden' },
  { name: 'Örebro', country: 'Sweden' },
  { name: 'Norrköping', country: 'Sweden' },
  { name: 'Helsingborg', country: 'Sweden' },
  { name: 'Jönköping', country: 'Sweden' },
  { name: 'Umeå', country: 'Sweden' },
  { name: 'Lund', country: 'Sweden' },
  { name: 'Borås', country: 'Sweden' },
  { name: 'Sundsvall', country: 'Sweden' },
  { name: 'Gävle', country: 'Sweden' },
  { name: 'Karlstad', country: 'Sweden' },
  { name: 'Eskilstuna', country: 'Sweden' },
  { name: 'Kiruna', country: 'Sweden' },
  { name: 'Visby', country: 'Sweden' },
  { name: 'Abisko', country: 'Sweden' },
  { name: 'Gotland', country: 'Sweden' },
  { name: 'Lapland', country: 'Sweden' },
  
  // Finland - Comprehensive Coverage
  { name: 'Helsinki', country: 'Finland' },
  { name: 'Espoo', country: 'Finland' },
  { name: 'Tampere', country: 'Finland' },
  { name: 'Vantaa', country: 'Finland' },
  { name: 'Oulu', country: 'Finland' },
  { name: 'Turku', country: 'Finland' },
  { name: 'Jyväskylä', country: 'Finland' },
  { name: 'Lahti', country: 'Finland' },
  { name: 'Kuopio', country: 'Finland' },
  { name: 'Pori', country: 'Finland' },
  { name: 'Lappeenranta', country: 'Finland' },
  { name: 'Rovaniemi', country: 'Finland' },
  { name: 'Vaasa', country: 'Finland' },
  { name: 'Joensuu', country: 'Finland' },
  { name: 'Seinäjoki', country: 'Finland' },
  { name: 'Savonlinna', country: 'Finland' },
  { name: 'Porvoo', country: 'Finland' },
  { name: 'Åland Islands', country: 'Finland' },
  { name: 'Mariehamn', country: 'Finland' },
  { name: 'Levi', country: 'Finland' },
  { name: 'Saariselkä', country: 'Finland' },
  { name: 'Inari', country: 'Finland' },
  { name: 'Kemi', country: 'Finland' },
  { name: 'Lake District', country: 'Finland' },
  
  // Denmark - Comprehensive Coverage
  { name: 'Copenhagen', country: 'Denmark' },
  { name: 'Aarhus', country: 'Denmark' },
  { name: 'Odense', country: 'Denmark' },
  { name: 'Aalborg', country: 'Denmark' },
  { name: 'Esbjerg', country: 'Denmark' },
  { name: 'Randers', country: 'Denmark' },
  { name: 'Kolding', country: 'Denmark' },
  { name: 'Horsens', country: 'Denmark' },
  { name: 'Vejle', country: 'Denmark' },
  { name: 'Roskilde', country: 'Denmark' },
  { name: 'Herning', country: 'Denmark' },
  { name: 'Helsingør', country: 'Denmark' },
  { name: 'Silkeborg', country: 'Denmark' },
  { name: 'Næstved', country: 'Denmark' },
  { name: 'Fredericia', country: 'Denmark' },
  { name: 'Viborg', country: 'Denmark' },
  { name: 'Køge', country: 'Denmark' },
  { name: 'Skagen', country: 'Denmark' },
  { name: 'Ribe', country: 'Denmark' },
  { name: 'Bornholm', country: 'Denmark' },
  { name: 'Faroe Islands', country: 'Denmark' },
  { name: 'Greenland', country: 'Denmark' },
  
  // Iceland - Comprehensive Coverage
  { name: 'Reykjavik', country: 'Iceland' },
  { name: 'Reykjavík', country: 'Iceland' },
  { name: 'Akureyri', country: 'Iceland' },
  { name: 'Reykjanesbær', country: 'Iceland' },
  { name: 'Kópavogur', country: 'Iceland' },
  { name: 'Hafnarfjörður', country: 'Iceland' },
  { name: 'Ísafjörður', country: 'Iceland' },
  { name: 'Vík', country: 'Iceland' },
  { name: 'Selfoss', country: 'Iceland' },
  { name: 'Egilsstaðir', country: 'Iceland' },
  { name: 'Höfn', country: 'Iceland' },
  { name: 'Blue Lagoon', country: 'Iceland' },
  { name: 'Golden Circle', country: 'Iceland' },
  { name: 'Jökulsárlón', country: 'Iceland' },
  { name: 'Skógafoss', country: 'Iceland' },
  { name: 'Seljalandsfoss', country: 'Iceland' },
  { name: 'Þingvellir', country: 'Iceland' },
  { name: 'Geysir', country: 'Iceland' },
  { name: 'Landmannalaugar', country: 'Iceland' },
  { name: 'Westfjords', country: 'Iceland' },
  
  // CENTRAL & EASTERN EUROPE
  // Austria - Comprehensive Coverage
  { name: 'Vienna', country: 'Austria' },
  { name: 'Salzburg', country: 'Austria' },
  { name: 'Innsbruck', country: 'Austria' },
  { name: 'Graz', country: 'Austria' },
  { name: 'Linz', country: 'Austria' },
  { name: 'Hallstatt', country: 'Austria' },
  { name: 'Zell am See', country: 'Austria' },
  { name: 'St. Anton am Arlberg', country: 'Austria' },
  { name: 'Kitzbühel', country: 'Austria' },
  { name: 'Bad Ischl', country: 'Austria' },
  { name: 'Wachau Valley', country: 'Austria' },
  { name: 'Melk', country: 'Austria' },
  { name: 'Dürnstein', country: 'Austria' },
  { name: 'Krems', country: 'Austria' },
  { name: 'Seefeld', country: 'Austria' },
  { name: 'Mayrhofen', country: 'Austria' },
  { name: 'Klagenfurt', country: 'Austria' },
  { name: 'Bregenz', country: 'Austria' },
  { name: 'Eisenstadt', country: 'Austria' },
  { name: 'St. Wolfgang', country: 'Austria' },
  { name: 'Sölden', country: 'Austria' },
  { name: 'Schladming', country: 'Austria' },
  
  // Poland - Comprehensive Coverage
  { name: 'Warsaw', country: 'Poland' },
  { name: 'Krakow', country: 'Poland' },
  { name: 'Wrocław', country: 'Poland' },
  { name: 'Gdańsk', country: 'Poland' },
  { name: 'Poznań', country: 'Poland' },
  { name: 'Łódź', country: 'Poland' },
  { name: 'Szczecin', country: 'Poland' },
  { name: 'Lublin', country: 'Poland' },
  { name: 'Katowice', country: 'Poland' },
  { name: 'Bydgoszcz', country: 'Poland' },
  { name: 'Białystok', country: 'Poland' },
  { name: 'Gdynia', country: 'Poland' },
  { name: 'Częstochowa', country: 'Poland' },
  { name: 'Radom', country: 'Poland' },
  { name: 'Toruń', country: 'Poland' },
  { name: 'Sopot', country: 'Poland' },
  { name: 'Zakopane', country: 'Poland' },
  { name: 'Auschwitz', country: 'Poland' },
  { name: 'Wieliczka', country: 'Poland' },
  { name: 'Malbork', country: 'Poland' },
  { name: 'Wawel Castle', country: 'Poland' },
  { name: 'Tatra Mountains', country: 'Poland' },
  { name: 'Mazury', country: 'Poland' },
  
  // Czech Republic - Comprehensive Coverage
  { name: 'Prague', country: 'Czech Republic' },
  { name: 'Brno', country: 'Czech Republic' },
  { name: 'Ostrava', country: 'Czech Republic' },
  { name: 'Plzeň', country: 'Czech Republic' },
  { name: 'Liberec', country: 'Czech Republic' },
  { name: 'Olomouc', country: 'Czech Republic' },
  { name: 'České Budějovice', country: 'Czech Republic' },
  { name: 'Hradec Králové', country: 'Czech Republic' },
  { name: 'Karlovy Vary', country: 'Czech Republic' },
  { name: 'Český Krumlov', country: 'Czech Republic' },
  { name: 'Kutná Hora', country: 'Czech Republic' },
  { name: 'Telč', country: 'Czech Republic' },
  { name: 'Litomyšl', country: 'Czech Republic' },
  { name: 'Lednice', country: 'Czech Republic' },
  { name: 'Třebíč', country: 'Czech Republic' },
  { name: 'Kroměříž', country: 'Czech Republic' },
  { name: 'Mariánské Lázně', country: 'Czech Republic' },
  { name: 'Špindlerův Mlýn', country: 'Czech Republic' },
  { name: 'Bohemian Switzerland', country: 'Czech Republic' },
  
  // Slovakia - Comprehensive Coverage
  { name: 'Bratislava', country: 'Slovakia' },
  { name: 'Košice', country: 'Slovakia' },
  { name: 'Prešov', country: 'Slovakia' },
  { name: 'Žilina', country: 'Slovakia' },
  { name: 'Banská Bystrica', country: 'Slovakia' },
  { name: 'Nitra', country: 'Slovakia' },
  { name: 'Trnava', country: 'Slovakia' },
  { name: 'Trenčín', country: 'Slovakia' },
  { name: 'Martin', country: 'Slovakia' },
  { name: 'Poprad', country: 'Slovakia' },
  { name: 'Tatry', country: 'Slovakia' },
  { name: 'High Tatras', country: 'Slovakia' },
  { name: 'Liptovský Mikuláš', country: 'Slovakia' },
  { name: 'Špania Dolina', country: 'Slovakia' },
  { name: 'Bardejov', country: 'Slovakia' },
  { name: 'Levoča', country: 'Slovakia' },
  { name: 'Spišský hrad', country: 'Slovakia' },
  { name: 'Orava Castle', country: 'Slovakia' },
  
  // Slovenia - Comprehensive Coverage
  { name: 'Ljubljana', country: 'Slovenia' },
  { name: 'Maribor', country: 'Slovenia' },
  { name: 'Celje', country: 'Slovenia' },
  { name: 'Kranj', country: 'Slovenia' },
  { name: 'Koper', country: 'Slovenia' },
  { name: 'Bled', country: 'Slovenia' },
  { name: 'Bohinj', country: 'Slovenia' },
  { name: 'Piran', country: 'Slovenia' },
  { name: 'Ptuj', country: 'Slovenia' },
  { name: 'Postojna', country: 'Slovenia' },
  { name: 'Škocjan Caves', country: 'Slovenia' },
  { name: 'Predjama Castle', country: 'Slovenia' },
  { name: 'Logarska Dolina', country: 'Slovenia' },
  { name: 'Soča Valley', country: 'Slovenia' },
  { name: 'Triglav National Park', country: 'Slovenia' },
  { name: 'Kranjska Gora', country: 'Slovenia' },
  
  // Croatia - Comprehensive Coverage
  { name: 'Zagreb', country: 'Croatia' },
  { name: 'Split', country: 'Croatia' },
  { name: 'Dubrovnik', country: 'Croatia' },
  { name: 'Rijeka', country: 'Croatia' },
  { name: 'Zadar', country: 'Croatia' },
  { name: 'Pula', country: 'Croatia' },
  { name: 'Osijek', country: 'Croatia' },
  { name: 'Šibenik', country: 'Croatia' },
  { name: 'Slavonski Brod', country: 'Croatia' },
  { name: 'Karlovac', country: 'Croatia' },
  { name: 'Varaždin', country: 'Croatia' },
  { name: 'Rovinj', country: 'Croatia' },
  { name: 'Poreč', country: 'Croatia' },
  { name: 'Hvar', country: 'Croatia' },
  { name: 'Korčula', country: 'Croatia' },
  { name: 'Brač', country: 'Croatia' },
  { name: 'Vis', country: 'Croatia' },
  { name: 'Trogir', country: 'Croatia' },
  { name: 'Plitvice Lakes', country: 'Croatia' },
  { name: 'Krka National Park', country: 'Croatia' },
  { name: 'Istria', country: 'Croatia' },
  { name: 'Makarska', country: 'Croatia' },
  { name: 'Bol', country: 'Croatia' },
  
  // Hungary - Comprehensive Coverage
  { name: 'Budapest', country: 'Hungary' },
  { name: 'Debrecen', country: 'Hungary' },
  { name: 'Szeged', country: 'Hungary' },
  { name: 'Miskolc', country: 'Hungary' },
  { name: 'Pécs', country: 'Hungary' },
  { name: 'Győr', country: 'Hungary' },
  { name: 'Nyíregyháza', country: 'Hungary' },
  { name: 'Kecskemét', country: 'Hungary' },
  { name: 'Székesfehérvár', country: 'Hungary' },
  { name: 'Eger', country: 'Hungary' },
  { name: 'Sopron', country: 'Hungary' },
  { name: 'Lake Balaton', country: 'Hungary' },
  { name: 'Visegrád', country: 'Hungary' },
  { name: 'Szentendre', country: 'Hungary' },
  { name: 'Esztergom', country: 'Hungary' },
  { name: 'Tokaj', country: 'Hungary' },
  { name: 'Hortobágy', country: 'Hungary' },
  { name: 'Aggtelek', country: 'Hungary' },
  
  // Romania - Comprehensive Coverage
  { name: 'Bucharest', country: 'Romania' },
  { name: 'Cluj-Napoca', country: 'Romania' },
  { name: 'Timișoara', country: 'Romania' },
  { name: 'Iași', country: 'Romania' },
  { name: 'Constanța', country: 'Romania' },
  { name: 'Craiova', country: 'Romania' },
  { name: 'Brașov', country: 'Romania' },
  { name: 'Galați', country: 'Romania' },
  { name: 'Ploiești', country: 'Romania' },
  { name: 'Oradea', country: 'Romania' },
  { name: 'Brăila', country: 'Romania' },
  { name: 'Arad', country: 'Romania' },
  { name: 'Pitești', country: 'Romania' },
  { name: 'Sibiu', country: 'Romania' },
  { name: 'Târgu Mureș', country: 'Romania' },
  { name: 'Baia Mare', country: 'Romania' },
  { name: 'Bran', country: 'Romania' },
  { name: 'Sighișoara', country: 'Romania' },
  { name: 'Sinaia', country: 'Romania' },
  { name: 'Peleș Castle', country: 'Romania' },
  { name: 'Transfăgărășan', country: 'Romania' },
  { name: 'Danube Delta', country: 'Romania' },
  { name: 'Transylvania', country: 'Romania' },
  
  // Serbia - Comprehensive Coverage
  { name: 'Belgrade', country: 'Serbia' },
  { name: 'Novi Sad', country: 'Serbia' },
  { name: 'Niš', country: 'Serbia' },
  { name: 'Kragujevac', country: 'Serbia' },
  { name: 'Subotica', country: 'Serbia' },
  { name: 'Leskovac', country: 'Serbia' },
  { name: 'Pančevo', country: 'Serbia' },
  { name: 'Čačak', country: 'Serbia' },
  { name: 'Novi Pazar', country: 'Serbia' },
  { name: 'Smederevo', country: 'Serbia' },
  { name: 'Vranje', country: 'Serbia' },
  { name: 'Zlatibor', country: 'Serbia' },
  { name: 'Kopaonik', country: 'Serbia' },
  { name: 'Đavolja Varoš', country: 'Serbia' },
  { name: 'Studenica Monastery', country: 'Serbia' },
  
  // Bulgaria - Comprehensive Coverage
  { name: 'Sofia', country: 'Bulgaria' },
  { name: 'Plovdiv', country: 'Bulgaria' },
  { name: 'Varna', country: 'Bulgaria' },
  { name: 'Burgas', country: 'Bulgaria' },
  { name: 'Ruse', country: 'Bulgaria' },
  { name: 'Stara Zagora', country: 'Bulgaria' },
  { name: 'Pleven', country: 'Bulgaria' },
  { name: 'Sliven', country: 'Bulgaria' },
  { name: 'Dobrich', country: 'Bulgaria' },
  { name: 'Shumen', country: 'Bulgaria' },
  { name: 'Pernik', country: 'Bulgaria' },
  { name: 'Veliko Tarnovo', country: 'Bulgaria' },
  { name: 'Bansko', country: 'Bulgaria' },
  { name: 'Sozopol', country: 'Bulgaria' },
  { name: 'Nessebar', country: 'Bulgaria' },
  { name: 'Sunny Beach', country: 'Bulgaria' },
  { name: 'Golden Sands', country: 'Bulgaria' },
  { name: 'Rila Monastery', country: 'Bulgaria' },
  { name: 'Belogradchik', country: 'Bulgaria' },
  
  // Albania - Comprehensive Coverage
  { name: 'Tirana', country: 'Albania' },
  { name: 'Durrës', country: 'Albania' },
  { name: 'Vlorë', country: 'Albania' },
  { name: 'Elbasan', country: 'Albania' },
  { name: 'Shkodër', country: 'Albania' },
  { name: 'Fier', country: 'Albania' },
  { name: 'Korçë', country: 'Albania' },
  { name: 'Berat', country: 'Albania' },
  { name: 'Gjirokastër', country: 'Albania' },
  { name: 'Sarandë', country: 'Albania' },
  { name: 'Ksamil', country: 'Albania' },
  { name: 'Theth', country: 'Albania' },
  { name: 'Valbona', country: 'Albania' },
  { name: 'Butrint', country: 'Albania' },
  { name: 'Albanian Riviera', country: 'Albania' },
  
  // Moldova - Comprehensive Coverage
  { name: 'Chișinău', country: 'Moldova' },
  { name: 'Tiraspol', country: 'Moldova' },
  { name: 'Bălți', country: 'Moldova' },
  { name: 'Bender', country: 'Moldova' },
  { name: 'Rîbnița', country: 'Moldova' },
  { name: 'Cahul', country: 'Moldova' },
  { name: 'Ungheni', country: 'Moldova' },
  { name: 'Soroca', country: 'Moldova' },
  { name: 'Orhei', country: 'Moldova' },
  { name: 'Comrat', country: 'Moldova' },
  { name: 'Orheiul Vechi', country: 'Moldova' },
  
  // Baltic States
  // Lithuania - Comprehensive Coverage
  { name: 'Vilnius', country: 'Lithuania' },
  { name: 'Kaunas', country: 'Lithuania' },
  { name: 'Klaipėda', country: 'Lithuania' },
  { name: 'Šiauliai', country: 'Lithuania' },
  { name: 'Panevėžys', country: 'Lithuania' },
  { name: 'Alytus', country: 'Lithuania' },
  { name: 'Marijampolė', country: 'Lithuania' },
  { name: 'Mažeikiai', country: 'Lithuania' },
  { name: 'Jonava', country: 'Lithuania' },
  { name: 'Utena', country: 'Lithuania' },
  { name: 'Trakai', country: 'Lithuania' },
  { name: 'Nida', country: 'Lithuania' },
  { name: 'Palanga', country: 'Lithuania' },
  { name: 'Curonian Spit', country: 'Lithuania' },
  { name: 'Hill of Crosses', country: 'Lithuania' },
  
  // Latvia - Comprehensive Coverage
  { name: 'Riga', country: 'Latvia' },
  { name: 'Daugavpils', country: 'Latvia' },
  { name: 'Liepāja', country: 'Latvia' },
  { name: 'Jelgava', country: 'Latvia' },
  { name: 'Jūrmala', country: 'Latvia' },
  { name: 'Ventspils', country: 'Latvia' },
  { name: 'Rēzekne', country: 'Latvia' },
  { name: 'Valmiera', country: 'Latvia' },
  { name: 'Jēkabpils', country: 'Latvia' },
  { name: 'Ogre', country: 'Latvia' },
  { name: 'Sigulda', country: 'Latvia' },
  { name: 'Cēsis', country: 'Latvia' },
  { name: 'Kuldīga', country: 'Latvia' },
  { name: 'Gauja National Park', country: 'Latvia' },
  
  // Estonia - Comprehensive Coverage
  { name: 'Tallinn', country: 'Estonia' },
  { name: 'Tartu', country: 'Estonia' },
  { name: 'Narva', country: 'Estonia' },
  { name: 'Pärnu', country: 'Estonia' },
  { name: 'Kohtla-Järve', country: 'Estonia' },
  { name: 'Viljandi', country: 'Estonia' },
  { name: 'Rakvere', country: 'Estonia' },
  { name: 'Kuressaare', country: 'Estonia' },
  { name: 'Võru', country: 'Estonia' },
  { name: 'Haapsalu', country: 'Estonia' },
  { name: 'Saaremaa', country: 'Estonia' },
  { name: 'Hiiumaa', country: 'Estonia' },
  { name: 'Lahemaa National Park', country: 'Estonia' },
  
  // Belarus - Comprehensive Coverage
  { name: 'Minsk', country: 'Belarus' },
  { name: 'Gomel', country: 'Belarus' },
  { name: 'Mogilev', country: 'Belarus' },
  { name: 'Vitebsk', country: 'Belarus' },
  { name: 'Grodno', country: 'Belarus' },
  { name: 'Brest', country: 'Belarus' },
  { name: 'Bobruisk', country: 'Belarus' },
  { name: 'Baranovichi', country: 'Belarus' },
  { name: 'Borisov', country: 'Belarus' },
  { name: 'Pinsk', country: 'Belarus' },
  { name: 'Mir Castle', country: 'Belarus' },
  { name: 'Nesvizh Castle', country: 'Belarus' },
  { name: 'Belovezhskaya Pushcha', country: 'Belarus' },

  // NORTH AMERICA
  { name: 'New York', country: 'United States' },
  { name: 'Los Angeles', country: 'United States' },
  { name: 'Chicago', country: 'United States' },
  { name: 'Houston', country: 'United States' },
  { name: 'Phoenix', country: 'United States' },
  { name: 'Philadelphia', country: 'United States' },
  { name: 'San Antonio', country: 'United States' },
  { name: 'San Diego', country: 'United States' },
  { name: 'Dallas', country: 'United States' },
  { name: 'San Jose', country: 'United States' },
  { name: 'Austin', country: 'United States' },
  { name: 'Jacksonville', country: 'United States' },
  { name: 'Fort Worth', country: 'United States' },
  { name: 'Columbus', country: 'United States' },
  { name: 'Charlotte', country: 'United States' },
  { name: 'San Francisco', country: 'United States' },
  { name: 'Indianapolis', country: 'United States' },
  { name: 'Seattle', country: 'United States' },
  { name: 'Denver', country: 'United States' },
  { name: 'Washington DC', country: 'United States' },
  { name: 'Boston', country: 'United States' },
  { name: 'El Paso', country: 'United States' },
  { name: 'Nashville', country: 'United States' },
  { name: 'Detroit', country: 'United States' },
  { name: 'Oklahoma City', country: 'United States' },
  { name: 'Portland', country: 'United States' },
  { name: 'Las Vegas', country: 'United States' },
  { name: 'Memphis', country: 'United States' },
  { name: 'Louisville', country: 'United States' },
  { name: 'Baltimore', country: 'United States' },
  { name: 'Milwaukee', country: 'United States' },
  { name: 'Albuquerque', country: 'United States' },
  { name: 'Tucson', country: 'United States' },
  { name: 'Fresno', country: 'United States' },
  { name: 'Sacramento', country: 'United States' },
  { name: 'Mesa', country: 'United States' },
  { name: 'Kansas City', country: 'United States' },
  { name: 'Atlanta', country: 'United States' },
  { name: 'Long Beach', country: 'United States' },
  { name: 'Colorado Springs', country: 'United States' },
  { name: 'Raleigh', country: 'United States' },
  { name: 'Miami', country: 'United States' },
  { name: 'Virginia Beach', country: 'United States' },
  { name: 'Omaha', country: 'United States' },
  { name: 'Oakland', country: 'United States' },
  { name: 'Minneapolis', country: 'United States' },
  { name: 'Tulsa', country: 'United States' },
  { name: 'Arlington', country: 'United States' },
  { name: 'New Orleans', country: 'United States' },
  { name: 'Wichita', country: 'United States' },
  { name: 'Cleveland', country: 'United States' },
  { name: 'Tampa', country: 'United States' },
  { name: 'Bakersfield', country: 'United States' },
  { name: 'Aurora', country: 'United States' },
  { name: 'Honolulu', country: 'United States' },
  { name: 'Anaheim', country: 'United States' },
  { name: 'Santa Ana', country: 'United States' },
  { name: 'Corpus Christi', country: 'United States' },
  { name: 'Riverside', country: 'United States' },
  { name: 'Lexington', country: 'United States' },
  { name: 'Stockton', country: 'United States' },
  { name: 'Henderson', country: 'United States' },
  { name: 'Saint Paul', country: 'United States' },
  { name: 'St. Louis', country: 'United States' },
  { name: 'Cincinnati', country: 'United States' },
  { name: 'Pittsburgh', country: 'United States' },
  { name: 'Greensboro', country: 'United States' },
  { name: 'Anchorage', country: 'United States' },
  { name: 'Plano', country: 'United States' },
  { name: 'Lincoln', country: 'United States' },
  { name: 'Orlando', country: 'United States' },
  { name: 'Irvine', country: 'United States' },
  { name: 'Newark', country: 'United States' },
  { name: 'Durham', country: 'United States' },
  { name: 'Chula Vista', country: 'United States' },
  { name: 'Toledo', country: 'United States' },
  { name: 'Fort Wayne', country: 'United States' },
  { name: 'St. Petersburg', country: 'United States' },
  { name: 'Laredo', country: 'United States' },
  { name: 'Jersey City', country: 'United States' },
  { name: 'Chandler', country: 'United States' },
  { name: 'Madison', country: 'United States' },
  { name: 'Lubbock', country: 'United States' },
  { name: 'Norfolk', country: 'United States' },
  { name: 'Baton Rouge', country: 'United States' },
  { name: 'Buffalo', country: 'United States' },
  { name: 'North Las Vegas', country: 'United States' },
  { name: 'Gilbert', country: 'United States' },
  { name: 'Glendale', country: 'United States' },
  { name: 'Scottsdale', country: 'United States' },
  { name: 'Garland', country: 'United States' },
  { name: 'Irving', country: 'United States' },
  { name: 'Chesapeake', country: 'United States' },
  { name: 'Fremont', country: 'United States' },
  { name: 'Boise', country: 'United States' },
  { name: 'Richmond', country: 'United States' },
  { name: 'San Bernardino', country: 'United States' },
  { name: 'Birmingham', country: 'United States' },
  { name: 'Spokane', country: 'United States' },
  { name: 'Rochester', country: 'United States' },
  { name: 'Des Moines', country: 'United States' },
  { name: 'Modesto', country: 'United States' },
  { name: 'Fayetteville', country: 'United States' },
  { name: 'Tacoma', country: 'United States' },
  { name: 'Oxnard', country: 'United States' },
  { name: 'Fontana', country: 'United States' },
  { name: 'Columbus', country: 'United States' },
  { name: 'Montgomery', country: 'United States' },
  { name: 'Moreno Valley', country: 'United States' },
  { name: 'Shreveport', country: 'United States' },
  { name: 'Aurora', country: 'United States' },
  { name: 'Yonkers', country: 'United States' },
  { name: 'Akron', country: 'United States' },
  { name: 'Huntington Beach', country: 'United States' },
  { name: 'Little Rock', country: 'United States' },
  { name: 'Augusta', country: 'United States' },
  { name: 'Amarillo', country: 'United States' },
  { name: 'Glendale', country: 'United States' },
  { name: 'Mobile', country: 'United States' },
  { name: 'Grand Rapids', country: 'United States' },
  { name: 'Salt Lake City', country: 'United States' },
  { name: 'Tallahassee', country: 'United States' },
  { name: 'Huntsville', country: 'United States' },
  { name: 'Grand Prairie', country: 'United States' },
  { name: 'Knoxville', country: 'United States' },
  { name: 'Worcester', country: 'United States' },
  { name: 'Newport News', country: 'United States' },
  { name: 'Brownsville', country: 'United States' },
  { name: 'Overland Park', country: 'United States' },
  { name: 'Santa Clarita', country: 'United States' },
  { name: 'Providence', country: 'United States' },
  { name: 'Garden Grove', country: 'United States' },
  { name: 'Chattanooga', country: 'United States' },
  { name: 'Oceanside', country: 'United States' },
  { name: 'Jackson', country: 'United States' },
  { name: 'Fort Lauderdale', country: 'United States' },
  { name: 'Santa Rosa', country: 'United States' },
  { name: 'Rancho Cucamonga', country: 'United States' },
  { name: 'Port St. Lucie', country: 'United States' },
  { name: 'Tempe', country: 'United States' },
  { name: 'Ontario', country: 'United States' },
  { name: 'Vancouver', country: 'United States' },
  { name: 'Cape Coral', country: 'United States' },
  { name: 'Sioux Falls', country: 'United States' },
  { name: 'Springfield', country: 'United States' },
  { name: 'Peoria', country: 'United States' },
  { name: 'Pembroke Pines', country: 'United States' },
  { name: 'Elk Grove', country: 'United States' },
  { name: 'Salem', country: 'United States' },
  { name: 'Lancaster', country: 'United States' },
  { name: 'Corona', country: 'United States' },
  { name: 'Eugene', country: 'United States' },
  { name: 'Palmdale', country: 'United States' },
  { name: 'Salinas', country: 'United States' },
  { name: 'Springfield', country: 'United States' },
  { name: 'Pasadena', country: 'United States' },
  { name: 'Fort Collins', country: 'United States' },
  { name: 'Hayward', country: 'United States' },
  { name: 'Pomona', country: 'United States' },
  { name: 'Cary', country: 'United States' },
  { name: 'Rockford', country: 'United States' },
  { name: 'Alexandria', country: 'United States' },
  { name: 'Escondido', country: 'United States' },
  { name: 'McKinney', country: 'United States' },
  { name: 'Kansas City', country: 'United States' },
  { name: 'Joliet', country: 'United States' },
  { name: 'Sunnyvale', country: 'United States' },
  { name: 'Torrance', country: 'United States' },
  { name: 'Bridgeport', country: 'United States' },
  { name: 'Lakewood', country: 'United States' },
  { name: 'Hollywood', country: 'United States' },
  { name: 'Paterson', country: 'United States' },
  { name: 'Naperville', country: 'United States' },
  { name: 'Syracuse', country: 'United States' },
  { name: 'Mesquite', country: 'United States' },
  { name: 'Dayton', country: 'United States' },
  { name: 'Savannah', country: 'United States' },
  { name: 'Clarksville', country: 'United States' },
  { name: 'Orange', country: 'United States' },
  { name: 'Pasadena', country: 'United States' },
  { name: 'Fullerton', country: 'United States' },
  { name: 'Killeen', country: 'United States' },
  { name: 'Frisco', country: 'United States' },
  { name: 'Hampton', country: 'United States' },
  { name: 'McAllen', country: 'United States' },
  { name: 'Warren', country: 'United States' },
  { name: 'Bellevue', country: 'United States' },
  { name: 'West Valley City', country: 'United States' },
  { name: 'Columbia', country: 'United States' },
  { name: 'Olathe', country: 'United States' },
  { name: 'Sterling Heights', country: 'United States' },
  { name: 'New Haven', country: 'United States' },
  { name: 'Miramar', country: 'United States' },
  { name: 'Waco', country: 'United States' },
  { name: 'Thousand Oaks', country: 'United States' },
  { name: 'Cedar Rapids', country: 'United States' },
  { name: 'Charleston', country: 'United States' },
  { name: 'Visalia', country: 'United States' },
  { name: 'Topeka', country: 'United States' },
  { name: 'Elizabeth', country: 'United States' },
  { name: 'Gainesville', country: 'United States' },
  { name: 'Thornton', country: 'United States' },
  { name: 'Roseville', country: 'United States' },
  { name: 'Carrollton', country: 'United States' },
  { name: 'Coral Springs', country: 'United States' },
  { name: 'Stamford', country: 'United States' },
  { name: 'Simi Valley', country: 'United States' },
  { name: 'Concord', country: 'United States' },
  { name: 'Hartford', country: 'United States' },
  { name: 'Kent', country: 'United States' },
  { name: 'Lafayette', country: 'United States' },
  { name: 'Midland', country: 'United States' },
  { name: 'Surprise', country: 'United States' },
  { name: 'Denton', country: 'United States' },
  { name: 'Victorville', country: 'United States' },
  { name: 'Evansville', country: 'United States' },
  { name: 'Santa Clara', country: 'United States' },
  { name: 'Abilene', country: 'United States' },
  { name: 'Athens', country: 'United States' },
  { name: 'Vallejo', country: 'United States' },
  { name: 'Allentown', country: 'United States' },
  { name: 'Norman', country: 'United States' },
  { name: 'Beaumont', country: 'United States' },
  { name: 'Independence', country: 'United States' },
  { name: 'Murfreesboro', country: 'United States' },
  { name: 'Ann Arbor', country: 'United States' },
  { name: 'Fargo', country: 'United States' },
  { name: 'Wilmington', country: 'United States' },
  { name: 'Golden', country: 'United States' },
  { name: 'Columbia', country: 'United States' },
  { name: 'Pearland', country: 'United States' },
  { name: 'Round Rock', country: 'United States' },
  { name: 'Carmel', country: 'United States' },
  { name: 'Westminster', country: 'United States' },
  { name: 'Arvada', country: 'United States' },
  { name: 'High Point', country: 'United States' },
  { name: 'Carlsbad', country: 'United States' },
  { name: 'Richardson', country: 'United States' },
  { name: 'Miami Gardens', country: 'United States' },
  { name: 'Clearwater', country: 'United States' },
  { name: 'West Palm Beach', country: 'United States' },
  { name: 'Manchester', country: 'United States' },
  { name: 'Cambridge', country: 'United States' },
  { name: 'Lowell', country: 'United States' },
  { name: 'Brockton', country: 'United States' },
  { name: 'New Bedford', country: 'United States' },
  { name: 'Quincy', country: 'United States' },
  { name: 'Lynn', country: 'United States' },
  { name: 'Fall River', country: 'United States' },
  { name: 'Newton', country: 'United States' },
  { name: 'Lawrence', country: 'United States' },
  { name: 'Somerville', country: 'United States' },
  { name: 'Framingham', country: 'United States' },
  { name: 'Haverhill', country: 'United States' },
  { name: 'Waltham', country: 'United States' },
  { name: 'Malden', country: 'United States' },
  { name: 'Brookline', country: 'United States' },
  { name: 'Plymouth', country: 'United States' },
  { name: 'Medford', country: 'United States' },
  { name: 'Taunton', country: 'United States' },
  { name: 'Chicopee', country: 'United States' },
  { name: 'Weymouth', country: 'United States' },
  { name: 'Revere', country: 'United States' },
  { name: 'Peabody', country: 'United States' },
  { name: 'Methuen', country: 'United States' },
  { name: 'Barnstable', country: 'United States' },
  { name: 'Pittsfield', country: 'United States' },
  { name: 'Attleboro', country: 'United States' },
  { name: 'Everett', country: 'United States' },
  { name: 'Salem', country: 'United States' },
  { name: 'Westfield', country: 'United States' },
  { name: 'Leominster', country: 'United States' },
  { name: 'Fitchburg', country: 'United States' },
  { name: 'Beverly', country: 'United States' },
  { name: 'Holyoke', country: 'United States' },
  { name: 'Marlborough', country: 'United States' },
  { name: 'Woburn', country: 'United States' },
  { name: 'Chelsea', country: 'United States' },
  { name: 'Amherst', country: 'United States' },
  { name: 'Dartmouth', country: 'United States' },
  { name: 'Shrewsbury', country: 'United States' },
  { name: 'Northampton', country: 'United States' },
  { name: 'Gloucester', country: 'United States' },
  { name: 'Milford', country: 'United States' },
  { name: 'Franklin', country: 'United States' },
  { name: 'Watertown', country: 'United States' },
  { name: 'Needham', country: 'United States' },
  { name: 'Danvers', country: 'United States' },
  { name: 'Wellesley', country: 'United States' },
  { name: 'Natick', country: 'United States' },
  { name: 'Norwood', country: 'United States' },
  { name: 'Randolph', country: 'United States' },
  { name: 'Braintree', country: 'United States' },
  { name: 'Reading', country: 'United States' },
  { name: 'Belmont', country: 'United States' },
  { name: 'Burlington', country: 'United States' },
  { name: 'Mansfield', country: 'United States' },
  { name: 'Wakefield', country: 'United States' },
  { name: 'Melrose', country: 'United States' },
  { name: 'Stoneham', country: 'United States' },
  { name: 'Dedham', country: 'United States' },
  { name: 'Winchester', country: 'United States' },
  { name: 'Wilmington', country: 'United States' },
  { name: 'Arlington', country: 'United States' },
  { name: 'Acton', country: 'United States' },
  { name: 'Concord', country: 'United States' },
  { name: 'Sudbury', country: 'United States' },
  { name: 'Lexington', country: 'United States' },
  { name: 'Weston', country: 'United States' },
  { name: 'Lincoln', country: 'United States' },
  { name: 'Wayland', country: 'United States' },
  { name: 'Waltham', country: 'United States' },
  { name: 'Watertown', country: 'United States' },
  { name: 'Belmont', country: 'United States' },
  { name: 'Arlington', country: 'United States' },
  { name: 'Cambridge', country: 'United States' },
  { name: 'Somerville', country: 'United States' },
  { name: 'Medford', country: 'United States' },
  { name: 'Malden', country: 'United States' },
  { name: 'Everett', country: 'United States' },
  { name: 'Chelsea', country: 'United States' },
  { name: 'Revere', country: 'United States' },
  { name: 'Winthrop', country: 'United States' },
  { name: 'Quincy', country: 'United States' },
  { name: 'Braintree', country: 'United States' },
  { name: 'Weymouth', country: 'United States' },
  { name: 'Hingham', country: 'United States' },
  { name: 'Cohasset', country: 'United States' },
  { name: 'Scituate', country: 'United States' },
  { name: 'Marshfield', country: 'United States' },
  { name: 'Duxbury', country: 'United States' },
  { name: 'Kingston', country: 'United States' },
  { name: 'Plymouth', country: 'United States' },
  { name: 'Carver', country: 'United States' },
  { name: 'Middleborough', country: 'United States' },
  { name: 'Bridgewater', country: 'United States' },
  { name: 'Brockton', country: 'United States' },
  { name: 'Easton', country: 'United States' },
  { name: 'Stoughton', country: 'United States' },
  { name: 'Sharon', country: 'United States' },
  { name: 'Foxborough', country: 'United States' },
  { name: 'Walpole', country: 'United States' },
  { name: 'Norwood', country: 'United States' },
  { name: 'Westwood', country: 'United States' },
  { name: 'Dedham', country: 'United States' },
  { name: 'Needham', country: 'United States' },
  { name: 'Wellesley', country: 'United States' },
  { name: 'Natick', country: 'United States' },
  { name: 'Framingham', country: 'United States' },
  { name: 'Ashland', country: 'United States' },
  { name: 'Hopkinton', country: 'United States' },
  { name: 'Holliston', country: 'United States' },
  { name: 'Milford', country: 'United States' },
  { name: 'Franklin', country: 'United States' },
  { name: 'Medway', country: 'United States' },
  { name: 'Millis', country: 'United States' },
  { name: 'Medfield', country: 'United States' },
  { name: 'Dover', country: 'United States' },
  { name: 'Sherborn', country: 'United States' },
  { name: 'Wayland', country: 'United States' },
  { name: 'Sudbury', country: 'United States' },
  { name: 'Lincoln', country: 'United States' },
  { name: 'Weston', country: 'United States' },
  { name: 'Concord', country: 'United States' },
  { name: 'Acton', country: 'United States' },
  { name: 'Maynard', country: 'United States' },
  { name: 'Stow', country: 'United States' },
  { name: 'Hudson', country: 'United States' },
  { name: 'Marlborough', country: 'United States' },
  { name: 'Southborough', country: 'United States' },
  { name: 'Westborough', country: 'United States' },
  { name: 'Shrewsbury', country: 'United States' },
  { name: 'Grafton', country: 'United States' },
  { name: 'Northborough', country: 'United States' },
  { name: 'Boylston', country: 'United States' },
  { name: 'Berlin', country: 'United States' },
  { name: 'Bolton', country: 'United States' },
  { name: 'Lancaster', country: 'United States' },
  { name: 'Sterling', country: 'United States' },
  { name: 'Princeton', country: 'United States' },
  { name: 'Westminster', country: 'United States' },
  { name: 'Fitchburg', country: 'United States' },
  { name: 'Leominster', country: 'United States' },
  { name: 'Lunenburg', country: 'United States' },
  { name: 'Shirley', country: 'United States' },
  { name: 'Ayer', country: 'United States' },
  { name: 'Groton', country: 'United States' },
  { name: 'Pepperell', country: 'United States' },
  { name: 'Townsend', country: 'United States' },
  { name: 'Ashby', country: 'United States' },
  { name: 'Mason', country: 'United States' },
  { name: 'Brookline', country: 'United States' },
  { name: 'Hollis', country: 'United States' },
  { name: 'Nashua', country: 'United States' },
  { name: 'Hudson', country: 'United States' },
  { name: 'Litchfield', country: 'United States' },
  { name: 'Merrimack', country: 'United States' },
  { name: 'Amherst', country: 'United States' },
  { name: 'Milford', country: 'United States' },
  { name: 'Wilton', country: 'United States' },
  { name: 'Lyndeborough', country: 'United States' },
  { name: 'Mont Vernon', country: 'United States' },
  { name: 'New Ipswich', country: 'United States' },
  { name: 'Temple', country: 'United States' },
  { name: 'Sharon', country: 'United States' },
  { name: 'Peterborough', country: 'United States' },
  { name: 'Jaffrey', country: 'United States' },
  { name: 'Rindge', country: 'United States' },
  { name: 'Fitzwilliam', country: 'United States' },
  { name: 'Troy', country: 'United States' },
  { name: 'Swanzey', country: 'United States' },
  { name: 'Keene', country: 'United States' },
  { name: 'Marlborough', country: 'United States' },
  { name: 'Dublin', country: 'United States' },
  { name: 'Harrisville', country: 'United States' },
  { name: 'Nelson', country: 'United States' },
  { name: 'Roxbury', country: 'United States' },
  { name: 'Surry', country: 'United States' },
  { name: 'Gilsum', country: 'United States' },
  { name: 'Sullivan', country: 'United States' },
  { name: 'Stoddard', country: 'United States' },
  { name: 'Antrim', country: 'United States' },
  { name: 'Bennington', country: 'United States' },
  { name: 'Francestown', country: 'United States' },
  { name: 'Greenfield', country: 'United States' },
  { name: 'Hancock', country: 'United States' },
  { name: 'Hillsborough', country: 'United States' },
  { name: 'Windsor', country: 'United States' },
  { name: 'Deering', country: 'United States' },
  { name: 'Weare', country: 'United States' },
  { name: 'Dunbarton', country: 'United States' },
  { name: 'Bow', country: 'United States' },
  { name: 'Hopkinton', country: 'United States' },
  { name: 'Henniker', country: 'United States' },
  { name: 'Warner', country: 'United States' },
  { name: 'Bradford', country: 'United States' },
  { name: 'Newbury', country: 'United States' },
  { name: 'Sunapee', country: 'United States' },
  { name: 'New London', country: 'United States' },
  { name: 'Wilmot', country: 'United States' },
  { name: 'Andover', country: 'United States' },
  { name: 'Danbury', country: 'United States' },
  { name: 'Salisbury', country: 'United States' },
  { name: 'Franklin', country: 'United States' },
  { name: 'Northfield', country: 'United States' },
  { name: 'Tilton', country: 'United States' },
  { name: 'Belmont', country: 'United States' },
  { name: 'Laconia', country: 'United States' },
  { name: 'Gilford', country: 'United States' },
  { name: 'Meredith', country: 'United States' },
  { name: 'Center Harbor', country: 'United States' },
  { name: 'Moultonborough', country: 'United States' },
  { name: 'Tuftonboro', country: 'United States' },
  { name: 'Wolfeboro', country: 'United States' },
  { name: 'Alton', country: 'United States' },
  { name: 'New Durham', country: 'United States' },
  { name: 'Farmington', country: 'United States' },
  { name: 'Rochester', country: 'United States' },
  { name: 'Dover', country: 'United States' },
  { name: 'Durham', country: 'United States' },
  { name: 'Madbury', country: 'United States' },
  { name: 'Lee', country: 'United States' },
  { name: 'Barrington', country: 'United States' },
  { name: 'Strafford', country: 'United States' },
  { name: 'Nottingham', country: 'United States' },
  { name: 'Northwood', country: 'United States' },
  { name: 'Epsom', country: 'United States' },
  { name: 'Pittsfield', country: 'United States' },
  { name: 'Chichester', country: 'United States' },
  { name: 'Loudon', country: 'United States' },
  { name: 'Canterbury', country: 'United States' },
  { name: 'Concord', country: 'United States' },
  { name: 'Pembroke', country: 'United States' },
  { name: 'Allenstown', country: 'United States' },
  { name: 'Hooksett', country: 'United States' },
  { name: 'Manchester', country: 'United States' },
  { name: 'Bedford', country: 'United States' },
  { name: 'Goffstown', country: 'United States' },
  { name: 'New Boston', country: 'United States' },
  { name: 'Francestown', country: 'United States' },
  { name: 'Greenfield', country: 'United States' },
  { name: 'Peterborough', country: 'United States' },
  { name: 'Jaffrey', country: 'United States' },
  { name: 'Rindge', country: 'United States' },
  { name: 'Fitzwilliam', country: 'United States' },
  { name: 'Troy', country: 'United States' },
  { name: 'Swanzey', country: 'United States' },
  { name: 'Keene', country: 'United States' },
  { name: 'Chesterfield', country: 'United States' },
  { name: 'Westmoreland', country: 'United States' },
  { name: 'Walpole', country: 'United States' },
  { name: 'Langdon', country: 'United States' },
  { name: 'Charlestown', country: 'United States' },
  { name: 'Claremont', country: 'United States' },
  { name: 'Newport', country: 'United States' },
  { name: 'Croydon', country: 'United States' },
  { name: 'Grantham', country: 'United States' },
  { name: 'Springfield', country: 'United States' },
  { name: 'Wilmot', country: 'United States' },
  { name: 'New London', country: 'United States' },
  { name: 'Sunapee', country: 'United States' },
  { name: 'Newbury', country: 'United States' },
  { name: 'Bradford', country: 'United States' },
  { name: 'Warner', country: 'United States' },
  { name: 'Henniker', country: 'United States' },
  { name: 'Hopkinton', country: 'United States' },
  { name: 'Bow', country: 'United States' },
  { name: 'Dunbarton', country: 'United States' },
  { name: 'Weare', country: 'United States' },
  { name: 'Deering', country: 'United States' },
  { name: 'Hillsborough', country: 'United States' },
  { name: 'Windsor', country: 'United States' },
  { name: 'Hancock', country: 'United States' },
  { name: 'Greenfield', country: 'United States' },
  { name: 'Francestown', country: 'United States' },
  { name: 'Bennington', country: 'United States' },
  { name: 'Antrim', country: 'United States' },
  { name: 'Stoddard', country: 'United States' },
  { name: 'Sullivan', country: 'United States' },
  { name: 'Gilsum', country: 'United States' },
  { name: 'Surry', country: 'United States' },
  { name: 'Roxbury', country: 'United States' },
  { name: 'Nelson', country: 'United States' },
  { name: 'Harrisville', country: 'United States' },
  { name: 'Dublin', country: 'United States' },
  { name: 'Marlborough', country: 'United States' },
  { name: 'Keene', country: 'United States' },
  { name: 'Swanzey', country: 'United States' },
  { name: 'Troy', country: 'United States' },
  { name: 'Fitzwilliam', country: 'United States' },
  { name: 'Rindge', country: 'United States' },
  { name: 'Jaffrey', country: 'United States' },
  { name: 'Peterborough', country: 'United States' },
  { name: 'Sharon', country: 'United States' },
  { name: 'Temple', country: 'United States' },
  { name: 'New Ipswich', country: 'United States' },
  { name: 'Mont Vernon', country: 'United States' },
  { name: 'Lyndeborough', country: 'United States' },
  { name: 'Wilton', country: 'United States' },
  { name: 'Milford', country: 'United States' },
  { name: 'Amherst', country: 'United States' },
  { name: 'Merrimack', country: 'United States' },
  { name: 'Litchfield', country: 'United States' },
  { name: 'Hudson', country: 'United States' },
  { name: 'Nashua', country: 'United States' },
  { name: 'Hollis', country: 'United States' },
  { name: 'Brookline', country: 'United States' },
  { name: 'Mason', country: 'United States' },
  
  // AUSTRALIA & OCEANIA
  { name: 'Sydney', country: 'Australia' },
  { name: 'Melbourne', country: 'Australia' },
  { name: 'Brisbane', country: 'Australia' },
  { name: 'Perth', country: 'Australia' },
  { name: 'Adelaide', country: 'Australia' },
  { name: 'Gold Coast', country: 'Australia' },
  { name: 'Newcastle', country: 'Australia' },
  { name: 'Canberra', country: 'Australia' },
  { name: 'Sunshine Coast', country: 'Australia' },
  { name: 'Wollongong', country: 'Australia' },
  { name: 'Hobart', country: 'Australia' },
  { name: 'Geelong', country: 'Australia' },
  { name: 'Townsville', country: 'Australia' },
  { name: 'Cairns', country: 'Australia' },
  { name: 'Darwin', country: 'Australia' },
  { name: 'Toowoomba', country: 'Australia' },
  { name: 'Ballarat', country: 'Australia' },
  { name: 'Bendigo', country: 'Australia' },
  { name: 'Albury', country: 'Australia' },
  { name: 'Launceston', country: 'Australia' },
  { name: 'Mackay', country: 'Australia' },
  { name: 'Rockhampton', country: 'Australia' },
  { name: 'Bunbury', country: 'Australia' },
  { name: 'Bundaberg', country: 'Australia' },
  { name: 'Coffs Harbour', country: 'Australia' },
  { name: 'Wagga Wagga', country: 'Australia' },
  { name: 'Hervey Bay', country: 'Australia' },
  { name: 'Mildura', country: 'Australia' },
  { name: 'Shepparton', country: 'Australia' },
  { name: 'Port Macquarie', country: 'Australia' },
  { name: 'Gladstone', country: 'Australia' },
  { name: 'Tamworth', country: 'Australia' },
  { name: 'Traralgon', country: 'Australia' },
  { name: 'Orange', country: 'Australia' },
  { name: 'Bowral', country: 'Australia' },
  { name: 'Geraldton', country: 'Australia' },
  { name: 'Kalgoorlie', country: 'Australia' },
  { name: 'Warrnambool', country: 'Australia' },
  { name: 'Devonport', country: 'Australia' },
  { name: 'Alice Springs', country: 'Australia' },
  { name: 'Dubbo', country: 'Australia' },
  { name: 'Mount Gambier', country: 'Australia' },
  { name: 'Lismore', country: 'Australia' },
  { name: 'Nelson Bay', country: 'Australia' },
  { name: 'Goulburn', country: 'Australia' },
  { name: 'Queanbeyan', country: 'Australia' },
  { name: 'Nowra', country: 'Australia' },
  { name: 'Broken Hill', country: 'Australia' },
  { name: 'Bathurst', country: 'Australia' },
  { name: 'Warwick', country: 'Australia' },
  { name: 'Kempsey', country: 'Australia' },
  { name: 'Grafton', country: 'Australia' },
  { name: 'Armidale', country: 'Australia' },
  { name: 'Gympie', country: 'Australia' },
  { name: 'Maryborough', country: 'Australia' },
  { name: 'Kingaroy', country: 'Australia' },
  { name: 'Casino', country: 'Australia' },
  { name: 'Murwillumbah', country: 'Australia' },
  { name: 'Byron Bay', country: 'Australia' },
  { name: 'Ballina', country: 'Australia' },
  { name: 'Tweed Heads', country: 'Australia' },
  { name: 'Coolangatta', country: 'Australia' },
  { name: 'Noosa', country: 'Australia' },
  { name: 'Caloundra', country: 'Australia' },
  { name: 'Maroochydore', country: 'Australia' },
  { name: 'Nambour', country: 'Australia' },
  { name: 'Caboolture', country: 'Australia' },
  { name: 'Redcliffe', country: 'Australia' },
  { name: 'Ipswich', country: 'Australia' },
  { name: 'Logan', country: 'Australia' },
  { name: 'Redland Bay', country: 'Australia' },
  { name: 'Beenleigh', country: 'Australia' },
  { name: 'Beaudesert', country: 'Australia' },
  { name: 'Gatton', country: 'Australia' },
  { name: 'Dalby', country: 'Australia' },
  { name: 'Chinchilla', country: 'Australia' },
  { name: 'Roma', country: 'Australia' },
  { name: 'Charleville', country: 'Australia' },
  { name: 'Cunnamulla', country: 'Australia' },
  { name: 'St George', country: 'Australia' },
  { name: 'Dirranbandi', country: 'Australia' },
  { name: 'Goondiwindi', country: 'Australia' },
  { name: 'Stanthorpe', country: 'Australia' },
  { name: 'Tenterfield', country: 'Australia' },
  { name: 'Glen Innes', country: 'Australia' },
  { name: 'Inverell', country: 'Australia' },
  { name: 'Moree', country: 'Australia' },
  { name: 'Narrabri', country: 'Australia' },
  { name: 'Gunnedah', country: 'Australia' },
  { name: 'Quirindi', country: 'Australia' },
  { name: 'Scone', country: 'Australia' },
  { name: 'Muswellbrook', country: 'Australia' },
  { name: 'Singleton', country: 'Australia' },
  { name: 'Cessnock', country: 'Australia' },
  { name: 'Maitland', country: 'Australia' },
  { name: 'Raymond Terrace', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Forster', country: 'Australia' },
  { name: 'Gloucester', country: 'Australia' },
  { name: 'Walcha', country: 'Australia' },
  { name: 'Uralla', country: 'Australia' },
  { name: 'Guyra', country: 'Australia' },
  { name: 'Tenterfield', country: 'Australia' },
  { name: 'Kyogle', country: 'Australia' },
  { name: 'Woodburn', country: 'Australia' },
  { name: 'Evans Head', country: 'Australia' },
  { name: 'Yamba', country: 'Australia' },
  { name: 'Iluka', country: 'Australia' },
  { name: 'Maclean', country: 'Australia' },
  { name: 'Grafton', country: 'Australia' },
  { name: 'Coffs Harbour', country: 'Australia' },
  { name: 'Bellingen', country: 'Australia' },
  { name: 'Dorrigo', country: 'Australia' },
  { name: 'Kempsey', country: 'Australia' },
  { name: 'Port Macquarie', country: 'Australia' },
  { name: 'Wauchope', country: 'Australia' },
  { name: 'Laurieton', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Gloucester', country: 'Australia' },
  { name: 'Dungog', country: 'Australia' },
  { name: 'Stroud', country: 'Australia' },
  { name: 'Tea Gardens', country: 'Australia' },
  { name: 'Hawks Nest', country: 'Australia' },
  { name: 'Bulahdelah', country: 'Australia' },
  { name: 'Forster', country: 'Australia' },
  { name: 'Tuncurry', country: 'Australia' },
  { name: 'Nabiac', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Wingham', country: 'Australia' },
  { name: 'Tinonee', country: 'Australia' },
  { name: 'Krambach', country: 'Australia' },
  { name: 'Hallidays Point', country: 'Australia' },
  { name: 'Old Bar', country: 'Australia' },
  { name: 'Wallabi Point', country: 'Australia' },
  { name: 'Diamond Beach', country: 'Australia' },
  { name: 'Black Head', country: 'Australia' },
  { name: 'Blueys Beach', country: 'Australia' },
  { name: 'Boomerang Beach', country: 'Australia' },
  { name: 'Pacific Palms', country: 'Australia' },
  { name: 'Seal Rocks', country: 'Australia' },
  { name: 'Bungwahl', country: 'Australia' },
  { name: 'Hawks Nest', country: 'Australia' },
  { name: 'Tea Gardens', country: 'Australia' },
  { name: 'Nelson Bay', country: 'Australia' },
  { name: 'Corlette', country: 'Australia' },
  { name: 'Fingal Bay', country: 'Australia' },
  { name: 'Anna Bay', country: 'Australia' },
  { name: 'Boat Harbour', country: 'Australia' },
  { name: 'One Mile', country: 'Australia' },
  { name: 'Williamtown', country: 'Australia' },
  { name: 'Salt Ash', country: 'Australia' },
  { name: 'Fern Bay', country: 'Australia' },
  { name: 'Medowie', country: 'Australia' },
  { name: 'Raymond Terrace', country: 'Australia' },
  { name: 'Heatherbrae', country: 'Australia' },
  { name: 'Tomago', country: 'Australia' },
  { name: 'Beresfield', country: 'Australia' },
  { name: 'Hexham', country: 'Australia' },
  { name: 'Tarro', country: 'Australia' },
  { name: 'Woodberry', country: 'Australia' },
  { name: 'Thornton', country: 'Australia' },
  { name: 'Maitland', country: 'Australia' },
  { name: 'East Maitland', country: 'Australia' },
  { name: 'Rutherford', country: 'Australia' },
  { name: 'Telarah', country: 'Australia' },
  { name: 'South Maitland', country: 'Australia' },
  { name: 'Lorn', country: 'Australia' },
  { name: 'Bolwarra', country: 'Australia' },
  { name: 'Largs', country: 'Australia' },
  { name: 'Hinton', country: 'Australia' },
  { name: 'Morpeth', country: 'Australia' },
  { name: 'Wallalong', country: 'Australia' },
  { name: 'Brandy Hill', country: 'Australia' },
  { name: 'Seaham', country: 'Australia' },
  { name: 'Nelsons Plains', country: 'Australia' },
  { name: 'Osterley', country: 'Australia' },
  { name: 'Clarence Town', country: 'Australia' },
  { name: 'Dungog', country: 'Australia' },
  { name: 'Paterson', country: 'Australia' },
  { name: 'Vacy', country: 'Australia' },
  { name: 'Gresford', country: 'Australia' },
  { name: 'Lostock', country: 'Australia' },
  { name: 'Hilldale', country: 'Australia' },
  { name: 'Wallarobba', country: 'Australia' },
  { name: 'Allynbrook', country: 'Australia' },
  { name: 'Eccleston', country: 'Australia' },
  { name: 'East Gresford', country: 'Australia' },
  { name: 'Brookfield', country: 'Australia' },
  { name: 'Martins Creek', country: 'Australia' },
  { name: 'Halton', country: 'Australia' },
  { name: 'Wallalong', country: 'Australia' },
  { name: 'Hinton', country: 'Australia' },
  { name: 'Largs', country: 'Australia' },
  { name: 'Bolwarra', country: 'Australia' },
  { name: 'Lorn', country: 'Australia' },
  { name: 'South Maitland', country: 'Australia' },
  { name: 'Telarah', country: 'Australia' },
  { name: 'Rutherford', country: 'Australia' },
  { name: 'East Maitland', country: 'Australia' },
  { name: 'Maitland', country: 'Australia' },
  { name: 'Thornton', country: 'Australia' },
  { name: 'Woodberry', country: 'Australia' },
  { name: 'Tarro', country: 'Australia' },
  { name: 'Hexham', country: 'Australia' },
  { name: 'Beresfield', country: 'Australia' },
  { name: 'Tomago', country: 'Australia' },
  { name: 'Heatherbrae', country: 'Australia' },
  { name: 'Raymond Terrace', country: 'Australia' },
  { name: 'Medowie', country: 'Australia' },
  { name: 'Fern Bay', country: 'Australia' },
  { name: 'Salt Ash', country: 'Australia' },
  { name: 'Williamtown', country: 'Australia' },
  { name: 'One Mile', country: 'Australia' },
  { name: 'Boat Harbour', country: 'Australia' },
  { name: 'Anna Bay', country: 'Australia' },
  { name: 'Fingal Bay', country: 'Australia' },
  { name: 'Corlette', country: 'Australia' },
  { name: 'Nelson Bay', country: 'Australia' },
  { name: 'Tea Gardens', country: 'Australia' },
  { name: 'Hawks Nest', country: 'Australia' },
  { name: 'Bungwahl', country: 'Australia' },
  { name: 'Seal Rocks', country: 'Australia' },
  { name: 'Pacific Palms', country: 'Australia' },
  { name: 'Boomerang Beach', country: 'Australia' },
  { name: 'Blueys Beach', country: 'Australia' },
  { name: 'Black Head', country: 'Australia' },
  { name: 'Diamond Beach', country: 'Australia' },
  { name: 'Wallabi Point', country: 'Australia' },
  { name: 'Old Bar', country: 'Australia' },
  { name: 'Hallidays Point', country: 'Australia' },
  { name: 'Krambach', country: 'Australia' },
  { name: 'Tinonee', country: 'Australia' },
  { name: 'Wingham', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Nabiac', country: 'Australia' },
  { name: 'Tuncurry', country: 'Australia' },
  { name: 'Forster', country: 'Australia' },
  { name: 'Bulahdelah', country: 'Australia' },
  { name: 'Hawks Nest', country: 'Australia' },
  { name: 'Tea Gardens', country: 'Australia' },
  { name: 'Stroud', country: 'Australia' },
  { name: 'Dungog', country: 'Australia' },
  { name: 'Gloucester', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Laurieton', country: 'Australia' },
  { name: 'Wauchope', country: 'Australia' },
  { name: 'Port Macquarie', country: 'Australia' },
  { name: 'Kempsey', country: 'Australia' },
  { name: 'Dorrigo', country: 'Australia' },
  { name: 'Bellingen', country: 'Australia' },
  { name: 'Coffs Harbour', country: 'Australia' },
  { name: 'Grafton', country: 'Australia' },
  { name: 'Maclean', country: 'Australia' },
  { name: 'Iluka', country: 'Australia' },
  { name: 'Yamba', country: 'Australia' },
  { name: 'Evans Head', country: 'Australia' },
  { name: 'Woodburn', country: 'Australia' },
  { name: 'Kyogle', country: 'Australia' },
  { name: 'Tenterfield', country: 'Australia' },
  { name: 'Guyra', country: 'Australia' },
  { name: 'Uralla', country: 'Australia' },
  { name: 'Walcha', country: 'Australia' },
  { name: 'Gloucester', country: 'Australia' },
  { name: 'Forster', country: 'Australia' },
  { name: 'Taree', country: 'Australia' },
  { name: 'Raymond Terrace', country: 'Australia' },
  { name: 'Maitland', country: 'Australia' },
  { name: 'Cessnock', country: 'Australia' },
  { name: 'Singleton', country: 'Australia' },
  { name: 'Muswellbrook', country: 'Australia' },
  { name: 'Scone', country: 'Australia' },
  { name: 'Quirindi', country: 'Australia' },
  { name: 'Gunnedah', country: 'Australia' },
  { name: 'Narrabri', country: 'Australia' },
  { name: 'Moree', country: 'Australia' },
  { name: 'Inverell', country: 'Australia' },
  { name: 'Glen Innes', country: 'Australia' },
  { name: 'Tenterfield', country: 'Australia' },
  { name: 'Stanthorpe', country: 'Australia' },
  { name: 'Goondiwindi', country: 'Australia' },
  { name: 'Dirranbandi', country: 'Australia' },
  { name: 'St George', country: 'Australia' },
  { name: 'Cunnamulla', country: 'Australia' },
  { name: 'Charleville', country: 'Australia' },
  { name: 'Roma', country: 'Australia' },
  { name: 'Chinchilla', country: 'Australia' },
  { name: 'Dalby', country: 'Australia' },
  { name: 'Gatton', country: 'Australia' },
  { name: 'Beaudesert', country: 'Australia' },
  { name: 'Beenleigh', country: 'Australia' },
  { name: 'Redland Bay', country: 'Australia' },
  { name: 'Logan', country: 'Australia' },
  { name: 'Ipswich', country: 'Australia' },
  { name: 'Redcliffe', country: 'Australia' },
  { name: 'Caboolture', country: 'Australia' },
  { name: 'Nambour', country: 'Australia' },
  { name: 'Maroochydore', country: 'Australia' },
  { name: 'Caloundra', country: 'Australia' },
  { name: 'Noosa', country: 'Australia' },
  { name: 'Coolangatta', country: 'Australia' },
  { name: 'Tweed Heads', country: 'Australia' },
  { name: 'Ballina', country: 'Australia' },
  { name: 'Byron Bay', country: 'Australia' },
  { name: 'Murwillumbah', country: 'Australia' },
  { name: 'Casino', country: 'Australia' },
  { name: 'Kingaroy', country: 'Australia' },
  { name: 'Maryborough', country: 'Australia' },
  { name: 'Gympie', country: 'Australia' },
  { name: 'Armidale', country: 'Australia' },
  { name: 'Grafton', country: 'Australia' },
  { name: 'Kempsey', country: 'Australia' },
  { name: 'Warwick', country: 'Australia' },
  { name: 'Bathurst', country: 'Australia' },
  { name: 'Broken Hill', country: 'Australia' },
  { name: 'Nowra', country: 'Australia' },
  { name: 'Queanbeyan', country: 'Australia' },
  { name: 'Goulburn', country: 'Australia' },
  { name: 'Nelson Bay', country: 'Australia' },
  { name: 'Lismore', country: 'Australia' },
  { name: 'Mount Gambier', country: 'Australia' },
  { name: 'Dubbo', country: 'Australia' },
  { name: 'Alice Springs', country: 'Australia' },
  { name: 'Devonport', country: 'Australia' },
  { name: 'Warrnambool', country: 'Australia' },
  { name: 'Kalgoorlie', country: 'Australia' },
  { name: 'Geraldton', country: 'Australia' },
  { name: 'Bowral', country: 'Australia' },
  { name: 'Orange', country: 'Australia' },
  { name: 'Traralgon', country: 'Australia' },
  { name: 'Tamworth', country: 'Australia' },
  { name: 'Gladstone', country: 'Australia' },
  { name: 'Port Macquarie', country: 'Australia' },
  { name: 'Shepparton', country: 'Australia' },
  { name: 'Mildura', country: 'Australia' },
  { name: 'Hervey Bay', country: 'Australia' },
  { name: 'Wagga Wagga', country: 'Australia' },
  { name: 'Coffs Harbour', country: 'Australia' },
  { name: 'Bundaberg', country: 'Australia' },
  { name: 'Bunbury', country: 'Australia' },
  { name: 'Rockhampton', country: 'Australia' },
  { name: 'Mackay', country: 'Australia' },
  { name: 'Launceston', country: 'Australia' },
  { name: 'Albury', country: 'Australia' },
  { name: 'Bendigo', country: 'Australia' },
  { name: 'Ballarat', country: 'Australia' },
  { name: 'Toowoomba', country: 'Australia' },
  { name: 'Darwin', country: 'Australia' },
  { name: 'Cairns', country: 'Australia' },
  { name: 'Townsville', country: 'Australia' },
  { name: 'Geelong', country: 'Australia' },
  { name: 'Hobart', country: 'Australia' },
  { name: 'Wollongong', country: 'Australia' },
  { name: 'Sunshine Coast', country: 'Australia' },
  { name: 'Canberra', country: 'Australia' },
  { name: 'Newcastle', country: 'Australia' },
  { name: 'Gold Coast', country: 'Australia' },
  { name: 'Adelaide', country: 'Australia' },
  { name: 'Perth', country: 'Australia' },
  { name: 'Brisbane', country: 'Australia' },
  { name: 'Melbourne', country: 'Australia' },
  { name: 'Sydney', country: 'Australia' },
  
  { name: 'Auckland', country: 'New Zealand' },
  { name: 'Wellington', country: 'New Zealand' },
  { name: 'Christchurch', country: 'New Zealand' },
  { name: 'Hamilton', country: 'New Zealand' },
  { name: 'Tauranga', country: 'New Zealand' },
  { name: 'Dunedin', country: 'New Zealand' },
  { name: 'Palmerston North', country: 'New Zealand' },
  { name: 'Napier', country: 'New Zealand' },
  { name: 'Nelson', country: 'New Zealand' },
  { name: 'Rotorua', country: 'New Zealand' },
  { name: 'New Plymouth', country: 'New Zealand' },
  { name: 'Whangarei', country: 'New Zealand' },
  { name: 'Invercargill', country: 'New Zealand' },
  { name: 'Wanganui', country: 'New Zealand' },
  { name: 'Gisborne', country: 'New Zealand' },
  { name: 'Timaru', country: 'New Zealand' },
  { name: 'Hastings', country: 'New Zealand' },
  { name: 'Blenheim', country: 'New Zealand' },
  { name: 'Oamaru', country: 'New Zealand' },
  { name: 'Ashburton', country: 'New Zealand' },
  { name: 'Greymouth', country: 'New Zealand' },
  { name: 'Westport', country: 'New Zealand' },
  { name: 'Te Awamutu', country: 'New Zealand' },
  { name: 'Cambridge', country: 'New Zealand' },
  { name: 'Morrinsville', country: 'New Zealand' },
  { name: 'Paeroa', country: 'New Zealand' },
  { name: 'Thames', country: 'New Zealand' },
  { name: 'Coromandel', country: 'New Zealand' },
  { name: 'Whitianga', country: 'New Zealand' },
  { name: 'Taupo', country: 'New Zealand' },
  { name: 'Turangi', country: 'New Zealand' },
  { name: 'Taumarunui', country: 'New Zealand' },
  { name: 'Ohakune', country: 'New Zealand' },
  { name: 'Waiouru', country: 'New Zealand' },
  { name: 'Bulls', country: 'New Zealand' },
  { name: 'Foxton', country: 'New Zealand' },
  { name: 'Levin', country: 'New Zealand' },
  { name: 'Otaki', country: 'New Zealand' },
  { name: 'Paraparaumu', country: 'New Zealand' },
  { name: 'Kapiti', country: 'New Zealand' },
  { name: 'Porirua', country: 'New Zealand' },
  { name: 'Upper Hutt', country: 'New Zealand' },
  { name: 'Lower Hutt', country: 'New Zealand' },
  { name: 'Masterton', country: 'New Zealand' },
  { name: 'Martinborough', country: 'New Zealand' },
  { name: 'Greytown', country: 'New Zealand' },
  { name: 'Carterton', country: 'New Zealand' },
  { name: 'Featherston', country: 'New Zealand' },
  { name: 'Picton', country: 'New Zealand' },
  { name: 'Kaikoura', country: 'New Zealand' },
  { name: 'Hanmer Springs', country: 'New Zealand' },
  { name: 'Reefton', country: 'New Zealand' },
  { name: 'Hokitika', country: 'New Zealand' },
  { name: 'Franz Josef', country: 'New Zealand' },
  { name: 'Fox Glacier', country: 'New Zealand' },
  { name: 'Wanaka', country: 'New Zealand' },
  { name: 'Queenstown', country: 'New Zealand' },
  { name: 'Te Anau', country: 'New Zealand' },
  { name: 'Milford Sound', country: 'New Zealand' },
  { name: 'Gore', country: 'New Zealand' },
  { name: 'Balclutha', country: 'New Zealand' },
  { name: 'Alexandra', country: 'New Zealand' },
  { name: 'Cromwell', country: 'New Zealand' },
  { name: 'Arrowtown', country: 'New Zealand' },
  { name: 'Clyde', country: 'New Zealand' },
  { name: 'Roxburgh', country: 'New Zealand' },
  { name: 'Lawrence', country: 'New Zealand' },
  { name: 'Milton', country: 'New Zealand' },
  { name: 'Mosgiel', country: 'New Zealand' },
  { name: 'Brighton', country: 'New Zealand' },
  { name: 'Green Island', country: 'New Zealand' },
  { name: 'Abbotsford', country: 'New Zealand' },
  { name: 'Kaikorai', country: 'New Zealand' },
  { name: 'Roslyn', country: 'New Zealand' },
  { name: 'Belleknowes', country: 'New Zealand' },
  { name: 'Waverley', country: 'New Zealand' },
  { name: 'St. Clair', country: 'New Zealand' },
  { name: 'Forbury', country: 'New Zealand' },
  { name: 'South Dunedin', country: 'New Zealand' },
  { name: 'Caversham', country: 'New Zealand' },
  { name: 'Kensington', country: 'New Zealand' },
  { name: 'Mornington', country: 'New Zealand' },
  { name: 'Maori Hill', country: 'New Zealand' },
  { name: 'Dalmore', country: 'New Zealand' },
  { name: 'North East Valley', country: 'New Zealand' },
  { name: 'Glenleith', country: 'New Zealand' },
  { name: 'Halfway Bush', country: 'New Zealand' },
  { name: 'Helensburgh', country: 'New Zealand' },
  { name: 'Fairfield', country: 'New Zealand' },
  { name: 'Wakari', country: 'New Zealand' },
  { name: 'Corstorphine', country: 'New Zealand' },
  { name: 'Burnside', country: 'New Zealand' },
  { name: 'Brockville', country: 'New Zealand' },
  { name: 'Concord', country: 'New Zealand' },
  { name: 'Lookout Point', country: 'New Zealand' },
  { name: 'Liberton', country: 'New Zealand' },
  { name: 'Shiel Hill', country: 'New Zealand' },
  { name: 'Tainui', country: 'New Zealand' },
  { name: 'Ocean Grove', country: 'New Zealand' },
  { name: 'Tahuna', country: 'New Zealand' },
  { name: 'Andersons Bay', country: 'New Zealand' },
  { name: 'Musselburgh', country: 'New Zealand' },
  { name: 'Lawyers Head', country: 'New Zealand' },
  { name: 'Tomahawk', country: 'New Zealand' },
  { name: 'Ocean View', country: 'New Zealand' },
  { name: 'Broad Bay', country: 'New Zealand' },
  { name: 'Portobello', country: 'New Zealand' },
  { name: 'Macandrew Bay', country: 'New Zealand' },
  { name: 'Company Bay', country: 'New Zealand' },
  { name: 'Quarantine Island', country: 'New Zealand' },
  { name: 'Otakou', country: 'New Zealand' },
  { name: 'Harington Point', country: 'New Zealand' },
  { name: 'Papanui Inlet', country: 'New Zealand' },
  { name: 'Hoopers Inlet', country: 'New Zealand' },
  { name: 'Allans Beach', country: 'New Zealand' },
  { name: 'Sandymount', country: 'New Zealand' },
  { name: 'Sandfly Bay', country: 'New Zealand' },
  { name: 'Seal Point', country: 'New Zealand' },
  { name: 'Pilots Beach', country: 'New Zealand' },
  { name: 'Taiaroa Head', country: 'New Zealand' },
  { name: 'Penguin Place', country: 'New Zealand' },
  
  { name: 'Suva', country: 'Fiji' },
  { name: 'Lautoka', country: 'Fiji' },
  { name: 'Nadi', country: 'Fiji' },
  { name: 'Labasa', country: 'Fiji' },
  { name: 'Ba', country: 'Fiji' },
  { name: 'Tavua', country: 'Fiji' },
  { name: 'Sigatoka', country: 'Fiji' },
  { name: 'Rakiraki', country: 'Fiji' },
  { name: 'Korovou', country: 'Fiji' },
  { name: 'Levuka', country: 'Fiji' },
  { name: 'Savusavu', country: 'Fiji' },
  { name: 'Navua', country: 'Fiji' },
  { name: 'Denarau Island', country: 'Fiji' },
  { name: 'Coral Coast', country: 'Fiji' },
  { name: 'Beqa Island', country: 'Fiji' },
  { name: 'Kadavu Island', country: 'Fiji' },
  { name: 'Taveuni Island', country: 'Fiji' },
  { name: 'Vanua Levu', country: 'Fiji' },
  { name: 'Viti Levu', country: 'Fiji' },
  { name: 'Mamanuca Islands', country: 'Fiji' },
  { name: 'Yasawa Islands', country: 'Fiji' },
  
  { name: 'Papeete', country: 'French Polynesia' },
  { name: 'Moorea', country: 'French Polynesia' },
  { name: 'Bora Bora', country: 'French Polynesia' },
  { name: 'Tahiti', country: 'French Polynesia' },
  { name: 'Huahine', country: 'French Polynesia' },
  { name: 'Raiatea', country: 'French Polynesia' },
  { name: 'Taha\'a', country: 'French Polynesia' },
  { name: 'Rangiroa', country: 'French Polynesia' },
  { name: 'Fakarava', country: 'French Polynesia' },
  { name: 'Tikehau', country: 'French Polynesia' },
  { name: 'Manihi', country: 'French Polynesia' },
  { name: 'Hiva Oa', country: 'French Polynesia' },
  { name: 'Nuku Hiva', country: 'French Polynesia' },

  // OTHERS - Additional important locations
  { name: 'Singapore', country: 'Singapore' },
  { name: 'Hong Kong', country: 'Hong Kong' },
  { name: 'Macau', country: 'Macau' },
  { name: 'Taipei', country: 'Taiwan' },
  { name: 'Kaohsiung', country: 'Taiwan' },
  { name: 'Taichung', country: 'Taiwan' },
  { name: 'Tainan', country: 'Taiwan' },
  { name: 'Hualien', country: 'Taiwan' },
  { name: 'Taitung', country: 'Taiwan' },
  { name: 'Kenting', country: 'Taiwan' },
  { name: 'Jiufen', country: 'Taiwan' },
  { name: 'Taroko Gorge', country: 'Taiwan' },
  { name: 'Sun Moon Lake', country: 'Taiwan' },
  { name: 'Alishan', country: 'Taiwan' },
  { name: 'Taipei 101', country: 'Taiwan' },
  { name: 'Shilin Night Market', country: 'Taiwan' },
  { name: 'Yangmingshan', country: 'Taiwan' },
  { name: 'Beitou', country: 'Taiwan' },
  { name: 'Tamsui', country: 'Taiwan' },
  { name: 'Yehliu Geopark', country: 'Taiwan' },
  { name: 'Pingxi', country: 'Taiwan' },
  { name: 'Shifen', country: 'Taiwan' },
  { name: 'Jinguashi', country: 'Taiwan' },
  { name: 'Bitou Cape', country: 'Taiwan' },
  { name: 'Yilan', country: 'Taiwan' },
  { name: 'Luodong', country: 'Taiwan' },
  { name: 'Toucheng', country: 'Taiwan' },
  { name: 'Wufengqi Waterfall', country: 'Taiwan' },
  { name: 'Dongshan River', country: 'Taiwan' },
  { name: 'Guishan Island', country: 'Taiwan' },
  { name: 'Taroko National Park', country: 'Taiwan' },
  { name: 'Qixingtan Beach', country: 'Taiwan' },
  { name: 'Liyu Lake', country: 'Taiwan' },
  { name: 'Farglory Ocean Park', country: 'Taiwan' },
  { name: 'Chishingtan', country: 'Taiwan' },
  { name: 'Green Island', country: 'Taiwan' },
  { name: 'Orchid Island', country: 'Taiwan' },
  { name: 'Sanxiantai', country: 'Taiwan' },
  { name: 'Xiaoyeliu', country: 'Taiwan' },
  { name: 'Taitung Forest Park', country: 'Taiwan' },
  { name: 'Chishang', country: 'Taiwan' },
  { name: 'Brown Avenue', country: 'Taiwan' },
  { name: 'Luye Highland', country: 'Taiwan' },
  { name: 'Zhiben Hot Springs', country: 'Taiwan' },
  { name: 'Kenting National Park', country: 'Taiwan' },
  { name: 'Eluanbi Lighthouse', country: 'Taiwan' },
  { name: 'Baisha Beach', country: 'Taiwan' },
  { name: 'Maobitou', country: 'Taiwan' },
  { name: 'Longpan Park', country: 'Taiwan' },
  { name: 'Hengchun', country: 'Taiwan' },
  { name: 'Chuhuo Special Scenic Area', country: 'Taiwan' },
  { name: 'Fenggang', country: 'Taiwan' },
  { name: 'Pingtung', country: 'Taiwan' },
  { name: 'Liuchiu', country: 'Taiwan' },
  { name: 'Xiao Liuchiu', country: 'Taiwan' },
  { name: 'Love River', country: 'Taiwan' },
  { name: 'Lotus Pond', country: 'Taiwan' },
  { name: 'Cijin Island', country: 'Taiwan' },
  { name: 'Sizihwan', country: 'Taiwan' },
  { name: 'Pier-2 Art Center', country: 'Taiwan' },
  { name: 'Fo Guang Shan', country: 'Taiwan' },
  { name: 'Meinong', country: 'Taiwan' },
  { name: 'Maolin', country: 'Taiwan' },
  { name: 'Taijiang National Park', country: 'Taiwan' },
  { name: 'Anping Fort', country: 'Taiwan' },
  { name: 'Anping Old Street', country: 'Taiwan' },
  { name: 'Chihkan Tower', country: 'Taiwan' },
  { name: 'Garden Night Market', country: 'Taiwan' },
  { name: 'Hayashi Department Store', country: 'Taiwan' },
  { name: 'Guanziling Hot Spring', country: 'Taiwan' },
  { name: 'Wuling Farm', country: 'Taiwan' },
  { name: 'Fengjia Night Market', country: 'Taiwan' },
  { name: 'Rainbow Village', country: 'Taiwan' },
  { name: 'Gaomei Wetlands', country: 'Taiwan' },
  { name: 'Miyahara Ice Cream', country: 'Taiwan' },
  { name: 'National Taichung Theater', country: 'Taiwan' },
  { name: 'Xinshe', country: 'Taiwan' },
  { name: 'Dakeng', country: 'Taiwan' },
  { name: 'Guguan', country: 'Taiwan' },
  { name: 'Cingjing Farm', country: 'Taiwan' },
  { name: 'Hehuanshan', country: 'Taiwan' },
  { name: 'Wushe', country: 'Taiwan' },
  { name: 'Puli', country: 'Taiwan' },
  { name: 'Formosan Aboriginal Culture Village', country: 'Taiwan' },
  { name: 'Xuanzang Temple', country: 'Taiwan' },
  { name: 'Shuishe Pier', country: 'Taiwan' },
  { name: 'Ita Thao', country: 'Taiwan' },
  { name: 'Wen Wu Temple', country: 'Taiwan' },
  { name: 'Meishan', country: 'Taiwan' },
  { name: 'Fenqihu', country: 'Taiwan' },
  { name: 'Alishan Forest Railway', country: 'Taiwan' },
  { name: 'Chiayi', country: 'Taiwan' },
  { name: 'Dabang', country: 'Taiwan' },
  { name: 'Shanlinxi', country: 'Taiwan' },
  { name: 'Xitou', country: 'Taiwan' },
  { name: 'Monster Village', country: 'Taiwan' },
  { name: 'Lukang', country: 'Taiwan' },
  { name: 'Lukang Old Street', country: 'Taiwan' },
  { name: 'Longshan Temple', country: 'Taiwan' },
  { name: 'Tianhou Temple', country: 'Taiwan' },
  { name: 'Changhua', country: 'Taiwan' },
  { name: 'Big Buddha', country: 'Taiwan' },
  { name: 'Baguashan', country: 'Taiwan' },
  { name: 'Hsinchu', country: 'Taiwan' },
  { name: 'Neiwan', country: 'Taiwan' },
  { name: 'Beipu', country: 'Taiwan' },
  { name: 'Emei Lake', country: 'Taiwan' },
  { name: 'Miaoli', country: 'Taiwan' },
  { name: 'Nanzhuang', country: 'Taiwan' },
  { name: 'Sanyi', country: 'Taiwan' },
  { name: 'Dahu', country: 'Taiwan' },
  { name: 'Shei-Pa National Park', country: 'Taiwan' },
  { name: 'Yushan', country: 'Taiwan' },
  { name: 'Yushan National Park', country: 'Taiwan' },
  { name: 'Taoyuan', country: 'Taiwan' },
  { name: 'Daxi', country: 'Taiwan' },
  { name: 'Fuxing', country: 'Taiwan' },
  { name: 'Lala Mountain', country: 'Taiwan' },
  { name: 'Shimen Reservoir', country: 'Taiwan' },
  { name: 'Penghu', country: 'Taiwan' },
  { name: 'Penghu Islands', country: 'Taiwan' },
  { name: 'Magong', country: 'Taiwan' },
  { name: 'Jibei Island', country: 'Taiwan' },
  { name: 'Qimei Island', country: 'Taiwan' },
  { name: 'Wangan', country: 'Taiwan' },
  { name: 'Baisha', country: 'Taiwan' },
  { name: 'Xiyu', country: 'Taiwan' },
  { name: 'Twin Hearts Stone Weir', country: 'Taiwan' },
  { name: 'Whale Cave', country: 'Taiwan' },
  { name: 'Kinmen', country: 'Taiwan' },
  { name: 'Jincheng', country: 'Taiwan' },
  { name: 'Shuitou', country: 'Taiwan' },
  { name: 'Juguang Tower', country: 'Taiwan' },
  { name: 'Matsu', country: 'Taiwan' },
  { name: 'Nangan', country: 'Taiwan' },
  { name: 'Beigan', country: 'Taiwan' },
  { name: 'Dongyin', country: 'Taiwan' },
  
  // Other European countries
  { name: 'Amsterdam', country: 'Netherlands' },
  { name: 'Rotterdam', country: 'Netherlands' },
  { name: 'The Hague', country: 'Netherlands' },
  { name: 'Utrecht', country: 'Netherlands' },
  { name: 'Eindhoven', country: 'Netherlands' },
  { name: 'Tilburg', country: 'Netherlands' },
  { name: 'Groningen', country: 'Netherlands' },
  { name: 'Almere', country: 'Netherlands' },
  { name: 'Breda', country: 'Netherlands' },
  { name: 'Nijmegen', country: 'Netherlands' },
  { name: 'Enschede', country: 'Netherlands' },
  { name: 'Haarlem', country: 'Netherlands' },
  { name: 'Arnhem', country: 'Netherlands' },
  { name: 'Zaanstad', country: 'Netherlands' },
  { name: 'Maastricht', country: 'Netherlands' },
  { name: 'Dordrecht', country: 'Netherlands' },
  { name: 'Leiden', country: 'Netherlands' },
  { name: 'Haarlemmermeer', country: 'Netherlands' },
  { name: 'Zoetermeer', country: 'Netherlands' },
  { name: '\'s-Hertogenbosch', country: 'Netherlands' },
  { name: 'Zwolle', country: 'Netherlands' },
  { name: 'Amersfoort', country: 'Netherlands' },
  { name: 'Leeuwarden', country: 'Netherlands' },
  { name: 'Apeldoorn', country: 'Netherlands' },
  { name: 'Delft', country: 'Netherlands' },
  { name: 'Alkmaar', country: 'Netherlands' },
  { name: 'Westland', country: 'Netherlands' },
  { name: 'Venlo', country: 'Netherlands' },
  { name: 'Deventer', country: 'Netherlands' },
  { name: 'Ede', country: 'Netherlands' },
  { name: 'Purmerend', country: 'Netherlands' },
  { name: 'Leidschendam-Voorburg', country: 'Netherlands' },
  { name: 'Hilversum', country: 'Netherlands' },
  { name: 'Hengelo', country: 'Netherlands' },
  { name: 'Alphen aan den Rijn', country: 'Netherlands' },
  { name: 'Emmen', country: 'Netherlands' },
  { name: 'Gouda', country: 'Netherlands' },
  { name: 'Spijkenisse', country: 'Netherlands' },
  { name: 'Roosendaal', country: 'Netherlands' },
  { name: 'Schiedam', country: 'Netherlands' },
  { name: 'Vlaardingen', country: 'Netherlands' },
  { name: 'Almelo', country: 'Netherlands' },
  { name: 'Bergen op Zoom', country: 'Netherlands' },
  { name: 'Sittard-Geleen', country: 'Netherlands' },
  { name: 'Hoorn', country: 'Netherlands' },
  { name: 'Middelburg', country: 'Netherlands' },
  { name: 'Nieuwegein', country: 'Netherlands' },
  { name: 'Doetinchem', country: 'Netherlands' },
  { name: 'Hoofddorp', country: 'Netherlands' },
  { name: 'Ridderkerk', country: 'Netherlands' },
  { name: 'Zeist', country: 'Netherlands' },
  { name: 'Beverwijk', country: 'Netherlands' },
  { name: 'Capelle aan den IJssel', country: 'Netherlands' },
  { name: 'Lisse', country: 'Netherlands' },
  { name: 'Volendam', country: 'Netherlands' },
  { name: 'Marken', country: 'Netherlands' },
  { name: 'Edam', country: 'Netherlands' },
  { name: 'Giethoorn', country: 'Netherlands' },
  { name: 'Kinderdijk', country: 'Netherlands' },
  { name: 'Zaanse Schans', country: 'Netherlands' },
  { name: 'Keukenhof', country: 'Netherlands' },
  
  { name: 'Brussels', country: 'Belgium' },
  { name: 'Antwerp', country: 'Belgium' },
  { name: 'Ghent', country: 'Belgium' },
  { name: 'Charleroi', country: 'Belgium' },
  { name: 'Liège', country: 'Belgium' },
  { name: 'Bruges', country: 'Belgium' },
  { name: 'Namur', country: 'Belgium' },
  { name: 'Leuven', country: 'Belgium' },
  { name: 'Mons', country: 'Belgium' },
  { name: 'Aalst', country: 'Belgium' },
  { name: 'La Louvière', country: 'Belgium' },
  { name: 'Kortrijk', country: 'Belgium' },
  { name: 'Hasselt', country: 'Belgium' },
  { name: 'Sint-Niklaas', country: 'Belgium' },
  { name: 'Ostend', country: 'Belgium' },
  { name: 'Genk', country: 'Belgium' },
  { name: 'Seraing', country: 'Belgium' },
  { name: 'Roeselare', country: 'Belgium' },
  { name: 'Mouscron', country: 'Belgium' },
  { name: 'Verviers', country: 'Belgium' },
  { name: 'Dinant', country: 'Belgium' },
  { name: 'Durbuy', country: 'Belgium' },
  { name: 'Waterloo', country: 'Belgium' },
  { name: 'Spa', country: 'Belgium' },
  { name: 'Mechelen', country: 'Belgium' },
  { name: 'Tournai', country: 'Belgium' },
  { name: 'Ypres', country: 'Belgium' },
  { name: 'Lier', country: 'Belgium' },
  { name: 'Damme', country: 'Belgium' },
  { name: 'Knokke-Heist', country: 'Belgium' },
  
  { name: 'Zurich', country: 'Switzerland' },
  { name: 'Geneva', country: 'Switzerland' },
  { name: 'Basel', country: 'Switzerland' },
  { name: 'Lausanne', country: 'Switzerland' },
  { name: 'Bern', country: 'Switzerland' },
  { name: 'Winterthur', country: 'Switzerland' },
  { name: 'Lucerne', country: 'Switzerland' },
  { name: 'St. Gallen', country: 'Switzerland' },
  { name: 'Lugano', country: 'Switzerland' },
  { name: 'Biel/Bienne', country: 'Switzerland' },
  { name: 'Thun', country: 'Switzerland' },
  { name: 'Köniz', country: 'Switzerland' },
  { name: 'La Chaux-de-Fonds', country: 'Switzerland' },
  { name: 'Schaffhausen', country: 'Switzerland' },
  { name: 'Fribourg', country: 'Switzerland' },
  { name: 'Vernier', country: 'Switzerland' },
  { name: 'Chur', country: 'Switzerland' },
  { name: 'Neuchâtel', country: 'Switzerland' },
  { name: 'Uster', country: 'Switzerland' },
  { name: 'Sion', country: 'Switzerland' },
  { name: 'Emmen', country: 'Switzerland' },
  { name: 'Zug', country: 'Switzerland' },
  { name: 'Yverdon-les-Bains', country: 'Switzerland' },
  { name: 'Dübendorf', country: 'Switzerland' },
  { name: 'Dietikon', country: 'Switzerland' },
  { name: 'Montreux', country: 'Switzerland' },
  { name: 'Frauenfeld', country: 'Switzerland' },
  { name: 'Wetzikon', country: 'Switzerland' },
  { name: 'Baar', country: 'Switzerland' },
  { name: 'Meyrin', country: 'Switzerland' },
  { name: 'Riehen', country: 'Switzerland' },
  { name: 'Carouge', country: 'Switzerland' },
  { name: 'Wädenswil', country: 'Switzerland' },
  { name: 'Allschwil', country: 'Switzerland' },
  { name: 'Renens', country: 'Switzerland' },
  { name: 'Kreuzlingen', country: 'Switzerland' },
  { name: 'Nyon', country: 'Switzerland' },
  { name: 'Aarau', country: 'Switzerland' },
  { name: 'Solothurn', country: 'Switzerland' },
  { name: 'Vevey', country: 'Switzerland' },
  { name: 'Kloten', country: 'Switzerland' },
  { name: 'Pully', country: 'Switzerland' },
  { name: 'Opfikon', country: 'Switzerland' },
  { name: 'Volketswil', country: 'Switzerland' },
  { name: 'Oftringen', country: 'Switzerland' },
  { name: 'Kriens', country: 'Switzerland' },
  { name: 'Pratteln', country: 'Switzerland' },
  { name: 'Horgen', country: 'Switzerland' },
  { name: 'Morges', country: 'Switzerland' },
  { name: 'Olten', country: 'Switzerland' },
  { name: 'Freienbach', country: 'Switzerland' },
  { name: 'Thalwil', country: 'Switzerland' },
  { name: 'Binningen', country: 'Switzerland' },
  { name: 'Adliswil', country: 'Switzerland' },
  { name: 'Steffisburg', country: 'Switzerland' },
  { name: 'Bellinzona', country: 'Switzerland' },
  { name: 'Liestal', country: 'Switzerland' },
  { name: 'Herisau', country: 'Switzerland' },
  { name: 'Reinach', country: 'Switzerland' },
  { name: 'Ostermundigen', country: 'Switzerland' },
  { name: 'Illnau-Effretikon', country: 'Switzerland' },
  { name: 'Monthey', country: 'Switzerland' },
  { name: 'Münsingen', country: 'Switzerland' },
  { name: 'Burgdorf', country: 'Switzerland' },
  { name: 'Muttenz', country: 'Switzerland' },
  { name: 'Langenthal', country: 'Switzerland' },
  { name: 'Weinfelden', country: 'Switzerland' },
  { name: 'Sierre', country: 'Switzerland' },
  { name: 'Grenchen', country: 'Switzerland' },
  { name: 'Rorschach', country: 'Switzerland' },
  { name: 'Arbon', country: 'Switzerland' },
  { name: 'Martigny', country: 'Switzerland' },
  { name: 'Cham', country: 'Switzerland' },
  { name: 'La Tour-de-Peilz', country: 'Switzerland' },
  { name: 'Gland', country: 'Switzerland' },
  { name: 'Locarno', country: 'Switzerland' },
  { name: 'Lancy', country: 'Switzerland' },
  { name: 'Einsiedeln', country: 'Switzerland' },
  { name: 'Richterswil', country: 'Switzerland' },
  { name: 'Schwyz', country: 'Switzerland' },
  { name: 'Gossau', country: 'Switzerland' },
  { name: 'Versoix', country: 'Switzerland' },
  { name: 'Amriswil', country: 'Switzerland' },
  { name: 'Onex', country: 'Switzerland' },
  { name: 'Wohlen', country: 'Switzerland' },
  { name: 'Glarus', country: 'Switzerland' },
  { name: 'Appenzell', country: 'Switzerland' },
  { name: 'Grindelwald', country: 'Switzerland' },
  { name: 'Interlaken', country: 'Switzerland' },
  { name: 'Jungfraujoch', country: 'Switzerland' },
  { name: 'Matterhorn', country: 'Switzerland' },
  { name: 'Zermatt', country: 'Switzerland' },
  { name: 'Saas-Fee', country: 'Switzerland' },
  { name: 'Verbier', country: 'Switzerland' },
  { name: 'Crans-Montana', country: 'Switzerland' },
  { name: 'Davos', country: 'Switzerland' },
  { name: 'Klosters', country: 'Switzerland' },
  { name: 'St. Moritz', country: 'Switzerland' },
  { name: 'Pontresina', country: 'Switzerland' },
  { name: 'Flims', country: 'Switzerland' },
  { name: 'Laax', country: 'Switzerland' },
  { name: 'Andermatt', country: 'Switzerland' },
  { name: 'Engelberg', country: 'Switzerland' },
  { name: 'Wengen', country: 'Switzerland' },
  { name: 'Mürren', country: 'Switzerland' },
  { name: 'Gimmelwald', country: 'Switzerland' },
  { name: 'Lauterbrunnen', country: 'Switzerland' },
  { name: 'Kandersteg', country: 'Switzerland' },
  { name: 'Gstaad', country: 'Switzerland' },
  { name: 'Saanen', country: 'Switzerland' },
  { name: 'Château-d\'Œx', country: 'Switzerland' },
  { name: 'Villars-sur-Ollon', country: 'Switzerland' },
  { name: 'Leysin', country: 'Switzerland' },
  { name: 'Les Diablerets', country: 'Switzerland' },
  { name: 'Anzère', country: 'Switzerland' },
  { name: 'Nendaz', country: 'Switzerland' },
  { name: 'Haute-Nendaz', country: 'Switzerland' },
  { name: 'Veysonnaz', country: 'Switzerland' },
  { name: 'Thyon', country: 'Switzerland' },
  { name: 'La Tzoumaz', country: 'Switzerland' },
  { name: 'Mayens-de-Riddes', country: 'Switzerland' },
  { name: 'Ovronnaz', country: 'Switzerland' },
  { name: 'Champéry', country: 'Switzerland' },
  { name: 'Morgins', country: 'Switzerland' },
  { name: 'Champoussin', country: 'Switzerland' },
  { name: 'Les Crosets', country: 'Switzerland' },
  { name: 'Avoriaz', country: 'Switzerland' },
  { name: 'Morzine', country: 'Switzerland' },
  { name: 'Les Gets', country: 'Switzerland' },
  { name: 'Châtel', country: 'Switzerland' },
  { name: 'Abondance', country: 'Switzerland' },
  { name: 'La Chapelle-d\'Abondance', country: 'Switzerland' },
  { name: 'Torgon', country: 'Switzerland' },
  { name: 'Chatel', country: 'Switzerland' },
  { name: 'Portes du Soleil', country: 'Switzerland' },
  { name: 'Emosson', country: 'Switzerland' },
  { name: 'Trient', country: 'Switzerland' },
  { name: 'Salvan', country: 'Switzerland' },
  { name: 'Finhaut', country: 'Switzerland' },
  { name: 'Vernayaz', country: 'Switzerland' },
  { name: 'Dorénaz', country: 'Switzerland' },
  { name: 'Collonges', country: 'Switzerland' },
  { name: 'Vouvry', country: 'Switzerland' },
  { name: 'Bouveret', country: 'Switzerland' },
  { name: 'Saint-Gingolph', country: 'Switzerland' },
  { name: 'Evian-les-Bains', country: 'Switzerland' },
  { name: 'Thonon-les-Bains', country: 'Switzerland' },
  { name: 'Yvoire', country: 'Switzerland' },
  { name: 'Excenevex', country: 'Switzerland' },
  { name: 'Sciez', country: 'Switzerland' },
  { name: 'Douvaine', country: 'Switzerland' },
  { name: 'Bons-en-Chablais', country: 'Switzerland' },
  { name: 'Machilly', country: 'Switzerland' },
  { name: 'Lully', country: 'Switzerland' },
  { name: 'Chens-sur-Léman', country: 'Switzerland' },
  { name: 'Nernier', country: 'Switzerland' },
  { name: 'Messery', country: 'Switzerland' },
  { name: 'Anthy-sur-Léman', country: 'Switzerland' },
  { name: 'Ballaison', country: 'Switzerland' },
  { name: 'Perrignier', country: 'Switzerland' },
  { name: 'Allinges', country: 'Switzerland' },
  { name: 'Draillant', country: 'Switzerland' },
  { name: 'Féternes', country: 'Switzerland' },
  { name: 'Lullin', country: 'Switzerland' },
  { name: 'Reyvroz', country: 'Switzerland' },
  { name: 'Vailly', country: 'Switzerland' },
  { name: 'Seytroux', country: 'Switzerland' },
  { name: 'Saint-Jean-d\'Aulps', country: 'Switzerland' },
  { name: 'Bellevaux', country: 'Switzerland' },
  { name: 'Habère-Lullin', country: 'Switzerland' },
  { name: 'Habère-Poche', country: 'Switzerland' },
  { name: 'Villard', country: 'Switzerland' },
  { name: 'Bogève', country: 'Switzerland' },
  { name: 'Onnion', country: 'Switzerland' },
  { name: 'Mégevette', country: 'Switzerland' },
  { name: 'La Côte-d\'Arbroz', country: 'Switzerland' },
  { name: 'Mieussy', country: 'Switzerland' },
  { name: 'Taninges', country: 'Switzerland' },
  { name: 'Cluses', country: 'Switzerland' },
  { name: 'Thyez', country: 'Switzerland' },
  { name: 'Marignier', country: 'Switzerland' },
  { name: 'Ayse', country: 'Switzerland' },
  { name: 'Bonneville', country: 'Switzerland' },
  { name: 'Vougy', country: 'Switzerland' },
  { name: 'Faucigny', country: 'Switzerland' },
  { name: 'Contamine-sur-Arve', country: 'Switzerland' },
  { name: 'Arthaz-Pont-Notre-Dame', country: 'Switzerland' },
  { name: 'Fillinges', country: 'Switzerland' },
  { name: 'Bonne', country: 'Switzerland' },
  { name: 'Lucinges', country: 'Switzerland' },
  { name: 'Cranves-Sales', country: 'Switzerland' },
  { name: 'Reignier', country: 'Switzerland' },
  { name: 'Pers-Jussy', country: 'Switzerland' },
  { name: 'Arenthon', country: 'Switzerland' },
  { name: 'Etaux', country: 'Switzerland' },
  { name: 'Amancy', country: 'Switzerland' },
  { name: 'La Roche-sur-Foron', country: 'Switzerland' },
  { name: 'Cornier', country: 'Switzerland' },
  { name: 'Saint-Laurent', country: 'Switzerland' },
  { name: 'Peillonnex', country: 'Switzerland' },
  { name: 'Marcellaz-Albanais', country: 'Switzerland' },
  { name: 'Groisy', country: 'Switzerland' },
  { name: 'Thorens-Glières', country: 'Switzerland' },
  { name: 'Aviernoz', country: 'Switzerland' },
  { name: 'Entremont', country: 'Switzerland' },
  { name: 'Villaz', country: 'Switzerland' },
  { name: 'Copponex', country: 'Switzerland' },
  { name: 'Scientrier', country: 'Switzerland' },
  { name: 'Évires', country: 'Switzerland' },
  { name: 'Usillon', country: 'Switzerland' },
  { name: 'Charvonnex', country: 'Switzerland' },
  { name: 'Sallenôves', country: 'Switzerland' },
  { name: 'Cercier', country: 'Switzerland' },
  { name: 'Lornay', country: 'Switzerland' },
  { name: 'Nonglard', country: 'Switzerland' },
  { name: 'Saint-Martin-Bellevue', country: 'Switzerland' },
  { name: 'Epagny', country: 'Switzerland' },
  { name: 'Metz-Tessy', country: 'Switzerland' },
  { name: 'Poisy', country: 'Switzerland' },
  { name: 'Chavanod', country: 'Switzerland' },
  { name: 'Lovagny', country: 'Switzerland' },
  { name: 'Argonay', country: 'Switzerland' },
  { name: 'La Balme-de-Sillingy', country: 'Switzerland' },
  { name: 'Sillingy', country: 'Switzerland' },
  { name: 'Choisy', country: 'Switzerland' },
  { name: 'Rumilly', country: 'Switzerland' },
  { name: 'Massingy', country: 'Switzerland' },
  { name: 'Bloye', country: 'Switzerland' },
  { name: 'Boussy', country: 'Switzerland' },
  { name: 'Héry-sur-Alby', country: 'Switzerland' },
  { name: 'Alby-sur-Chéran', country: 'Switzerland' },
  { name: 'Cusy', country: 'Switzerland' },
  { name: 'Gruffy', country: 'Switzerland' },
  { name: 'Viuz-la-Chiésaz', country: 'Switzerland' },
  { name: 'Lescheraines', country: 'Switzerland' },
  { name: 'École', country: 'Switzerland' },
  { name: 'La Compôte', country: 'Switzerland' },
  { name: 'Jarsy', country: 'Switzerland' },
  { name: 'Doucy-en-Bauges', country: 'Switzerland' },
  { name: 'Le Châtelard', country: 'Switzerland' },
  { name: 'Sainte-Reine', country: 'Switzerland' },
  { name: 'Arith', country: 'Switzerland' },
  { name: 'Lescheraines', country: 'Switzerland' },
  { name: 'École', country: 'Switzerland' },
  { name: 'La Compôte', country: 'Switzerland' },
  { name: 'Jarsy', country: 'Switzerland' },
  { name: 'Doucy-en-Bauges', country: 'Switzerland' },
  { name: 'Le Châtelard', country: 'Switzerland' },
  { name: 'Sainte-Reine', country: 'Switzerland' },
  { name: 'Arith', country: 'Switzerland' },

  // SOUTH AMERICA - Comprehensive coverage
  // Brazil
  { name: 'São Paulo', country: 'Brazil' },
  { name: 'Rio de Janeiro', country: 'Brazil' },
  { name: 'Brasília', country: 'Brazil' },
  { name: 'Salvador', country: 'Brazil' },
  { name: 'Fortaleza', country: 'Brazil' },
  { name: 'Belo Horizonte', country: 'Brazil' },
  { name: 'Manaus', country: 'Brazil' },
  { name: 'Curitiba', country: 'Brazil' },
  { name: 'Recife', country: 'Brazil' },
  { name: 'Porto Alegre', country: 'Brazil' },
  { name: 'Belém', country: 'Brazil' },
  { name: 'Goiânia', country: 'Brazil' },
  { name: 'Guarulhos', country: 'Brazil' },
  { name: 'Campinas', country: 'Brazil' },
  { name: 'São Luís', country: 'Brazil' },
  { name: 'São Gonçalo', country: 'Brazil' },
  { name: 'Maceió', country: 'Brazil' },
  { name: 'Duque de Caxias', country: 'Brazil' },
  { name: 'Natal', country: 'Brazil' },
  { name: 'Teresina', country: 'Brazil' },
  { name: 'Campo Grande', country: 'Brazil' },
  { name: 'Nova Iguaçu', country: 'Brazil' },
  { name: 'São Bernardo do Campo', country: 'Brazil' },
  { name: 'João Pessoa', country: 'Brazil' },
  { name: 'Santo André', country: 'Brazil' },
  { name: 'Osasco', country: 'Brazil' },
  { name: 'Jaboatão dos Guararapes', country: 'Brazil' },
  { name: 'Contagem', country: 'Brazil' },
  { name: 'Ribeirão Preto', country: 'Brazil' },
  { name: 'Uberlândia', country: 'Brazil' },
  { name: 'Sorocaba', country: 'Brazil' },
  { name: 'Aracaju', country: 'Brazil' },
  { name: 'Feira de Santana', country: 'Brazil' },
  { name: 'Cuiabá', country: 'Brazil' },
  { name: 'Joinville', country: 'Brazil' },
  { name: 'Juiz de Fora', country: 'Brazil' },
  { name: 'Londrina', country: 'Brazil' },
  { name: 'Aparecida de Goiânia', country: 'Brazil' },
  { name: 'Porto Velho', country: 'Brazil' },
  { name: 'Ananindeua', country: 'Brazil' },
  { name: 'Florianópolis', country: 'Brazil' },
  { name: 'Serra', country: 'Brazil' },
  { name: 'Niterói', country: 'Brazil' },
  { name: 'Caxias do Sul', country: 'Brazil' },
  { name: 'Campos dos Goytacazes', country: 'Brazil' },
  { name: 'Macapá', country: 'Brazil' },
  { name: 'Vila Velha', country: 'Brazil' },
  { name: 'Mauá', country: 'Brazil' },
  { name: 'São José dos Campos', country: 'Brazil' },
  { name: 'Iguazu Falls', country: 'Brazil' },
  { name: 'Fernando de Noronha', country: 'Brazil' },
  { name: 'Paraty', country: 'Brazil' },
  { name: 'Búzios', country: 'Brazil' },
  { name: 'Ilha Grande', country: 'Brazil' },
  { name: 'Ouro Preto', country: 'Brazil' },
  { name: 'Bonito', country: 'Brazil' },
  { name: 'Jericoacoara', country: 'Brazil' },
  { name: 'Lençóis Maranhenses', country: 'Brazil' },
  { name: 'Pantanal', country: 'Brazil' },
  { name: 'Amazon Rainforest', country: 'Brazil' },
  { name: 'Chapada Diamantina', country: 'Brazil' },
  { name: 'Chapada dos Veadeiros', country: 'Brazil' },
  { name: 'Jalapão', country: 'Brazil' },
  { name: 'Trancoso', country: 'Brazil' },
  { name: 'Arraial d\'Ajuda', country: 'Brazil' },
  { name: 'Porto de Galinhas', country: 'Brazil' },
  { name: 'Morro de São Paulo', country: 'Brazil' },
  { name: 'Alter do Chão', country: 'Brazil' },
  { name: 'Pipa Beach', country: 'Brazil' },

  // Argentina
  { name: 'Buenos Aires', country: 'Argentina' },
  { name: 'Córdoba', country: 'Argentina' },
  { name: 'Rosario', country: 'Argentina' },
  { name: 'Mendoza', country: 'Argentina' },
  { name: 'San Miguel de Tucumán', country: 'Argentina' },
  { name: 'La Plata', country: 'Argentina' },
  { name: 'Mar del Plata', country: 'Argentina' },
  { name: 'Salta', country: 'Argentina' },
  { name: 'San Juan', country: 'Argentina' },
  { name: 'Santa Fe', country: 'Argentina' },
  { name: 'Resistencia', country: 'Argentina' },
  { name: 'Corrientes', country: 'Argentina' },
  { name: 'Santiago del Estero', country: 'Argentina' },
  { name: 'San Salvador de Jujuy', country: 'Argentina' },
  { name: 'Bahía Blanca', country: 'Argentina' },
  { name: 'Paraná', country: 'Argentina' },
  { name: 'Posadas', country: 'Argentina' },
  { name: 'Neuquén', country: 'Argentina' },
  { name: 'Formosa', country: 'Argentina' },
  { name: 'San Luis', country: 'Argentina' },
  { name: 'Bariloche', country: 'Argentina' },
  { name: 'Ushuaia', country: 'Argentina' },
  { name: 'El Calafate', country: 'Argentina' },
  { name: 'El Chaltén', country: 'Argentina' },
  { name: 'Puerto Madryn', country: 'Argentina' },
  { name: 'Iguazu Falls', country: 'Argentina' },
  { name: 'Península Valdés', country: 'Argentina' },
  { name: 'Perito Moreno Glacier', country: 'Argentina' },
  { name: 'Valdes Peninsula', country: 'Argentina' },
  { name: 'Patagonia', country: 'Argentina' },
  { name: 'Torres del Paine', country: 'Argentina' },
  { name: 'Tierra del Fuego', country: 'Argentina' },
  { name: 'Quebrada de Humahuaca', country: 'Argentina' },
  { name: 'Purmamarca', country: 'Argentina' },
  { name: 'Tilcara', country: 'Argentina' },
  { name: 'Cafayate', country: 'Argentina' },
  { name: 'Cachi', country: 'Argentina' },
  { name: 'Tafí del Valle', country: 'Argentina' },
  { name: 'Villa General Belgrano', country: 'Argentina' },
  { name: 'Villa La Angostura', country: 'Argentina' },
  { name: 'San Martín de los Andes', country: 'Argentina' },
  { name: 'Colonia del Sacramento', country: 'Argentina' },

  // Chile
  { name: 'Santiago', country: 'Chile' },
  { name: 'Valparaíso', country: 'Chile' },
  { name: 'Concepción', country: 'Chile' },
  { name: 'La Serena', country: 'Chile' },
  { name: 'Antofagasta', country: 'Chile' },
  { name: 'Temuco', country: 'Chile' },
  { name: 'Rancagua', country: 'Chile' },
  { name: 'Talca', country: 'Chile' },
  { name: 'Arica', country: 'Chile' },
  { name: 'Chillán', country: 'Chile' },
  { name: 'Iquique', country: 'Chile' },
  { name: 'Puerto Montt', country: 'Chile' },
  { name: 'Coquimbo', country: 'Chile' },
  { name: 'Osorno', country: 'Chile' },
  { name: 'Valdivia', country: 'Chile' },
  { name: 'Punta Arenas', country: 'Chile' },
  { name: 'Copiapó', country: 'Chile' },
  { name: 'Calama', country: 'Chile' },
  { name: 'Los Ángeles', country: 'Chile' },
  { name: 'San Pedro de Atacama', country: 'Chile' },
  { name: 'Torres del Paine', country: 'Chile' },
  { name: 'Atacama Desert', country: 'Chile' },
  { name: 'Easter Island', country: 'Chile' },
  { name: 'Patagonia', country: 'Chile' },
  { name: 'Chiloé Island', country: 'Chile' },
  { name: 'Valle de la Luna', country: 'Chile' },
  { name: 'El Tatio Geysers', country: 'Chile' },
  { name: 'Marble Caves', country: 'Chile' },
  { name: 'Lauca National Park', country: 'Chile' },
  { name: 'Vicente Pérez Rosales', country: 'Chile' },
  { name: 'Pucón', country: 'Chile' },
  { name: 'Viña del Mar', country: 'Chile' },
  { name: 'Casablanca Valley', country: 'Chile' },
  { name: 'Colchagua Valley', country: 'Chile' },
  { name: 'Maipo Valley', country: 'Chile' },

  // Peru
  { name: 'Lima', country: 'Peru' },
  { name: 'Arequipa', country: 'Peru' },
  { name: 'Trujillo', country: 'Peru' },
  { name: 'Chiclayo', country: 'Peru' },
  { name: 'Piura', country: 'Peru' },
  { name: 'Iquitos', country: 'Peru' },
  { name: 'Cusco', country: 'Peru' },
  { name: 'Huancayo', country: 'Peru' },
  { name: 'Chimbote', country: 'Peru' },
  { name: 'Pucallpa', country: 'Peru' },
  { name: 'Tacna', country: 'Peru' },
  { name: 'Ica', country: 'Peru' },
  { name: 'Juliaca', country: 'Peru' },
  { name: 'Ayacucho', country: 'Peru' },
  { name: 'Cajamarca', country: 'Peru' },
  { name: 'Puno', country: 'Peru' },
  { name: 'Sullana', country: 'Peru' },
  { name: 'Chincha Alta', country: 'Peru' },
  { name: 'Huánuco', country: 'Peru' },
  { name: 'Tarapoto', country: 'Peru' },
  { name: 'Machu Picchu', country: 'Peru' },
  { name: 'Sacred Valley', country: 'Peru' },
  { name: 'Lake Titicaca', country: 'Peru' },
  { name: 'Nazca Lines', country: 'Peru' },
  { name: 'Colca Canyon', country: 'Peru' },
  { name: 'Amazon Rainforest', country: 'Peru' },
  { name: 'Huacachina', country: 'Peru' },
  { name: 'Paracas', country: 'Peru' },
  { name: 'Máncora', country: 'Peru' },
  { name: 'Chan Chan', country: 'Peru' },
  { name: 'Kuelap', country: 'Peru' },
  { name: 'Choquequirao', country: 'Peru' },
  { name: 'Huaraz', country: 'Peru' },
  { name: 'Cordillera Blanca', country: 'Peru' },
  { name: 'Rainbow Mountain', country: 'Peru' },
  { name: 'Tambopata', country: 'Peru' },
  { name: 'Manu National Park', country: 'Peru' },
  { name: 'Pacaya-Samiria', country: 'Peru' },

  // Colombia
  { name: 'Bogotá', country: 'Colombia' },
  { name: 'Medellín', country: 'Colombia' },
  { name: 'Cali', country: 'Colombia' },
  { name: 'Barranquilla', country: 'Colombia' },
  { name: 'Cartagena', country: 'Colombia' },
  { name: 'Cúcuta', country: 'Colombia' },
  { name: 'Bucaramanga', country: 'Colombia' },
  { name: 'Pereira', country: 'Colombia' },
  { name: 'Santa Marta', country: 'Colombia' },
  { name: 'Ibagué', country: 'Colombia' },
  { name: 'Pasto', country: 'Colombia' },
  { name: 'Manizales', country: 'Colombia' },
  { name: 'Neiva', country: 'Colombia' },
  { name: 'Villavicencio', country: 'Colombia' },
  { name: 'Armenia', country: 'Colombia' },
  { name: 'Valledupar', country: 'Colombia' },
  { name: 'Montería', country: 'Colombia' },
  { name: 'San Andrés', country: 'Colombia' },
  { name: 'Providencia', country: 'Colombia' },
  { name: 'Tayrona National Park', country: 'Colombia' },
  { name: 'Caño Cristales', country: 'Colombia' },
  { name: 'Coffee Region', country: 'Colombia' },
  { name: 'Salento', country: 'Colombia' },
  { name: 'Valle de Cocora', country: 'Colombia' },
  { name: 'Guatapé', country: 'Colombia' },
  { name: 'Villa de Leyva', country: 'Colombia' },
  { name: 'Barichara', country: 'Colombia' },
  { name: 'Tatacoa Desert', country: 'Colombia' },
  { name: 'Amazon Rainforest', country: 'Colombia' },
  { name: 'Leticia', country: 'Colombia' },
  { name: 'Los Llanos', country: 'Colombia' },
  { name: 'San Agustín', country: 'Colombia' },
  { name: 'Popayán', country: 'Colombia' },

  // Ecuador
  { name: 'Quito', country: 'Ecuador' },
  { name: 'Guayaquil', country: 'Ecuador' },
  { name: 'Cuenca', country: 'Ecuador' },
  { name: 'Santo Domingo', country: 'Ecuador' },
  { name: 'Machala', country: 'Ecuador' },
  { name: 'Manta', country: 'Ecuador' },
  { name: 'Portoviejo', country: 'Ecuador' },
  { name: 'Loja', country: 'Ecuador' },
  { name: 'Ambato', country: 'Ecuador' },
  { name: 'Esmeraldas', country: 'Ecuador' },
  { name: 'Riobamba', country: 'Ecuador' },
  { name: 'Quevedo', country: 'Ecuador' },
  { name: 'Ibarra', country: 'Ecuador' },
  { name: 'Galápagos Islands', country: 'Ecuador' },
  { name: 'Baños', country: 'Ecuador' },
  { name: 'Otavalo', country: 'Ecuador' },
  { name: 'Montañita', country: 'Ecuador' },
  { name: 'Cotopaxi', country: 'Ecuador' },
  { name: 'Chimborazo', country: 'Ecuador' },
  { name: 'Amazon Rainforest', country: 'Ecuador' },
  { name: 'Mindo Cloud Forest', country: 'Ecuador' },
  { name: 'Quilotoa', country: 'Ecuador' },
  { name: 'Salinas', country: 'Ecuador' },
  { name: 'Papallacta Hot Springs', country: 'Ecuador' },

  // Bolivia
  { name: 'La Paz', country: 'Bolivia' },
  { name: 'Santa Cruz de la Sierra', country: 'Bolivia' },
  { name: 'Cochabamba', country: 'Bolivia' },
  { name: 'Sucre', country: 'Bolivia' },
  { name: 'Oruro', country: 'Bolivia' },
  { name: 'Tarija', country: 'Bolivia' },
  { name: 'Potosí', country: 'Bolivia' },
  { name: 'Uyuni', country: 'Bolivia' },
  { name: 'Salar de Uyuni', country: 'Bolivia' },
  { name: 'Lake Titicaca', country: 'Bolivia' },
  { name: 'Copacabana', country: 'Bolivia' },
  { name: 'Isla del Sol', country: 'Bolivia' },
  { name: 'Rurrenabaque', country: 'Bolivia' },
  { name: 'Tiwanaku', country: 'Bolivia' },
  { name: 'Madidi National Park', country: 'Bolivia' },
  { name: 'Eduardo Avaroa Reserve', country: 'Bolivia' },
  { name: 'Samaipata', country: 'Bolivia' },
  { name: 'Toro Toro', country: 'Bolivia' },
  { name: 'Laguna Colorada', country: 'Bolivia' },

  // Venezuela
  { name: 'Caracas', country: 'Venezuela' },
  { name: 'Maracaibo', country: 'Venezuela' },
  { name: 'Valencia', country: 'Venezuela' },
  { name: 'Barquisimeto', country: 'Venezuela' },
  { name: 'Maracay', country: 'Venezuela' },
  { name: 'Ciudad Guayana', country: 'Venezuela' },
  { name: 'Maturín', country: 'Venezuela' },
  { name: 'Barcelona', country: 'Venezuela' },
  { name: 'Puerto La Cruz', country: 'Venezuela' },
  { name: 'Los Teques', country: 'Venezuela' },
  { name: 'Mérida', country: 'Venezuela' },
  { name: 'Santa Teresa', country: 'Venezuela' },
  { name: 'Angel Falls', country: 'Venezuela' },
  { name: 'Los Roques', country: 'Venezuela' },
  { name: 'Canaima National Park', country: 'Venezuela' },
  { name: 'Isla Margarita', country: 'Venezuela' },
  { name: 'Morrocoy National Park', country: 'Venezuela' },
  { name: 'Gran Sabana', country: 'Venezuela' },
  { name: 'Roraima Tepui', country: 'Venezuela' },

  // Paraguay
  { name: 'Asunción', country: 'Paraguay' },
  { name: 'Ciudad del Este', country: 'Paraguay' },
  { name: 'San Lorenzo', country: 'Paraguay' },
  { name: 'Capiatá', country: 'Paraguay' },
  { name: 'Lambaré', country: 'Paraguay' },
  { name: 'Fernando de la Mora', country: 'Paraguay' },
  { name: 'Luque', country: 'Paraguay' },
  { name: 'Encarnación', country: 'Paraguay' },
  { name: 'Itaguá', country: 'Paraguay' },
  { name: 'Ñemby', country: 'Paraguay' },
  { name: 'Chaco', country: 'Paraguay' },
  { name: 'Jesuit Missions', country: 'Paraguay' },

  // Uruguay
  { name: 'Montevideo', country: 'Uruguay' },
  { name: 'Salto', country: 'Uruguay' },
  { name: 'Paysandú', country: 'Uruguay' },
  { name: 'Las Piedras', country: 'Uruguay' },
  { name: 'Rivera', country: 'Uruguay' },
  { name: 'Maldonado', country: 'Uruguay' },
  { name: 'Tacuarembó', country: 'Uruguay' },
  { name: 'Melo', country: 'Uruguay' },
  { name: 'Mercedes', country: 'Uruguay' },
  { name: 'Artigas', country: 'Uruguay' },
  { name: 'Punta del Este', country: 'Uruguay' },
  { name: 'Colonia del Sacramento', country: 'Uruguay' },
  { name: 'La Paloma', country: 'Uruguay' },
  { name: 'Piriápolis', country: 'Uruguay' },
  { name: 'Cabo Polonio', country: 'Uruguay' },
  { name: 'Carmelo', country: 'Uruguay' },
  { name: 'José Ignacio', country: 'Uruguay' },

  // Guyana, Suriname, French Guiana
  { name: 'Georgetown', country: 'Guyana' },
  { name: 'Linden', country: 'Guyana' },
  { name: 'New Amsterdam', country: 'Guyana' },
  { name: 'Kaieteur Falls', country: 'Guyana' },
  { name: 'Iwokrama Forest', country: 'Guyana' },

  { name: 'Paramaribo', country: 'Suriname' },
  { name: 'Lelydorp', country: 'Suriname' },
  { name: 'Nieuw Nickerie', country: 'Suriname' },
  { name: 'Brownsberg Nature Park', country: 'Suriname' },

  { name: 'Cayenne', country: 'French Guiana' },
  { name: 'Saint-Laurent-du-Maroni', country: 'French Guiana' },
  { name: 'Kourou', country: 'French Guiana' },
  { name: 'Îles du Salut', country: 'French Guiana' },

  // CENTRAL AMERICA
  // Mexico
  { name: 'Mexico City', country: 'Mexico' },
  { name: 'Guadalajara', country: 'Mexico' },
  { name: 'Monterrey', country: 'Mexico' },
  { name: 'Puebla', country: 'Mexico' },
  { name: 'Tijuana', country: 'Mexico' },
  { name: 'León', country: 'Mexico' },
  { name: 'Juárez', country: 'Mexico' },
  { name: 'Torreón', country: 'Mexico' },
  { name: 'Querétaro', country: 'Mexico' },
  { name: 'San Luis Potosí', country: 'Mexico' },
  { name: 'Mérida', country: 'Mexico' },
  { name: 'Mexicali', country: 'Mexico' },
  { name: 'Aguascalientes', country: 'Mexico' },
  { name: 'Hermosillo', country: 'Mexico' },
  { name: 'Saltillo', country: 'Mexico' },
  { name: 'Cancún', country: 'Mexico' },
  { name: 'Culiacán', country: 'Mexico' },
  { name: 'Acapulco', country: 'Mexico' },
  { name: 'Chihuahua', country: 'Mexico' },
  { name: 'Playa del Carmen', country: 'Mexico' },
  { name: 'Tulum', country: 'Mexico' },
  { name: 'Cozumel', country: 'Mexico' },
  { name: 'Isla Mujeres', country: 'Mexico' },
  { name: 'Puerto Vallarta', country: 'Mexico' },
  { name: 'Cabo San Lucas', country: 'Mexico' },
  { name: 'San Miguel de Allende', country: 'Mexico' },
  { name: 'Guanajuato', country: 'Mexico' },
  { name: 'Oaxaca', country: 'Mexico' },
  { name: 'San Cristóbal de las Casas', country: 'Mexico' },
  { name: 'Chichén Itzá', country: 'Mexico' },
  { name: 'Palenque', country: 'Mexico' },
  { name: 'Uxmal', country: 'Mexico' },
  { name: 'Tulum Ruins', country: 'Mexico' },
  { name: 'Copper Canyon', country: 'Mexico' },
  { name: 'Monarch Butterfly Reserve', country: 'Mexico' },
  { name: 'Teotihuacán', country: 'Mexico' },
  { name: 'Cenote Dos Ojos', country: 'Mexico' },
  { name: 'Bacalar Lagoon', country: 'Mexico' },
  { name: 'Sayulita', country: 'Mexico' },
  { name: 'Mazatlán', country: 'Mexico' },
  { name: 'Manzanillo', country: 'Mexico' },
  { name: 'Huatulco', country: 'Mexico' },
  { name: 'Zipolite', country: 'Mexico' },
  { name: 'Puerto Escondido', country: 'Mexico' },
  { name: 'Taxco', country: 'Mexico' },
  { name: 'Cuernavaca', country: 'Mexico' },
  { name: 'Valle de Bravo', country: 'Mexico' },
  { name: 'Morelia', country: 'Mexico' },
  { name: 'Zacatecas', country: 'Mexico' },

  // Guatemala
  { name: 'Guatemala City', country: 'Guatemala' },
  { name: 'Antigua Guatemala', country: 'Guatemala' },
  { name: 'Quetzaltenango', country: 'Guatemala' },
  { name: 'Escuintla', country: 'Guatemala' },
  { name: 'Mixco', country: 'Guatemala' },
  { name: 'Villa Nueva', country: 'Guatemala' },
  { name: 'Petapa', country: 'Guatemala' },
  { name: 'San Juan Sacatepéquez', country: 'Guatemala' },
  { name: 'Lake Atitlán', country: 'Guatemala' },
  { name: 'Tikal', country: 'Guatemala' },
  { name: 'Flores', country: 'Guatemala' },
  { name: 'Panajachel', country: 'Guatemala' },
  { name: 'Chichicastenango', country: 'Guatemala' },
  { name: 'Livingston', country: 'Guatemala' },
  { name: 'Río Dulce', country: 'Guatemala' },
  { name: 'Semuc Champey', country: 'Guatemala' },
  { name: 'Monterrico', country: 'Guatemala' },
  { name: 'Cobán', country: 'Guatemala' },

  // Belize
  { name: 'Belize City', country: 'Belize' },
  { name: 'San Pedro', country: 'Belize' },
  { name: 'Belmopan', country: 'Belize' },
  { name: 'Orange Walk Town', country: 'Belize' },
  { name: 'Dangriga', country: 'Belize' },
  { name: 'Corozal Town', country: 'Belize' },
  { name: 'San Ignacio', country: 'Belize' },
  { name: 'Caye Caulker', country: 'Belize' },
  { name: 'Placencia', country: 'Belize' },
  { name: 'Great Blue Hole', country: 'Belize' },
  { name: 'Xunantunich', country: 'Belize' },
  { name: 'Caracol', country: 'Belize' },
  { name: 'Ambergris Caye', country: 'Belize' },
  { name: 'Hol Chan Marine Reserve', country: 'Belize' },
  { name: 'Lamanai', country: 'Belize' },

  // Honduras
  { name: 'Tegucigalpa', country: 'Honduras' },
  { name: 'San Pedro Sula', country: 'Honduras' },
  { name: 'Choloma', country: 'Honduras' },
  { name: 'La Ceiba', country: 'Honduras' },
  { name: 'El Progreso', country: 'Honduras' },
  { name: 'Choluteca', country: 'Honduras' },
  { name: 'Comayagua', country: 'Honduras' },
  { name: 'Roatán', country: 'Honduras' },
  { name: 'Utila', country: 'Honduras' },
  { name: 'Guanaja', country: 'Honduras' },
  { name: 'Copán Ruinas', country: 'Honduras' },
  { name: 'Tela', country: 'Honduras' },
  { name: 'Trujillo', country: 'Honduras' },
  { name: 'Pico Bonito National Park', country: 'Honduras' },
  { name: 'Lake Yojoa', country: 'Honduras' },

  // El Salvador
  { name: 'San Salvador', country: 'El Salvador' },
  { name: 'Santa Ana', country: 'El Salvador' },
  { name: 'San Miguel', country: 'El Salvador' },
  { name: 'Soyapango', country: 'El Salvador' },
  { name: 'Mejicanos', country: 'El Salvador' },
  { name: 'Santa Tecla', country: 'El Salvador' },
  { name: 'Apopa', country: 'El Salvador' },
  { name: 'Delgado', country: 'El Salvador' },
  { name: 'El Tunco', country: 'El Salvador' },
  { name: 'El Sunzal', country: 'El Salvador' },
  { name: 'Suchitoto', country: 'El Salvador' },
  { name: 'Joya de Cerén', country: 'El Salvador' },
  { name: 'Lake Coatepeque', country: 'El Salvador' },
  { name: 'Tazumal', country: 'El Salvador' },
  { name: 'Santa Ana Volcano', country: 'El Salvador' },

  // Nicaragua
  { name: 'Managua', country: 'Nicaragua' },
  { name: 'León', country: 'Nicaragua' },
  { name: 'Tipitapa', country: 'Nicaragua' },
  { name: 'Masaya', country: 'Nicaragua' },
  { name: 'Chinandega', country: 'Nicaragua' },
  { name: 'Matagalpa', country: 'Nicaragua' },
  { name: 'Estelí', country: 'Nicaragua' },
  { name: 'Granada', country: 'Nicaragua' },
  { name: 'San Juan del Sur', country: 'Nicaragua' },
  { name: 'Corn Islands', country: 'Nicaragua' },
  { name: 'Ometepe Island', country: 'Nicaragua' },
  { name: 'Masaya Volcano', country: 'Nicaragua' },
  { name: 'Somoto Canyon', country: 'Nicaragua' },
  { name: 'Pearl Lagoon', country: 'Nicaragua' },
  { name: 'Little Corn Island', country: 'Nicaragua' },

  // Costa Rica
  { name: 'San José', country: 'Costa Rica' },
  { name: 'Limón', country: 'Costa Rica' },
  { name: 'Alajuela', country: 'Costa Rica' },
  { name: 'Heredia', country: 'Costa Rica' },
  { name: 'Puntarenas', country: 'Costa Rica' },
  { name: 'Cartago', country: 'Costa Rica' },
  { name: 'Liberia', country: 'Costa Rica' },
  { name: 'Tamarindo', country: 'Costa Rica' },
  { name: 'Manuel Antonio', country: 'Costa Rica' },
  { name: 'Jacó', country: 'Costa Rica' },
  { name: 'La Fortuna', country: 'Costa Rica' },
  { name: 'Monteverde', country: 'Costa Rica' },
  { name: 'Puerto Viejo', country: 'Costa Rica' },
  { name: 'Uvita', country: 'Costa Rica' },
  { name: 'Santa Teresa', country: 'Costa Rica' },
  { name: 'Nosara', country: 'Costa Rica' },
  { name: 'Arenal Volcano', country: 'Costa Rica' },
  { name: 'Tortuguero', country: 'Costa Rica' },
  { name: 'Corcovado National Park', country: 'Costa Rica' },
  { name: 'Poás Volcano', country: 'Costa Rica' },
  { name: 'Irazú Volcano', country: 'Costa Rica' },
  { name: 'Rincón de la Vieja', country: 'Costa Rica' },
  { name: 'Cahuita', country: 'Costa Rica' },
  { name: 'Drake Bay', country: 'Costa Rica' },
  { name: 'Dominical', country: 'Costa Rica' },

  // Panama
  { name: 'Panama City', country: 'Panama' },
  { name: 'San Miguelito', country: 'Panama' },
  { name: 'Tocumen', country: 'Panama' },
  { name: 'David', country: 'Panama' },
  { name: 'Las Cumbres', country: 'Panama' },
  { name: 'Colón', country: 'Panama' },
  { name: 'La Chorrera', country: 'Panama' },
  { name: 'Pacora', country: 'Panama' },
  { name: 'Bocas del Toro', country: 'Panama' },
  { name: 'Boquete', country: 'Panama' },
  { name: 'San Blas Islands', country: 'Panama' },
  { name: 'Casco Viejo', country: 'Panama' },
  { name: 'Pearl Islands', country: 'Panama' },
  { name: 'Santa Catalina', country: 'Panama' },
  { name: 'Coiba National Park', country: 'Panama' },
  { name: 'Volcán Barú', country: 'Panama' },
  { name: 'Panama Canal', country: 'Panama' },
  { name: 'Bastimentos Island', country: 'Panama' },
  { name: 'Isla Taboga', country: 'Panama' },

  // CARIBBEAN
  // Cuba
  { name: 'Havana', country: 'Cuba' },
  { name: 'Santiago de Cuba', country: 'Cuba' },
  { name: 'Camagüey', country: 'Cuba' },
  { name: 'Holguín', country: 'Cuba' },
  { name: 'Guantánamo', country: 'Cuba' },
  { name: 'Santa Clara', country: 'Cuba' },
  { name: 'Las Tunas', country: 'Cuba' },
  { name: 'Bayamo', country: 'Cuba' },
  { name: 'Cienfuegos', country: 'Cuba' },
  { name: 'Pinar del Río', country: 'Cuba' },
  { name: 'Matanzas', country: 'Cuba' },
  { name: 'Varadero', country: 'Cuba' },
  { name: 'Viñales', country: 'Cuba' },
  { name: 'Trinidad', country: 'Cuba' },
  { name: 'Cayo Coco', country: 'Cuba' },
  { name: 'Cayo Largo', country: 'Cuba' },
  { name: 'Cayo Santa María', country: 'Cuba' },
  { name: 'Cayo Guillermo', country: 'Cuba' },
  { name: 'Baracoa', country: 'Cuba' },

  // Jamaica
  { name: 'Kingston', country: 'Jamaica' },
  { name: 'Spanish Town', country: 'Jamaica' },
  { name: 'Portmore', country: 'Jamaica' },
  { name: 'Montego Bay', country: 'Jamaica' },
  { name: 'Ocho Rios', country: 'Jamaica' },
  { name: 'Negril', country: 'Jamaica' },
  { name: 'Port Antonio', country: 'Jamaica' },
  { name: 'Treasure Beach', country: 'Jamaica' },
  { name: 'Blue Mountains', country: 'Jamaica' },
  { name: 'Dunn\'s River Falls', country: 'Jamaica' },
  { name: 'Seven Mile Beach', country: 'Jamaica' },
  { name: 'Rick\'s Cafe', country: 'Jamaica' },

  // Haiti
  { name: 'Port-au-Prince', country: 'Haiti' },
  { name: 'Carrefour', country: 'Haiti' },
  { name: 'Delmas', country: 'Haiti' },
  { name: 'Cap-Haïtien', country: 'Haiti' },
  { name: 'Pétionville', country: 'Haiti' },
  { name: 'Port-de-Paix', country: 'Haiti' },
  { name: 'Jacmel', country: 'Haiti' },
  { name: 'Labadee', country: 'Haiti' },
  { name: 'Citadelle Laferrière', country: 'Haiti' },

  // Dominican Republic
  { name: 'Santo Domingo', country: 'Dominican Republic' },
  { name: 'Santiago de los Caballeros', country: 'Dominican Republic' },
  { name: 'La Romana', country: 'Dominican Republic' },
  { name: 'San Pedro de Macorís', country: 'Dominican Republic' },
  { name: 'San Cristóbal', country: 'Dominican Republic' },
  { name: 'Puerto Plata', country: 'Dominican Republic' },
  { name: 'San Francisco de Macorís', country: 'Dominican Republic' },
  { name: 'Higüey', country: 'Dominican Republic' },
  { name: 'Concepción de La Vega', country: 'Dominican Republic' },
  { name: 'Punta Cana', country: 'Dominican Republic' },
  { name: 'Bávaro', country: 'Dominican Republic' },
  { name: 'Samaná', country: 'Dominican Republic' },
  { name: 'Las Terrenas', country: 'Dominican Republic' },
  { name: 'Cabarete', country: 'Dominican Republic' },
  { name: 'Jarabacoa', country: 'Dominican Republic' },
  { name: 'Bayahíbe', country: 'Dominican Republic' },
  { name: 'Juan Dolio', country: 'Dominican Republic' },
  { name: 'Sosúa', country: 'Dominican Republic' },

  // Puerto Rico
  { name: 'San Juan', country: 'Puerto Rico' },
  { name: 'Bayamón', country: 'Puerto Rico' },
  { name: 'Carolina', country: 'Puerto Rico' },
  { name: 'Ponce', country: 'Puerto Rico' },
  { name: 'Caguas', country: 'Puerto Rico' },
  { name: 'Guaynabo', country: 'Puerto Rico' },
  { name: 'Arecibo', country: 'Puerto Rico' },
  { name: 'Mayagüez', country: 'Puerto Rico' },
  { name: 'Toa Baja', country: 'Puerto Rico' },
  { name: 'Trujillo Alto', country: 'Puerto Rico' },
  { name: 'Culebra', country: 'Puerto Rico' },
  { name: 'Vieques', country: 'Puerto Rico' },
  { name: 'El Yunque', country: 'Puerto Rico' },
  { name: 'Old San Juan', country: 'Puerto Rico' },
  { name: 'Rincón', country: 'Puerto Rico' },
  { name: 'Fajardo', country: 'Puerto Rico' },

  // Trinidad and Tobago
  { name: 'Port of Spain', country: 'Trinidad and Tobago' },
  { name: 'San Fernando', country: 'Trinidad and Tobago' },
  { name: 'Chaguanas', country: 'Trinidad and Tobago' },
  { name: 'Arima', country: 'Trinidad and Tobago' },
  { name: 'Tobago', country: 'Trinidad and Tobago' },
  { name: 'Scarborough', country: 'Trinidad and Tobago' },
  { name: 'Maracas Bay', country: 'Trinidad and Tobago' },
  { name: 'Pigeon Point', country: 'Trinidad and Tobago' },

  // Bahamas
  { name: 'Nassau', country: 'Bahamas' },
  { name: 'Lucaya', country: 'Bahamas' },
  { name: 'Freeport', country: 'Bahamas' },
  { name: 'West End', country: 'Bahamas' },
  { name: 'Paradise Island', country: 'Bahamas' },
  { name: 'Exuma', country: 'Bahamas' },
  { name: 'Eleuthera', country: 'Bahamas' },
  { name: 'Harbour Island', country: 'Bahamas' },
  { name: 'Andros', country: 'Bahamas' },
  { name: 'Abacos', country: 'Bahamas' },
  { name: 'Cat Island', country: 'Bahamas' },
  { name: 'Long Island', country: 'Bahamas' },

  // Barbados
  { name: 'Bridgetown', country: 'Barbados' },
  { name: 'Speightstown', country: 'Barbados' },
  { name: 'Oistins', country: 'Barbados' },
  { name: 'Holetown', country: 'Barbados' },
  { name: 'Bathsheba', country: 'Barbados' },
  { name: 'Carlisle Bay', country: 'Barbados' },
  { name: 'Harrison\'s Cave', country: 'Barbados' },

  // Lesser Antilles
  { name: 'Willemstad', country: 'Curaçao' },
  { name: 'Oranjestad', country: 'Aruba' },
  { name: 'Philipsburg', country: 'Sint Maarten' },
  { name: 'Gustavia', country: 'Saint Barthélemy' },
  { name: 'Marigot', country: 'Saint Martin' },
  { name: 'The Valley', country: 'Anguilla' },
  { name: 'Basseterre', country: 'Saint Kitts and Nevis' },
  { name: 'Charlestown', country: 'Saint Kitts and Nevis' },
  { name: 'St. John\'s', country: 'Antigua and Barbuda' },
  { name: 'Codrington', country: 'Antigua and Barbuda' },
  { name: 'Roseau', country: 'Dominica' },
  { name: 'Portsmouth', country: 'Dominica' },
  { name: 'Castries', country: 'Saint Lucia' },
  { name: 'Soufrière', country: 'Saint Lucia' },
  { name: 'Kingstown', country: 'Saint Vincent and the Grenadines' },
  { name: 'Bequia', country: 'Saint Vincent and the Grenadines' },
  { name: 'Mustique', country: 'Saint Vincent and the Grenadines' },
  { name: 'St. George\'s', country: 'Grenada' },
  { name: 'Carriacou', country: 'Grenada' },

  // EXPANDED INDIA - Adding more cities and regions
  { name: 'Varanasi', country: 'India' },
  { name: 'Srinagar', country: 'India' },
  { name: 'Amritsar', country: 'India' },
  { name: 'Goa', country: 'India' },
  { name: 'Rishikesh', country: 'India' },
  { name: 'Haridwar', country: 'India' },
  { name: 'Shimla', country: 'India' },
  { name: 'Manali', country: 'India' },
  { name: 'Leh', country: 'India' },
  { name: 'Ladakh', country: 'India' },
  { name: 'Dharamshala', country: 'India' },
  { name: 'Mysore', country: 'India' },
  { name: 'Coorg', country: 'India' },
  { name: 'Hampi', country: 'India' },
  { name: 'Kochi', country: 'India' },
  { name: 'Alleppey', country: 'India' },
  { name: 'Munnar', country: 'India' },
  { name: 'Thekkady', country: 'India' },
  { name: 'Kovalam', country: 'India' },
  { name: 'Varkala', country: 'India' },
  { name: 'Pondicherry', country: 'India' },
  { name: 'Madurai', country: 'India' },
  { name: 'Rameswaram', country: 'India' },
  { name: 'Kanyakumari', country: 'India' },
  { name: 'Ooty', country: 'India' },
  { name: 'Kodaikanal', country: 'India' },
  { name: 'Mahabalipuram', country: 'India' },
  { name: 'Tirupati', country: 'India' },
  { name: 'Visakhapatnam', country: 'India' },
  { name: 'Andaman Islands', country: 'India' },
  { name: 'Port Blair', country: 'India' },
  { name: 'Havelock Island', country: 'India' },
  { name: 'Neil Island', country: 'India' },
  { name: 'Nicobar Islands', country: 'India' },
  { name: 'Lakshadweep', country: 'India' },
  { name: 'Mount Abu', country: 'India' },
  { name: 'Udaipur', country: 'India' },
  { name: 'Jodhpur', country: 'India' },
  { name: 'Bikaner', country: 'India' },
  { name: 'Pushkar', country: 'India' },
  { name: 'Ajmer', country: 'India' },
  { name: 'Ranthambore', country: 'India' },
  { name: 'Jim Corbett National Park', country: 'India' },
  { name: 'Kaziranga National Park', country: 'India' },
  { name: 'Periyar National Park', country: 'India' },
  { name: 'Khajuraho', country: 'India' },
  { name: 'Ajanta Caves', country: 'India' },
  { name: 'Ellora Caves', country: 'India' },
  { name: 'Konark', country: 'India' },
  { name: 'Puri', country: 'India' },
  { name: 'Bhubaneswar', country: 'India' },
  { name: 'Darjeeling', country: 'India' },
  { name: 'Gangtok', country: 'India' },
  { name: 'Sikkim', country: 'India' },
  { name: 'Shillong', country: 'India' },
  { name: 'Cherrapunji', country: 'India' },
  { name: 'Meghalaya', country: 'India' },
  { name: 'Tawang', country: 'India' },
  { name: 'Arunachal Pradesh', country: 'India' },
  { name: 'Guwahati', country: 'India' },
  { name: 'Assam', country: 'India' },
  { name: 'Imphal', country: 'India' },
  { name: 'Manipur', country: 'India' },
  { name: 'Kohima', country: 'India' },
  { name: 'Nagaland', country: 'India' },
  { name: 'Aizawl', country: 'India' },
  { name: 'Mizoram', country: 'India' },
  { name: 'Agartala', country: 'India' },
  { name: 'Tripura', country: 'India' },

  // CANADA - North America
  { name: 'Toronto', country: 'Canada' },
  { name: 'Vancouver', country: 'Canada' },
  { name: 'Montreal', country: 'Canada' },
  { name: 'Calgary', country: 'Canada' },
  { name: 'Ottawa', country: 'Canada' },
  { name: 'Edmonton', country: 'Canada' },
  { name: 'Quebec City', country: 'Canada' },
  { name: 'Winnipeg', country: 'Canada' },
  { name: 'Victoria', country: 'Canada' },
  { name: 'Halifax', country: 'Canada' },
  { name: 'Banff', country: 'Canada' },
  { name: 'Jasper', country: 'Canada' },
  { name: 'Whistler', country: 'Canada' },
  { name: 'Niagara Falls', country: 'Canada' },
  { name: 'Charlottetown', country: 'Canada' },
  { name: 'St. John\'s', country: 'Canada' },
  { name: 'Yellowknife', country: 'Canada' },
  { name: 'Whitehorse', country: 'Canada' },
  { name: 'Iqaluit', country: 'Canada' },
  { name: 'Churchill', country: 'Canada' },

  // CYPRUS - Europe/Middle East
  { name: 'Nicosia', country: 'Cyprus' },
  { name: 'Limassol', country: 'Cyprus' },
  { name: 'Larnaca', country: 'Cyprus' },
  { name: 'Paphos', country: 'Cyprus' },
  { name: 'Famagusta', country: 'Cyprus' },
  { name: 'Kyrenia', country: 'Cyprus' },
  { name: 'Ayia Napa', country: 'Cyprus' },
  { name: 'Protaras', country: 'Cyprus' },
  { name: 'Troodos Mountains', country: 'Cyprus' },

  // YEMEN - Middle East
  { name: 'Sana\'a', country: 'Yemen' },
  { name: 'Aden', country: 'Yemen' },
  { name: 'Taiz', country: 'Yemen' },
  { name: 'Al Hudaydah', country: 'Yemen' },
  { name: 'Ibb', country: 'Yemen' },
  { name: 'Dhamar', country: 'Yemen' },
  { name: 'Al Mukalla', country: 'Yemen' },
  { name: 'Shibam', country: 'Yemen' },
  { name: 'Socotra', country: 'Yemen' },

  // ERITREA - East Africa
  { name: 'Asmara', country: 'Eritrea' },
  { name: 'Massawa', country: 'Eritrea' },
  { name: 'Keren', country: 'Eritrea' },
  { name: 'Assab', country: 'Eritrea' },
  { name: 'Mendefera', country: 'Eritrea' },
  { name: 'Barentu', country: 'Eritrea' },
  { name: 'Dekemhare', country: 'Eritrea' },
  { name: 'Dahlak Archipelago', country: 'Eritrea' },

  // SOMALIA - East Africa
  { name: 'Mogadishu', country: 'Somalia' },
  { name: 'Hargeisa', country: 'Somalia' },
  { name: 'Berbera', country: 'Somalia' },
  { name: 'Kismayo', country: 'Somalia' },
  { name: 'Baidoa', country: 'Somalia' },
  { name: 'Galkayo', country: 'Somalia' },
  { name: 'Bosaso', country: 'Somalia' },
  { name: 'Merca', country: 'Somalia' },
  { name: 'Garowe', country: 'Somalia' },

  // SEYCHELLES - East Africa (Island)
  { name: 'Victoria', country: 'Seychelles' },
  { name: 'Mahé', country: 'Seychelles' },
  { name: 'Praslin', country: 'Seychelles' },
  { name: 'La Digue', country: 'Seychelles' },
  { name: 'Anse Source d\'Argent', country: 'Seychelles' },
  { name: 'Beau Vallon', country: 'Seychelles' },
  { name: 'Vallée de Mai', country: 'Seychelles' },

  // SOUTH SUDAN - East Africa
  { name: 'Juba', country: 'South Sudan' },
  { name: 'Malakal', country: 'South Sudan' },
  { name: 'Wau', country: 'South Sudan' },
  { name: 'Yei', country: 'South Sudan' },
  { name: 'Bor', country: 'South Sudan' },
  { name: 'Rumbek', country: 'South Sudan' },
  { name: 'Aweil', country: 'South Sudan' },
  { name: 'Bentiu', country: 'South Sudan' },

  // BURUNDI - East Africa
  { name: 'Bujumbura', country: 'Burundi' },
  { name: 'Gitega', country: 'Burundi' },
  { name: 'Muyinga', country: 'Burundi' },
  { name: 'Ngozi', country: 'Burundi' },
  { name: 'Ruyigi', country: 'Burundi' },
  { name: 'Kayanza', country: 'Burundi' },
  { name: 'Bururi', country: 'Burundi' },
  { name: 'Cibitoke', country: 'Burundi' },

  // THE GAMBIA - West Africa
  { name: 'Banjul', country: 'The Gambia' },
  { name: 'Serekunda', country: 'The Gambia' },
  { name: 'Brikama', country: 'The Gambia' },
  { name: 'Bakau', country: 'The Gambia' },
  { name: 'Farafenni', country: 'The Gambia' },
  { name: 'Lamin', country: 'The Gambia' },
  { name: 'Kololi', country: 'The Gambia' },
  { name: 'Kotu', country: 'The Gambia' },

  // TOGO - West Africa
  { name: 'Lomé', country: 'Togo' },
  { name: 'Sokodé', country: 'Togo' },
  { name: 'Kara', country: 'Togo' },
  { name: 'Atakpamé', country: 'Togo' },
  { name: 'Kpalimé', country: 'Togo' },
  { name: 'Dapaong', country: 'Togo' },
  { name: 'Tsévié', country: 'Togo' },
  { name: 'Aného', country: 'Togo' },

  // BENIN - West Africa
  { name: 'Porto-Novo', country: 'Benin' },
  { name: 'Cotonou', country: 'Benin' },
  { name: 'Parakou', country: 'Benin' },
  { name: 'Djougou', country: 'Benin' },
  { name: 'Bohicon', country: 'Benin' },
  { name: 'Kandi', country: 'Benin' },
  { name: 'Abomey', country: 'Benin' },
  { name: 'Natitingou', country: 'Benin' },
  { name: 'Ouidah', country: 'Benin' },

  // WESTERN SAHARA - North Africa
  { name: 'Laayoune', country: 'Western Sahara' },
  { name: 'Dakhla', country: 'Western Sahara' },
  { name: 'Smara', country: 'Western Sahara' },
  { name: 'Boujdour', country: 'Western Sahara' },

  // MAURITANIA - West Africa
  { name: 'Nouakchott', country: 'Mauritania' },
  { name: 'Nouadhibou', country: 'Mauritania' },
  { name: 'Néma', country: 'Mauritania' },
  { name: 'Kaédi', country: 'Mauritania' },
  { name: 'Rosso', country: 'Mauritania' },
  { name: 'Zouérat', country: 'Mauritania' },
  { name: 'Atar', country: 'Mauritania' },
  { name: 'Chinguetti', country: 'Mauritania' },

  // GIBRALTAR - Europe
  { name: 'Gibraltar', country: 'Gibraltar' },

  // ANGOLA - Southern Africa
  { name: 'Luanda', country: 'Angola' },
  { name: 'Huambo', country: 'Angola' },
  { name: 'Lobito', country: 'Angola' },
  { name: 'Benguela', country: 'Angola' },
  { name: 'Lubango', country: 'Angola' },
  { name: 'Malanje', country: 'Angola' },
  { name: 'Namibe', country: 'Angola' },
  { name: 'Soyo', country: 'Angola' },
  { name: 'Cabinda', country: 'Angola' },
  { name: 'Kuito', country: 'Angola' },

  // MALTA - Europe (Mediterranean)
  { name: 'Valletta', country: 'Malta' },
  { name: 'Sliema', country: 'Malta' },
  { name: 'St. Julian\'s', country: 'Malta' },
  { name: 'Mdina', country: 'Malta' },
  { name: 'Gozo', country: 'Malta' },
  { name: 'Comino', country: 'Malta' },
  { name: 'Marsaxlokk', country: 'Malta' },
  { name: 'Mellieħa', country: 'Malta' },

  // ANDORRA - Europe
  { name: 'Andorra la Vella', country: 'Andorra' },
  { name: 'Escaldes-Engordany', country: 'Andorra' },
  { name: 'Encamp', country: 'Andorra' },
  { name: 'La Massana', country: 'Andorra' },
  { name: 'Ordino', country: 'Andorra' },
  { name: 'Canillo', country: 'Andorra' },
  { name: 'Pas de la Casa', country: 'Andorra' },

  // LUXEMBOURG - Europe
  { name: 'Luxembourg City', country: 'Luxembourg' },
  { name: 'Esch-sur-Alzette', country: 'Luxembourg' },
  { name: 'Differdange', country: 'Luxembourg' },
  { name: 'Dudelange', country: 'Luxembourg' },
  { name: 'Ettelbruck', country: 'Luxembourg' },
  { name: 'Diekirch', country: 'Luxembourg' },
  { name: 'Vianden', country: 'Luxembourg' },

  // BHUTAN - South Asia
  { name: 'Thimphu', country: 'Bhutan' },
  { name: 'Paro', country: 'Bhutan' },
  { name: 'Punakha', country: 'Bhutan' },
  { name: 'Phuentsholing', country: 'Bhutan' },
  { name: 'Jakar', country: 'Bhutan' },
  { name: 'Wangdue Phodrang', country: 'Bhutan' },
  { name: 'Tiger\'s Nest Monastery', country: 'Bhutan' },
  { name: 'Haa Valley', country: 'Bhutan' },

  // AFGHANISTAN - Central Asia
  { name: 'Kabul', country: 'Afghanistan' },
  { name: 'Herat', country: 'Afghanistan' },
  { name: 'Kandahar', country: 'Afghanistan' },
  { name: 'Mazar-i-Sharif', country: 'Afghanistan' },
  { name: 'Jalalabad', country: 'Afghanistan' },
  { name: 'Kunduz', country: 'Afghanistan' },
  { name: 'Bamyan', country: 'Afghanistan' },
  { name: 'Ghazni', country: 'Afghanistan' },

  // KOSOVO - Europe (Balkans)
  { name: 'Pristina', country: 'Kosovo' },
  { name: 'Prizren', country: 'Kosovo' },
  { name: 'Peja', country: 'Kosovo' },
  { name: 'Gjakova', country: 'Kosovo' },
  { name: 'Mitrovica', country: 'Kosovo' },
  { name: 'Ferizaj', country: 'Kosovo' },
  { name: 'Gjilan', country: 'Kosovo' },

  // UKRAINE - Europe (Eastern)
  { name: 'Kyiv', country: 'Ukraine' },
  { name: 'Lviv', country: 'Ukraine' },
  { name: 'Odesa', country: 'Ukraine' },
  { name: 'Kharkiv', country: 'Ukraine' },
  { name: 'Dnipro', country: 'Ukraine' },
  { name: 'Zaporizhzhia', country: 'Ukraine' },
  { name: 'Chernivtsi', country: 'Ukraine' },
  { name: 'Ivano-Frankivsk', country: 'Ukraine' },
  { name: 'Chernihiv', country: 'Ukraine' },
  { name: 'Poltava', country: 'Ukraine' },

  // NORTH MACEDONIA - Europe (Balkans)
  { name: 'Skopje', country: 'North Macedonia' },
  { name: 'Bitola', country: 'North Macedonia' },
  { name: 'Ohrid', country: 'North Macedonia' },
  { name: 'Prilep', country: 'North Macedonia' },
  { name: 'Tetovo', country: 'North Macedonia' },
  { name: 'Kumanovo', country: 'North Macedonia' },
  { name: 'Veles', country: 'North Macedonia' },
  { name: 'Strumica', country: 'North Macedonia' },

  // RUSSIA - Europe/Asia
  { name: 'Moscow', country: 'Russia' },
  { name: 'Saint Petersburg', country: 'Russia' },
  { name: 'Novosibirsk', country: 'Russia' },
  { name: 'Yekaterinburg', country: 'Russia' },
  { name: 'Kazan', country: 'Russia' },
  { name: 'Nizhny Novgorod', country: 'Russia' },
  { name: 'Chelyabinsk', country: 'Russia' },
  { name: 'Samara', country: 'Russia' },
  { name: 'Omsk', country: 'Russia' },
  { name: 'Rostov-on-Don', country: 'Russia' },
  { name: 'Ufa', country: 'Russia' },
  { name: 'Krasnoyarsk', country: 'Russia' },
  { name: 'Perm', country: 'Russia' },
  { name: 'Voronezh', country: 'Russia' },
  { name: 'Volgograd', country: 'Russia' },
  { name: 'Sochi', country: 'Russia' },
  { name: 'Vladivostok', country: 'Russia' },
  { name: 'Irkutsk', country: 'Russia' },
  { name: 'Lake Baikal', country: 'Russia' },
  { name: 'Kamchatka', country: 'Russia' },

  // PAPUA NEW GUINEA - Oceania
  { name: 'Port Moresby', country: 'Papua New Guinea' },
  { name: 'Lae', country: 'Papua New Guinea' },
  { name: 'Mount Hagen', country: 'Papua New Guinea' },
  { name: 'Madang', country: 'Papua New Guinea' },
  { name: 'Kokopo', country: 'Papua New Guinea' },
  { name: 'Goroka', country: 'Papua New Guinea' },
  { name: 'Wewak', country: 'Papua New Guinea' },
  { name: 'Rabaul', country: 'Papua New Guinea' },

  // NORTH KOREA - East Asia
  { name: 'Pyongyang', country: 'North Korea' },
  { name: 'Hamhung', country: 'North Korea' },
  { name: 'Chongjin', country: 'North Korea' },
  { name: 'Nampo', country: 'North Korea' },
  { name: 'Wonsan', country: 'North Korea' },
  { name: 'Kaesong', country: 'North Korea' },
  { name: 'Mount Kumgang', country: 'North Korea' },
  { name: 'Mount Paektu', country: 'North Korea' }
];

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
  onLocationSelect?: (location: { name: string; country: string }) => void;
}

export function LocationAutocomplete({
  value,
  onChange,
  placeholder = "Search for cities, islands, or countries...",
  label = "Location",
  required = false,
  className,
  onLocationSelect
}: LocationAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);
  const isTypingRef = useRef(false);

  const filteredLocations = useMemo(() => {
    if (!inputValue.trim()) return [];
    const searchTerm = inputValue.toLowerCase().trim();
    
    const results = LOCATIONS.filter(location =>
      location.name.toLowerCase().includes(searchTerm) ||
      location.country.toLowerCase().includes(searchTerm)
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Remove exact duplicates by name+country while preserving order
    const seen = new Set<string>();
    const unique: { name: string; country: string }[] = [];
    for (const loc of results) {
      const key = `${loc.name.toLowerCase()}|${loc.country.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(loc);
      }
    }

    return unique.slice(0, 8);
  }, [inputValue]);

  const handleInputChange = (newValue: string) => {
    isTypingRef.current = true;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(newValue.trim().length > 0);
    // Reset typing flag after input is processed
    setTimeout(() => {
      isTypingRef.current = false;
    }, 50);
  };

  const selectLocation = (location: { name: string; country: string }) => {
    const formattedLocation = `${location.name}, ${location.country}`;
    setInputValue(formattedLocation);
    onChange(formattedLocation);
    setIsOpen(false);
    onLocationSelect?.(location);
  };

  const detectCurrentLocation = async () => {
    if (!navigator.geolocation) return;

    setIsDetectingLocation(true);
    setHasRequestedLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Use reverse geocoding to get city name
          // For demo purposes, we'll simulate this with a mock response
          const mockLocation = { name: 'Current Location', country: 'Detected' };
          selectLocation(mockLocation);
        } catch (error) {
          console.error('Error getting location name:', error);
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsDetectingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  // Prevent external value updates when user is actively typing
  useEffect(() => {
    if (!isTypingRef.current && value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value, inputValue]);

  // Auto-detect location on component mount if not already set and not previously requested
  useEffect(() => {
    if (!value && !hasRequestedLocation && navigator.geolocation) {
      detectCurrentLocation();
    }
  }, [value, hasRequestedLocation]);

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label htmlFor="location-input">{label}</Label>}
      <div className="relative">
        <div className="relative">
          <Input
            id="location-input"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => setIsOpen(inputValue.trim().length > 0)}
            onBlur={() => {
              // Delay closing to allow click on suggestion
              setTimeout(() => setIsOpen(false), 200);
            }}
            placeholder={placeholder}
            required={required}
            className="pr-20"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {isDetectingLocation ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <button
                type="button"
                onClick={detectCurrentLocation}
                className="p-1 hover:bg-muted rounded-sm transition-colors"
                title="Detect current location"
              >
                <MapPinIcon className="h-4 w-4 text-muted-foreground hover:text-primary" />
              </button>
            )}
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        {isOpen && filteredLocations.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md">
            <Command className="w-full">
              <CommandList>
                <CommandGroup>
                  {filteredLocations.map((location, index) => (
                    <CommandItem
                      key={`${location.name}-${location.country}-${index}`}
                      value={`${location.name}, ${location.country}`}
                      onSelect={() => selectLocation(location)}
                      onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {location.name}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {location.country}
                          </div>
                        </div>
                      </div>
                      {inputValue === `${location.name}, ${location.country}` && (
                        <Check className="h-3 w-3 text-primary" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
}
